// App publish, share and unpublish (DES1 `publish*`, `share`, `unpublish`).
//
// Namespace `app-publish`; owner: T2-UI1 (ui/workspace/apps/publish). The host computes the diff;
// this only words it. `{app}` app name, `{n}` / `{prev}` versions, `{team}` the audience name,
// `{count}` / `{people}` numbers, `{names}` an already-joined list, `{place}` a location,
// `{who}` a person, `{role}` a role. Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Publish {app} v{n}',
  firstTitle: 'Publish {app}',
  lead: '{team} will use v{n}. v{prev} stays in Versions.',
  firstLead: '{team} will be able to open {app}. You can unpublish any time.',
  close: 'Close',
  whatsNew: 'What’s new in v{n}',
  whatsNewPlaceholder: 'One line people will see',
  changesHeading: 'What changes for people',
  rowLooks: 'Looks',
  rowStores: 'Stores',
  rowReads: 'Reads',
  rowWrites: 'Writes',
  rowRuns: 'Runs',
  whoHeading: 'Who can use it',
  whoMeta: '{team} · {people} people',
  whoRole: 'Can use · same as v{prev}',
  whoRoleFirst: 'Can use',
  changeInShare: 'Change in Share',
  checksHeading: 'Checks',
  blocksPublishing: 'Blocks publishing',
  fixInChat: 'Fix in chat',
  checksPassed: '{count} checks passed',
  show: 'Show',
  hide: 'Hide',
  contractionHeading: 'This version removes something',
  contractionNote: 'Tick each one to confirm. Nothing is removed until you publish.',
  permission: 'You can publish because you have the publisher permission at {company}.',
  undoStays: 'Undo stays available after publishing, because nothing is removed.',
  undoGone: 'Undo is not available after publishing, because this removes data.',
  footerAudience: 'Company members only. No public links.',
  footerBlocked: '{count} check blocks publishing',
  footerBlockedMany: '{count} checks block publishing',
  footerContraction: 'Confirm what is removed to publish.',
  cancel: 'Cancel',
  publish: 'Publish v{n}',
  publishFirst: 'Publish',
  publishing: 'Publishing…',
  doneTitle: 'v{n} is live for {team}',
  doneFirstTitle: '{app} is live for {team}',
  doneBody: '{people} people use v{n} from now on. Their {entries} entries stay as they are.',
  doneBodyUncounted: '{people} people use v{n} from now on. Their entries stay as they are.',
  doneBodyBare: '{people} people use v{n} from now on.',
  doneFirstBody: '{people} people can open it now. Their entries start here.',
  doneWhere: 'in their Library and pinned for the team',
  doneThey: 'They’ll see {what} the next time they open {app}.',
  doneUndoNote: 'Nothing was removed, so Undo stays available.',
  undoPublish: 'Undo publish',
  publishUnknownTitle: 'We don’t know whether v{n} was published',
  publishUnknownBody:
    '{team} may or may not have it yet. Check before publishing again, so nothing is published twice.',
  publishFailedTitle: 'v{n} wasn’t published',
  publishFailedBody: 'Nothing changed for {team}. Try again.',
  checkAgain: 'Check again',
  tryAgain: 'Try again',
  askOwner: 'Ask {owner}',
  restoreAction: 'Restore…',
  copyLink: 'Copy link',
  copied: 'Link copied',
  done: 'Done',
  peopleList: '{names} in {place}',
  peopleAndOthers: '{first}, {second} and {count} others',
  peopleAndOneOther: '{first}, {second} and 1 other',
  agentPublished: 'Published · v{n} by {agent}',
  undo: 'Undo',
  restoreVersion: 'Restore v{prev}',
  agentPublishedNote:
    'The agent published it with its checks. Undo or restore v{prev} from Versions.',
  shareTitle: 'Share {app}',
  shareSearch: 'Add people or groups at {company}',
  roleUse: 'Can use',
  roleUseHint: 'Opens the app and adds entries. Sees only what the app shows.',
  roleEdit: 'Can edit',
  roleEditHint:
    'Changes the app in chat; sees Data, History and Versions; can export. Can’t clear or restore data.',
  rolePublish: 'Can publish',
  rolePublishHint: 'Only people with the publisher permission: {names}.',
  roleRemove: 'Remove',
  owner: 'Owner',
  ownerLine: '{you} (you)',
  ownerPublisher: 'Owner · publisher',
  throughConnection: 'People see data through your connection',
  throughConnectionBody:
    'The app shows {system} only through what it shows, not your other {system} data.',
  sharePublishDisabledReason: 'Needs the publisher permission',
  accessTitle: 'How viewers reach company systems',
  accessOwnerYours: 'Through your connection · everyone sees the same',
  accessOwnerOther: 'Through {owner}\u2019s connection · everyone sees the same',
  accessViewer: 'Each person\u2019s own connection · each sees their own',
  accessConnected: '{k} of {n} people in the audience have connected {system}',
  accessChange: 'Change\u2026',
  accessApplies: 'Changes apply when you publish again',
  accessNarrowedPill: 'Not allowed any more',
  accessNarrowed:
    '{company} changed what shared apps may read from {system}. That part is hidden until you choose again and republish.',
  accessUnavailablePill: 'Connection unavailable',
  accessUnavailable: '{owner} needs to reconnect {system}',
  accessUnavailableTold: '{owner} has been told',
  unpublishTitle: 'Unpublish {app}?',
  unpublishBody:
    '{team} will no longer be able to open it. Their {entries} entries are kept and come back if you publish again.',
  unpublishConfirm: 'Unpublish',
  unpublishBusy: 'Unpublishing…',
  error: 'That didn’t work. Nothing was changed. Try again.',
  checkChecking: 'Checking the code and packages…',
  checkClean: 'No secrets or vulnerable packages found in this version.',
  checkWarnings: '{things}. You can still publish.',
  checkHeldSecret: 'Publishing is held. A secret was found in the code.',
  checkHeldDependency: 'Publishing is held. A package has a serious known vulnerability.',
  checkHeldMany: 'Publishing is held. {issues} must be fixed first.',
  checkExpired: 'This check is out of date.',
  checkIncomplete: 'The check couldn’t finish. Publishing stays held until it does.',
  checkHeldPrivate: 'Publishing is held by a security check.',
  checkLastChecked: 'Last checked {time}',
  severityCritical: 'Critical',
  severityHigh: 'High',
  severityModerate: 'Moderate',
  severityLow: 'Low',
  severityUnrated: 'Not rated',
  severityWordCritical: 'critical',
  severityWordHigh: 'serious',
  severityWordModerate: 'moderate',
  severityWordLow: 'minor',
  secretApiKey: 'API key in the code',
  secretPrivateKey: 'Private key in the code',
  secretAccessToken: 'Access token in the code',
  secretPassword: 'Password in the code',
  secretUnknown: 'Possible secret in the code',
  secretWhere: '{area} {file}, line {line}',
  secretAreaCode: 'Code:',
  secretAreaBuilt: 'Built files:',
  secretWhy: 'Anyone who can open the App could read and use it.',
  secretWhat:
    'Move it into a connection or a secret setting. Then replace the key with its provider, because it may already have been copied.',
  depTitle: '{package} {version} has a known {severity} vulnerability',
  depTitleUnrated: '{package} {version} has a vulnerability that isn’t rated yet',
  depWhere: 'Package used by the App',
  depWhy: 'A published security advisory affects this version.',
  depViewAdvisory: 'View advisory',
  depAdviceUpdate: 'Update to a fixed version.',
  depAdviceNoFix: 'No fixed version exists yet. You can accept the risk, or remove the package.',
  depAdviceUnrated: 'Update it, or wait until the advisory is rated.',
  actionFixAndRecheck: 'Fix and re-check',
  actionCheckAgain: 'Check again',
  actionReview: 'Review',
  actionPublishAnyway: 'Accept risk…',
  publishAnywayTitle: 'Accept this known vulnerability?',
  publishAnywayBody:
    '{package} {version} has a {severity} vulnerability with no fix yet. People who use this App could be affected. This is recorded with your name and the time.',
  publishAnywayConfirm: 'Accept risk',
  publishAnywayCancel: 'Cancel',
  publishAnywayAcknowledged: 'Risk accepted · acknowledged by {name} · {date}',
  fixRequestSecret: 'Move the {label} in {file} into a secret setting and re-check',
  fixRequestPackage: 'Update {package} to a fixed version and re-check',
  chatHeldSecret: 'The publish check found a secret in the code. Publishing is held.',
  chatHeldMany: 'The publish check found {issues}. Publishing is held.',
  chatWarnings: 'The publish check found {things}. You can still publish.',
  chatReview: 'Review',
  checkIssues: 'one:issue|other:issues',
  checkThings: 'one:thing to look at|other:things to look at',
};

export type AppPublishCopyKey = keyof typeof en;
type Copy = Record<AppPublishCopyKey, string>;

const sv: Copy = {
  title: 'Publicera {app} v{n}',
  firstTitle: 'Publicera {app}',
  lead: '{team} kommer att använda v{n}. v{prev} finns kvar under Versioner.',
  firstLead: '{team} kommer att kunna öppna {app}. Du kan avpublicera när som helst.',
  close: 'Stäng',
  whatsNew: 'Nytt i v{n}',
  whatsNewPlaceholder: 'En rad som folk ser',
  changesHeading: 'Det här ändras för folk',
  rowLooks: 'Utseende',
  rowStores: 'Lagrar',
  rowReads: 'Läser',
  rowWrites: 'Skriver',
  rowRuns: 'Körs',
  whoHeading: 'Vem kan använda den',
  whoMeta: '{team} · {people} personer',
  whoRole: 'Kan använda · som v{prev}',
  whoRoleFirst: 'Kan använda',
  changeInShare: 'Ändra under Dela',
  checksHeading: 'Kontroller',
  blocksPublishing: 'Stoppar publicering',
  fixInChat: 'Åtgärda i chatten',
  checksPassed: '{count} kontroller godkända',
  show: 'Visa',
  hide: 'Dölj',
  contractionHeading: 'Den här versionen tar bort något',
  contractionNote: 'Kryssa i varje för att bekräfta. Inget tas bort förrän du publicerar.',
  permission: 'Du kan publicera eftersom du har publiceringsbehörighet hos {company}.',
  undoStays: 'Ångra finns kvar efter publicering, eftersom inget tas bort.',
  undoGone: 'Ångra finns inte efter publicering, eftersom data tas bort.',
  footerAudience: 'Bara medarbetare i företaget. Inga publika länkar.',
  footerBlocked: '{count} kontroll stoppar publicering',
  footerBlockedMany: '{count} kontroller stoppar publicering',
  footerContraction: 'Bekräfta vad som tas bort för att publicera.',
  cancel: 'Avbryt',
  publish: 'Publicera v{n}',
  publishFirst: 'Publicera',
  publishing: 'Publicerar…',
  doneTitle: 'v{n} är live för {team}',
  doneFirstTitle: '{app} är live för {team}',
  doneBody: '{people} personer använder v{n} från nu. Deras {entries} poster är kvar som de är.',
  doneBodyUncounted: '{people} personer använder v{n} från nu. Deras poster är kvar som de är.',
  doneBodyBare: '{people} personer använder v{n} från nu.',
  doneFirstBody: '{people} personer kan öppna den nu. Deras poster börjar här.',
  doneWhere: 'i deras bibliotek och fästa för teamet',
  doneThey: 'De ser {what} nästa gång de öppnar {app}.',
  doneUndoNote: 'Inget togs bort, så Ångra finns kvar.',
  undoPublish: 'Ångra publicering',
  publishUnknownTitle: 'Vi vet inte om v{n} publicerades',
  publishUnknownBody:
    '{team} kan ha den eller inte än. Kontrollera innan du publicerar igen, så att inget publiceras två gånger.',
  publishFailedTitle: 'v{n} publicerades inte',
  publishFailedBody: 'Inget ändrades för {team}. Försök igen.',
  checkAgain: 'Kontrollera igen',
  tryAgain: 'Försök igen',
  askOwner: 'Fråga {owner}',
  restoreAction: 'Återställ…',
  copyLink: 'Kopiera länk',
  copied: 'Länken kopierad',
  done: 'Klar',
  peopleList: '{names} i {place}',
  peopleAndOthers: '{first}, {second} och {count} till',
  peopleAndOneOther: '{first}, {second} och 1 till',
  agentPublished: 'Publicerad · v{n} av {agent}',
  undo: 'Ångra',
  restoreVersion: 'Återställ v{prev}',
  agentPublishedNote:
    'Agenten publicerade den med sina kontroller. Ångra eller återställ v{prev} under Versioner.',
  shareTitle: 'Dela {app}',
  shareSearch: 'Lägg till personer eller grupper hos {company}',
  roleUse: 'Kan använda',
  roleUseHint: 'Öppnar appen och lägger till poster. Ser bara det appen visar.',
  roleEdit: 'Kan redigera',
  roleEditHint:
    'Ändrar appen i chatten; ser Data, Historik och Versioner; kan exportera. Kan inte rensa eller återställa data.',
  rolePublish: 'Kan publicera',
  rolePublishHint: 'Bara personer med publiceringsbehörighet: {names}.',
  roleRemove: 'Ta bort',
  owner: 'Ägare',
  ownerLine: '{you} (du)',
  ownerPublisher: 'Ägare · publicerare',
  throughConnection: 'Folk ser data via din anslutning',
  throughConnectionBody:
    'Appen visar {system} bara genom det den visar, inte dina andra {system}-data.',
  sharePublishDisabledReason: 'Kräver publiceringsbehörighet',
  accessTitle: 'Hur läsare når företagssystem',
  accessOwnerYours: 'Via din anslutning · alla ser samma sak',
  accessOwnerOther: 'Via {owner}s anslutning · alla ser samma sak',
  accessViewer: 'Var och ens egen anslutning · alla ser sitt eget',
  accessConnected: '{k} av {n} i målgruppen har anslutit {system}',
  accessChange: 'Ändra…',
  accessApplies: 'Ändringar gäller när du publicerar igen',
  accessNarrowedPill: 'Inte tillåtet längre',
  accessNarrowed:
    '{company} har ändrat vad delade appar får läsa från {system}. Den delen är dold tills du väljer igen och publicerar om.',
  accessUnavailablePill: 'Anslutningen är otillgänglig',
  accessUnavailable: '{owner} behöver ansluta {system} igen',
  accessUnavailableTold: '{owner} har fått veta',
  unpublishTitle: 'Avpublicera {app}?',
  unpublishBody:
    '{team} kan inte längre öppna den. Deras {entries} poster sparas och kommer tillbaka om du publicerar igen.',
  unpublishConfirm: 'Avpublicera',
  unpublishBusy: 'Avpublicerar…',
  error: 'Det gick inte. Inget ändrades. Försök igen.',
  checkChecking: 'Kontrollerar koden och paketen…',
  checkClean: 'Inga hemligheter eller sårbara paket hittades i den här versionen.',
  checkWarnings: '{things}. Du kan ändå publicera.',
  checkHeldSecret: 'Publiceringen är stoppad. En hemlighet hittades i koden.',
  checkHeldDependency: 'Publiceringen är stoppad. Ett paket har en känd allvarlig sårbarhet.',
  checkHeldMany: 'Publiceringen är stoppad. {issues} måste åtgärdas först.',
  checkExpired: 'Den här kontrollen är inaktuell.',
  checkIncomplete:
    'Kontrollen kunde inte slutföras. Publiceringen är stoppad tills den har slutförts.',
  checkHeldPrivate: 'Publiceringen har stoppats av en säkerhetskontroll.',
  checkLastChecked: 'Senast kontrollerad {time}',
  severityCritical: 'Kritisk',
  severityHigh: 'Hög',
  severityModerate: 'Måttlig',
  severityLow: 'Låg',
  severityUnrated: 'Inte bedömd',
  severityWordCritical: 'kritisk',
  severityWordHigh: 'allvarlig',
  severityWordModerate: 'måttlig',
  severityWordLow: 'mindre',
  secretApiKey: 'API-nyckel i koden',
  secretPrivateKey: 'Privat nyckel i koden',
  secretAccessToken: 'Åtkomsttoken i koden',
  secretPassword: 'Lösenord i koden',
  secretUnknown: 'Möjlig hemlighet i koden',
  secretWhere: '{area} {file}, rad {line}',
  secretAreaCode: 'Kod:',
  secretAreaBuilt: 'Byggda filer:',
  secretWhy: 'Alla som kan öppna appen kan läsa och använda den.',
  secretWhat:
    'Flytta den till en anslutning eller en hemlig inställning. Byt sedan ut nyckeln hos leverantören, eftersom den redan kan ha kopierats.',
  depTitle: '{package} {version} har en känd {severity} sårbarhet',
  depTitleUnrated: '{package} {version} har en sårbarhet som ännu inte är bedömd',
  depWhere: 'Paket som appen använder',
  depWhy: 'En publicerad säkerhetsbulletin gäller den här versionen.',
  depViewAdvisory: 'Visa bulletin',
  depAdviceUpdate: 'Uppdatera till en åtgärdad version.',
  depAdviceNoFix:
    'Det finns ingen åtgärdad version än. Du kan acceptera risken eller ta bort paketet.',
  depAdviceUnrated: 'Uppdatera det, eller vänta tills bulletinen har bedömts.',
  actionFixAndRecheck: 'Åtgärda och kontrollera igen',
  actionCheckAgain: 'Kontrollera igen',
  actionReview: 'Granska',
  actionPublishAnyway: 'Acceptera risken…',
  publishAnywayTitle: 'Acceptera den här kända sårbarheten?',
  publishAnywayBody:
    '{package} {version} har en {severity} sårbarhet som ännu inte har åtgärdats. De som använder den här appen kan påverkas. Det registreras med ditt namn och tidpunkten.',
  publishAnywayConfirm: 'Acceptera risken',
  publishAnywayCancel: 'Avbryt',
  publishAnywayAcknowledged: 'Risk accepterad · bekräftad av {name} · {date}',
  fixRequestSecret:
    'Flytta hemligheten ({label}) i {file} till en hemlig inställning och kontrollera igen',
  fixRequestPackage: 'Uppdatera {package} till en åtgärdad version och kontrollera igen',
  chatHeldSecret: 'Publiceringskontrollen hittade en hemlighet i koden. Publiceringen är stoppad.',
  chatHeldMany: 'Publiceringskontrollen hittade {issues}. Publiceringen är stoppad.',
  chatWarnings: 'Publiceringskontrollen hittade {things}. Du kan ändå publicera.',
  chatReview: 'Granska',
  checkIssues: 'one:problem|other:problem',
  checkThings: 'one:sak att titta på|other:saker att titta på',
};

// Full translations (DES2); keys and placeholders identical to `en`.
const translated: Record<string, Copy> = {
  bg: {
    title: 'Публикуване на {app} v{n}',
    firstTitle: 'Публикуване на {app}',
    lead: '{team} ще използва v{n}. v{prev} остава във „Версии“.',
    firstLead: '{team} ще може да отваря {app}. Можете да спрете публикуването по всяко време.',
    close: 'Затваряне',
    whatsNew: 'Какво ново във v{n}',
    whatsNewPlaceholder: 'Един ред, който хората ще видят',
    changesHeading: 'Какво се променя за хората',
    rowLooks: 'Външен вид',
    rowStores: 'Съхранява',
    rowReads: 'Чете',
    rowWrites: 'Записва',
    rowRuns: 'Изпълнява',
    whoHeading: 'Кой може да го използва',
    whoMeta: '{team} · {people} души',
    whoRole: 'Може да използва · както при v{prev}',
    whoRoleFirst: 'Може да използва',
    changeInShare: 'Промяна в „Споделяне“',
    checksHeading: 'Проверки',
    blocksPublishing: 'Блокира публикуването',
    fixInChat: 'Поправка в чата',
    checksPassed: '{count} успешни проверки',
    show: 'Показване',
    hide: 'Скриване',
    contractionHeading: 'Тази версия премахва нещо',
    contractionNote:
      'Отметнете всяко, за да потвърдите. Нищо не се премахва, докато не публикувате.',
    permission: 'Можете да публикувате, защото имате права за публикуване в {company}.',
    undoStays: '„Отмяна“ остава достъпна след публикуването, защото нищо не се премахва.',
    undoGone: '„Отмяна“ не е достъпна след публикуването, защото това премахва данни.',
    footerAudience: 'Само за членове на компанията. Без публични връзки.',
    footerBlocked: '{count} проверка блокира публикуването',
    footerBlockedMany: '{count} проверки блокират публикуването',
    footerContraction: 'Потвърдете какво се премахва, за да публикувате.',
    cancel: 'Отказ',
    publish: 'Публикуване на v{n}',
    publishFirst: 'Публикуване',
    publishing: 'Публикуване…',
    doneTitle: 'v{n} вече е достъпна за {team}',
    doneFirstTitle: '{app} вече е достъпно за {team}',
    doneBody: 'Отсега {people} души използват v{n}. Техните {entries} записа остават непроменени.',
    doneBodyUncounted: 'Отсега {people} души използват v{n}. Записите им остават непроменени.',
    doneBodyBare: 'Отсега {people} души използват v{n}.',
    doneFirstBody: '{people} души вече могат да го отворят. Записите им започват оттук.',
    doneWhere: 'в тяхната библиотека и закачено за екипа',
    doneThey: 'Ще видят {what} следващия път, когато отворят {app}.',
    doneUndoNote: 'Нищо не беше премахнато, така че „Отмяна“ остава достъпна.',
    undoPublish: 'Отмяна на публикуването',
    publishUnknownTitle: 'Не знаем дали v{n} е публикувана',
    publishUnknownBody:
      '{team} може вече да я има, а може и да не я има. Проверете, преди да публикувате отново, за да не се публикува нищо два пъти.',
    publishFailedTitle: 'v{n} не беше публикувана',
    publishFailedBody: 'Нищо не се промени за {team}. Опитайте отново.',
    checkAgain: 'Нова проверка',
    tryAgain: 'Опитайте отново',
    askOwner: 'Попитайте {owner}',
    restoreAction: 'Възстановяване…',
    copyLink: 'Копиране на връзката',
    copied: 'Връзката е копирана',
    done: 'Готово',
    peopleList: '{names} в {place}',
    peopleAndOthers: '{first}, {second} и още {count}',
    peopleAndOneOther: '{first}, {second} и още 1',
    agentPublished: 'Публикувано · v{n} от {agent}',
    undo: 'Отмяна',
    restoreVersion: 'Възстановяване на v{prev}',
    agentPublishedNote:
      'Агентът го публикува със своите проверки. Отменете или възстановете v{prev} от „Версии“.',
    shareTitle: 'Споделяне на {app}',
    shareSearch: 'Добавете хора или групи от {company}',
    roleUse: 'Може да използва',
    roleUseHint:
      'Отваря приложението и добавя записи. Вижда само това, което приложението показва.',
    roleEdit: 'Може да редактира',
    roleEditHint:
      'Променя приложението в чата; вижда „Данни“, „История“ и „Версии“; може да експортира. Не може да изчиства или възстановява данни.',
    rolePublish: 'Може да публикува',
    rolePublishHint: 'Само хора с права за публикуване: {names}.',
    roleRemove: 'Премахване',
    owner: 'Собственик',
    ownerLine: '{you} (Вие)',
    ownerPublisher: 'Собственик · публикуващ',
    throughConnection: 'Хората виждат данните чрез вашата връзка',
    throughConnectionBody:
      'Приложението показва {system} само чрез това, което самото то показва, а не другите ви данни от {system}.',
    sharePublishDisabledReason: 'Изисква права за публикуване',
    accessTitle: 'Как зрителите достигат до системите на компанията',
    accessOwnerYours: 'Чрез вашата връзка · всички виждат едно и също',
    accessOwnerOther: 'Чрез връзката на {owner} · всички виждат едно и също',
    accessViewer: 'Собствената връзка на всеки · всеки вижда своето',
    accessConnected: '{k} от {n} души от аудиторията са свързали {system}',
    accessChange: 'Промяна…',
    accessApplies: 'Промените влизат в сила, когато публикувате отново',
    accessNarrowedPill: 'Вече не е разрешено',
    accessNarrowed:
      '{company} промени какво могат да четат споделените приложения от {system}. Тази част е скрита, докато не изберете отново и не публикувате пак.',
    accessUnavailablePill: 'Връзката не е достъпна',
    accessUnavailable: '{owner} трябва да свърже отново {system}',
    accessUnavailableTold: 'Уведомихме {owner}',
    unpublishTitle: 'Да се спре ли публикуването на {app}?',
    unpublishBody:
      '{team} вече няма да може да го отваря. Техните {entries} записа се запазват и се връщат, ако публикувате отново.',
    unpublishConfirm: 'Спиране на публикуването',
    unpublishBusy: 'Спиране на публикуването…',
    error: 'Не се получи. Нищо не беше променено. Опитайте отново.',
    checkChecking: 'Проверка на кода и пакетите…',
    checkClean: 'В тази версия не са открити тайни или уязвими пакети.',
    checkWarnings: '{things}. Пак можете да публикувате.',
    checkHeldSecret: 'Публикуването е задържано. В кода е открита тайна.',
    checkHeldDependency: 'Публикуването е задържано. Пакет има известна сериозна уязвимост.',
    checkHeldMany: 'Публикуването е задържано. Първо трябва да поправите {issues}.',
    checkExpired: 'Тази проверка вече не е актуална.',
    checkIncomplete:
      'Проверката не можа да завърши. Публикуването остава задържано, докато не завърши.',
    checkHeldPrivate: 'Публикуването е задържано от проверка за сигурност.',
    checkLastChecked: 'Последна проверка: {time}',
    severityCritical: 'Критична',
    severityHigh: 'Висока',
    severityModerate: 'Умерена',
    severityLow: 'Ниска',
    severityUnrated: 'Без оценка',
    severityWordCritical: 'критична',
    severityWordHigh: 'сериозна',
    severityWordModerate: 'умерена',
    severityWordLow: 'незначителна',
    secretApiKey: 'API ключ в кода',
    secretPrivateKey: 'Частен ключ в кода',
    secretAccessToken: 'Токен за достъп в кода',
    secretPassword: 'Парола в кода',
    secretUnknown: 'Възможна тайна в кода',
    secretWhere: '{area} {file}, ред {line}',
    secretAreaCode: 'Код:',
    secretAreaBuilt: 'Компилирани файлове:',
    secretWhy: 'Всеки, който може да отвори приложението, би могъл да го прочете и използва.',
    secretWhat:
      'Преместете го във връзка или в тайна настройка. След това сменете ключа при неговия доставчик, защото може вече да е бил копиран.',
    depTitle: '{package} {version} има известна {severity} уязвимост',
    depTitleUnrated: '{package} {version} има уязвимост, която още не е оценена',
    depWhere: 'Пакет, използван от приложението',
    depWhy: 'Публикувано известие за сигурност засяга тази версия.',
    depViewAdvisory: 'Преглед на известието',
    depAdviceUpdate: 'Актуализирайте до поправена версия.',
    depAdviceNoFix:
      'Все още няма коригирана версия. Можете да приемете риска или да премахнете пакета.',
    depAdviceUnrated: 'Актуализирайте го или изчакайте, докато известието бъде оценено.',
    actionFixAndRecheck: 'Поправка и нова проверка',
    actionCheckAgain: 'Нова проверка',
    actionReview: 'Преглед',
    actionPublishAnyway: 'Приемане на риска…',
    publishAnywayTitle: 'Приемате ли тази известна уязвимост?',
    publishAnywayBody:
      '{package} {version} има {severity} уязвимост, за която още няма поправка. Хората, които използват това приложение, може да бъдат засегнати. Това се записва с вашето име и часа.',
    publishAnywayConfirm: 'Приемане на риска',
    publishAnywayCancel: 'Отказ',
    publishAnywayAcknowledged: 'Рискът е приет · потвърдено от {name} · {date}',
    fixRequestSecret:
      'Преместване на тайната ({label}) от {file} в тайна настройка и нова проверка',
    fixRequestPackage: 'Актуализиране на {package} до поправена версия и нова проверка',
    chatHeldSecret: 'Проверката преди публикуване откри тайна в кода. Публикуването е задържано.',
    chatHeldMany: 'Проверката преди публикуване откри {issues}. Публикуването е задържано.',
    chatWarnings: 'Проверката преди публикуване откри {things}. Пак можете да публикувате.',
    chatReview: 'Преглед',
    checkIssues: 'one:проблем|other:проблема',
    checkThings: 'one:нещо за преглед|other:неща за преглед',
  },
  cs: {
    title: 'Publikovat {app} v{n}',
    firstTitle: 'Publikovat {app}',
    lead: '{team} bude používat v{n}. v{prev} zůstane ve Verzích.',
    firstLead: '{team} bude moct otevřít aplikaci {app}. Publikování můžete kdykoli zrušit.',
    close: 'Zavřít',
    whatsNew: 'Co je nového ve v{n}',
    whatsNewPlaceholder: 'Jeden řádek, který lidé uvidí',
    changesHeading: 'Co se pro lidi změní',
    rowLooks: 'Vzhled',
    rowStores: 'Ukládá',
    rowReads: 'Čte',
    rowWrites: 'Zapisuje',
    rowRuns: 'Spouští',
    whoHeading: 'Kdo ji může používat',
    whoMeta: '{team} · počet lidí: {people}',
    whoRole: 'Může používat · stejně jako v{prev}',
    whoRoleFirst: 'Může používat',
    changeInShare: 'Změnit ve Sdílení',
    checksHeading: 'Kontroly',
    blocksPublishing: 'Blokuje publikování',
    fixInChat: 'Opravit v chatu',
    checksPassed: 'Úspěšné kontroly: {count}',
    show: 'Zobrazit',
    hide: 'Skrýt',
    contractionHeading: 'Tato verze něco odebírá',
    contractionNote: 'Každou položku potvrďte zaškrtnutím. Nic se neodebere, dokud nepublikujete.',
    permission: 'Můžete publikovat, protože máte ve firmě {company} oprávnění k publikování.',
    undoStays: 'Vrácení zpět zůstane po publikování dostupné, protože se nic neodebírá.',
    undoGone: 'Vrácení zpět nebude po publikování dostupné, protože se tím odeberou data.',
    footerAudience: 'Jen členové firmy. Žádné veřejné odkazy.',
    footerBlocked: 'Kontroly blokující publikování: {count}',
    footerBlockedMany: 'Kontroly blokující publikování: {count}',
    footerContraction: 'Pro publikování potvrďte, co se odebere.',
    cancel: 'Zrušit',
    publish: 'Publikovat v{n}',
    publishFirst: 'Publikovat',
    publishing: 'Publikování…',
    doneTitle: '{team} už má v{n}',
    doneFirstTitle: '{team} už má aplikaci {app}',
    doneBody:
      'Počet lidí, kteří od teď používají v{n}: {people}. Jejich záznamy ({entries}) zůstávají beze změny.',
    doneBodyUncounted:
      'Počet lidí, kteří od teď používají v{n}: {people}. Jejich záznamy zůstávají beze změny.',
    doneBodyBare: 'Počet lidí, kteří od teď používají v{n}: {people}.',
    doneFirstBody:
      'Počet lidí, kteří ji teď mohou otevřít: {people}. Jejich záznamy začínají tady.',
    doneWhere: 'v jejich Knihovně a mezi připnutými pro tým',
    doneThey: 'Uvidí {what}, až příště otevřou aplikaci {app}.',
    doneUndoNote: 'Nic nebylo odebráno, takže vrácení zpět zůstává dostupné.',
    undoPublish: 'Vrátit publikování zpět',
    publishUnknownTitle: 'Nevíme, jestli byla v{n} publikována',
    publishUnknownBody:
      '{team} ji možná už má, možná ne. Než budete publikovat znovu, zkontrolujte to, aby se nic nepublikovalo dvakrát.',
    publishFailedTitle: 'v{n} nebyla publikována',
    publishFailedBody: 'Pro {team} se nic nezměnilo. Zkuste to znovu.',
    checkAgain: 'Zkontrolovat znovu',
    tryAgain: 'Zkusit znovu',
    askOwner: 'Zeptat se: {owner}',
    restoreAction: 'Obnovit…',
    copyLink: 'Kopírovat odkaz',
    copied: 'Odkaz zkopírován',
    done: 'Hotovo',
    peopleList: '{names} v {place}',
    peopleAndOthers: '{first}, {second} a další ({count})',
    peopleAndOneOther: '{first}, {second} a 1 další osoba',
    agentPublished: 'Publikováno · v{n} agentem {agent}',
    undo: 'Vrátit zpět',
    restoreVersion: 'Obnovit v{prev}',
    agentPublishedNote:
      'Agent ji publikoval po vlastních kontrolách. Ve Verzích to můžete vrátit zpět nebo obnovit v{prev}.',
    shareTitle: 'Sdílet {app}',
    shareSearch: 'Přidat lidi nebo skupiny z firmy {company}',
    roleUse: 'Může používat',
    roleUseHint: 'Otevírá aplikaci a přidává záznamy. Vidí jen to, co aplikace zobrazuje.',
    roleEdit: 'Může upravovat',
    roleEditHint:
      'Mění aplikaci v chatu; vidí Data, Historii a Verze; může exportovat. Nemůže mazat ani obnovovat data.',
    rolePublish: 'Může publikovat',
    rolePublishHint: 'Jen lidé s oprávněním k publikování: {names}.',
    roleRemove: 'Odebrat',
    owner: 'Vlastník',
    ownerLine: '{you} (vy)',
    ownerPublisher: 'Vlastník · publikující',
    throughConnection: 'Lidé vidí data přes vaše připojení',
    throughConnectionBody:
      'Aplikace ukazuje data ze systému {system} jen v rozsahu toho, co sama zobrazuje, ne vaše ostatní data ze systému {system}.',
    sharePublishDisabledReason: 'Vyžaduje oprávnění k publikování',
    accessTitle: 'Jak se uživatelé dostanou k firemním systémům',
    accessOwnerYours: 'Přes vaše připojení · všichni vidí totéž',
    accessOwnerOther: 'Přes připojení uživatele {owner} · všichni vidí totéž',
    accessViewer: 'Vlastní připojení každého · každý vidí svoje',
    accessConnected: 'Lidé v publiku s připojeným systémem {system}: {k} z {n}',
    accessChange: 'Změnit…',
    accessApplies: 'Změny se projeví při dalším publikování',
    accessNarrowedPill: 'Už není povoleno',
    accessNarrowed:
      'Firma {company} změnila, co mohou sdílené aplikace číst ze systému {system}. Tato část je skrytá, dokud znovu nevyberete a znovu nepublikujete.',
    accessUnavailablePill: 'Připojení není dostupné',
    accessUnavailable: '{owner} musí znovu připojit {system}',
    accessUnavailableTold: 'Upozornili jsme uživatele {owner}',
    unpublishTitle: 'Zrušit publikování aplikace {app}?',
    unpublishBody:
      '{team} ji už nebude moct otevřít. Jejich záznamy ({entries}) zůstanou zachované a vrátí se, pokud ji znovu publikujete.',
    unpublishConfirm: 'Zrušit publikování',
    unpublishBusy: 'Rušení publikování…',
    error: 'Nepovedlo se to. Nic se nezměnilo. Zkuste to znovu.',
    checkChecking: 'Kontrola kódu a balíčků…',
    checkClean: 'V této verzi nebyly nalezeny žádné tajné údaje ani zranitelné balíčky.',
    checkWarnings: '{things}. Publikovat můžete i tak.',
    checkHeldSecret: 'Publikování je pozastavené. V kódu byl nalezen tajný údaj.',
    checkHeldDependency: 'Publikování je pozastavené. Balíček má známou závažnou zranitelnost.',
    checkHeldMany: 'Publikování je pozastavené. Nejdřív je třeba opravit {issues}.',
    checkExpired: 'Tato kontrola už není aktuální.',
    checkIncomplete:
      'Kontrolu se nepodařilo dokončit. Publikování zůstane pozastavené, dokud se nedokončí.',
    checkHeldPrivate: 'Publikování pozastavila bezpečnostní kontrola.',
    checkLastChecked: 'Naposledy zkontrolováno {time}',
    severityCritical: 'Kritická',
    severityHigh: 'Vysoká',
    severityModerate: 'Střední',
    severityLow: 'Nízká',
    severityUnrated: 'Nehodnoceno',
    severityWordCritical: 'kritickou',
    severityWordHigh: 'závažnou',
    severityWordModerate: 'středně závažnou',
    severityWordLow: 'méně závažnou',
    secretApiKey: 'Klíč API v kódu',
    secretPrivateKey: 'Soukromý klíč v kódu',
    secretAccessToken: 'Přístupový token v kódu',
    secretPassword: 'Heslo v kódu',
    secretUnknown: 'Možný tajný údaj v kódu',
    secretWhere: '{area} {file}, řádek {line}',
    secretAreaCode: 'Kód:',
    secretAreaBuilt: 'Sestavené soubory:',
    secretWhy: 'Kdokoli, kdo může aplikaci otevřít, by ho mohl přečíst a použít.',
    secretWhat:
      'Přesuňte ho do připojení nebo do tajného nastavení. Potom klíč u jeho poskytovatele vyměňte, protože už mohl být zkopírován.',
    depTitle: '{package} {version} má známou {severity} zranitelnost',
    depTitleUnrated: '{package} {version} má zranitelnost, která zatím není ohodnocená',
    depWhere: 'Balíček používaný aplikací',
    depWhy: 'Na tuto verzi se vztahuje zveřejněné bezpečnostní upozornění.',
    depViewAdvisory: 'Zobrazit upozornění',
    depAdviceUpdate: 'Aktualizujte na opravenou verzi.',
    depAdviceNoFix:
      'Opravená verze zatím neexistuje. Můžete přijmout riziko nebo balíček odstranit.',
    depAdviceUnrated: 'Aktualizujte ho, nebo počkejte, až bude upozornění ohodnoceno.',
    actionFixAndRecheck: 'Opravit a zkontrolovat znovu',
    actionCheckAgain: 'Zkontrolovat znovu',
    actionReview: 'Prohlédnout',
    actionPublishAnyway: 'Přijmout riziko…',
    publishAnywayTitle: 'Přijmout tuto známou zranitelnost?',
    publishAnywayBody:
      '{package} {version} má {severity} zranitelnost, pro kterou zatím neexistuje oprava. Může to ovlivnit lidi, kteří tuto aplikaci používají. Zaznamená se to s vaším jménem a časem.',
    publishAnywayConfirm: 'Přijmout riziko',
    publishAnywayCancel: 'Zrušit',
    publishAnywayAcknowledged: 'Riziko přijato · potvrdil(a) {name} · {date}',
    fixRequestSecret:
      'Přesunout tajný údaj ({label}) ze souboru {file} do tajného nastavení a zkontrolovat znovu',
    fixRequestPackage: 'Aktualizovat {package} na opravenou verzi a zkontrolovat znovu',
    chatHeldSecret:
      'Kontrola před publikováním našla v kódu tajný údaj. Publikování je pozastavené.',
    chatHeldMany: 'Kontrola před publikováním našla {issues}. Publikování je pozastavené.',
    chatWarnings: 'Kontrola před publikováním našla {things}. Publikovat můžete i tak.',
    chatReview: 'Prohlédnout',
    checkIssues: 'one:problém|few:problémy|many:problému|other:problémů',
    checkThings:
      'one:věc k prohlédnutí|few:věci k prohlédnutí|many:věci k prohlédnutí|other:věcí k prohlédnutí',
  },
  da: {
    title: 'Udgiv {app} v{n}',
    firstTitle: 'Udgiv {app}',
    lead: '{team} kommer til at bruge v{n}. v{prev} bliver i Versioner.',
    firstLead: '{team} kan åbne {app}. Du kan fjerne udgivelsen når som helst.',
    close: 'Luk',
    whatsNew: 'Nyt i v{n}',
    whatsNewPlaceholder: 'Én linje, som folk ser',
    changesHeading: 'Det her ændrer sig for folk',
    rowLooks: 'Udseende',
    rowStores: 'Gemmer',
    rowReads: 'Læser',
    rowWrites: 'Skriver',
    rowRuns: 'Kører',
    whoHeading: 'Hvem kan bruge den',
    whoMeta: '{team} · {people} personer',
    whoRole: 'Kan bruge · som v{prev}',
    whoRoleFirst: 'Kan bruge',
    changeInShare: 'Skift under Del',
    checksHeading: 'Kontroller',
    blocksPublishing: 'Blokerer udgivelse',
    fixInChat: 'Ret i chatten',
    checksPassed: '{count} kontroller bestået',
    show: 'Vis',
    hide: 'Skjul',
    contractionHeading: 'Denne version fjerner noget',
    contractionNote: 'Sæt flueben ved hver for at bekræfte. Intet fjernes, før du udgiver.',
    permission: 'Du kan udgive, fordi du har udgivertilladelsen hos {company}.',
    undoStays: 'Fortryd er stadig tilgængelig efter udgivelsen, fordi intet fjernes.',
    undoGone: 'Fortryd er ikke tilgængelig efter udgivelsen, fordi data bliver fjernet.',
    footerAudience: 'Kun medarbejdere i virksomheden. Ingen offentlige links.',
    footerBlocked: '{count} kontrol blokerer udgivelsen',
    footerBlockedMany: '{count} kontroller blokerer udgivelsen',
    footerContraction: 'Bekræft, hvad der fjernes, for at udgive.',
    cancel: 'Annuller',
    publish: 'Udgiv v{n}',
    publishFirst: 'Udgiv',
    publishing: 'Udgiver…',
    doneTitle: 'v{n} er live for {team}',
    doneFirstTitle: '{app} er live for {team}',
    doneBody:
      '{people} personer bruger v{n} fra nu af. Deres {entries} poster forbliver, som de er.',
    doneBodyUncounted:
      '{people} personer bruger v{n} fra nu af. Deres poster forbliver, som de er.',
    doneBodyBare: '{people} personer bruger v{n} fra nu af.',
    doneFirstBody: '{people} personer kan åbne den nu. Deres poster starter her.',
    doneWhere: 'i deres bibliotek og fastgjort for teamet',
    doneThey: 'De ser {what}, næste gang de åbner {app}.',
    doneUndoNote: 'Intet blev fjernet, så Fortryd er stadig tilgængelig.',
    undoPublish: 'Fortryd udgivelse',
    publishUnknownTitle: 'Vi ved ikke, om v{n} blev udgivet',
    publishUnknownBody:
      '{team} har den måske, måske ikke endnu. Tjek, før du udgiver igen, så intet bliver udgivet to gange.',
    publishFailedTitle: 'v{n} blev ikke udgivet',
    publishFailedBody: 'Intet er ændret for {team}. Prøv igen.',
    checkAgain: 'Tjek igen',
    tryAgain: 'Prøv igen',
    askOwner: 'Spørg {owner}',
    restoreAction: 'Gendan…',
    copyLink: 'Kopiér link',
    copied: 'Link kopieret',
    done: 'Færdig',
    peopleList: '{names} i {place}',
    peopleAndOthers: '{first}, {second} og {count} andre',
    peopleAndOneOther: '{first}, {second} og 1 anden',
    agentPublished: 'Udgivet · v{n} af {agent}',
    undo: 'Fortryd',
    restoreVersion: 'Gendan v{prev}',
    agentPublishedNote:
      'Agenten udgav den med sine kontroller. Fortryd, eller gendan v{prev} under Versioner.',
    shareTitle: 'Del {app}',
    shareSearch: 'Tilføj personer eller grupper hos {company}',
    roleUse: 'Kan bruge',
    roleUseHint: 'Åbner appen og tilføjer poster. Ser kun det, appen viser.',
    roleEdit: 'Kan redigere',
    roleEditHint:
      'Ændrer appen i chatten; ser Data, Historik og Versioner; kan eksportere. Kan ikke rydde eller gendanne data.',
    rolePublish: 'Kan udgive',
    rolePublishHint: 'Kun personer med udgivertilladelsen: {names}.',
    roleRemove: 'Fjern',
    owner: 'Ejer',
    ownerLine: '{you} (dig)',
    ownerPublisher: 'Ejer · udgiver',
    throughConnection: 'Folk ser data via din forbindelse',
    throughConnectionBody:
      'Appen viser kun {system} gennem det, den selv viser, ikke dine andre {system}-data.',
    sharePublishDisabledReason: 'Kræver udgivertilladelsen',
    accessTitle: 'Sådan når seerne virksomhedens systemer',
    accessOwnerYours: 'Via din forbindelse · alle ser det samme',
    accessOwnerOther: 'Via {owner}s forbindelse · alle ser det samme',
    accessViewer: 'Hver persons egen forbindelse · hver ser sit eget',
    accessConnected: '{k} af {n} personer i målgruppen har forbundet {system}',
    accessChange: 'Skift…',
    accessApplies: 'Ændringer gælder, når du udgiver igen',
    accessNarrowedPill: 'Ikke længere tilladt',
    accessNarrowed:
      '{company} har ændret, hvad delte apps må læse fra {system}. Den del er skjult, indtil du vælger igen og udgiver på ny.',
    accessUnavailablePill: 'Forbindelsen er utilgængelig',
    accessUnavailable: '{owner} skal forbinde {system} igen',
    accessUnavailableTold: '{owner} har fået besked',
    unpublishTitle: 'Fjern udgivelsen af {app}?',
    unpublishBody:
      '{team} kan ikke længere åbne den. Deres {entries} poster bevares og kommer tilbage, hvis du udgiver igen.',
    unpublishConfirm: 'Fjern udgivelse',
    unpublishBusy: 'Fjerner udgivelse…',
    error: 'Det virkede ikke. Intet blev ændret. Prøv igen.',
    checkChecking: 'Tjekker koden og pakkerne…',
    checkClean: 'Ingen hemmeligheder eller sårbare pakker fundet i denne version.',
    checkWarnings: '{things}. Du kan stadig udgive.',
    checkHeldSecret: 'Udgivelsen er sat i bero. Der blev fundet en hemmelighed i koden.',
    checkHeldDependency: 'Udgivelsen er sat i bero. En pakke har en kendt alvorlig sårbarhed.',
    checkHeldMany: 'Udgivelsen er sat i bero. {issues} skal rettes først.',
    checkExpired: 'Dette tjek er forældet.',
    checkIncomplete:
      'Tjekket kunne ikke gøres færdigt. Udgivelsen er sat i bero, indtil det lykkes.',
    checkHeldPrivate: 'Udgivelsen er sat i bero af et sikkerhedstjek.',
    checkLastChecked: 'Sidst tjekket {time}',
    severityCritical: 'Kritisk',
    severityHigh: 'Høj',
    severityModerate: 'Moderat',
    severityLow: 'Lav',
    severityUnrated: 'Ikke vurderet',
    severityWordCritical: 'kritisk',
    severityWordHigh: 'alvorlig',
    severityWordModerate: 'moderat',
    severityWordLow: 'mindre',
    secretApiKey: 'API-nøgle i koden',
    secretPrivateKey: 'Privat nøgle i koden',
    secretAccessToken: 'Adgangstoken i koden',
    secretPassword: 'Adgangskode i koden',
    secretUnknown: 'Mulig hemmelighed i koden',
    secretWhere: '{area} {file}, linje {line}',
    secretAreaCode: 'Kode:',
    secretAreaBuilt: 'Byggede filer:',
    secretWhy: 'Alle, der kan åbne appen, kan læse og bruge den.',
    secretWhat:
      'Flyt den til en forbindelse eller en hemmelig indstilling. Udskift derefter nøglen hos udbyderen, for den kan allerede være kopieret.',
    depTitle: '{package} {version} har en kendt {severity} sårbarhed',
    depTitleUnrated: '{package} {version} har en sårbarhed, der endnu ikke er vurderet',
    depWhere: 'Pakke, som appen bruger',
    depWhy: 'En offentliggjort sikkerhedsmeddelelse berører denne version.',
    depViewAdvisory: 'Se meddelelse',
    depAdviceUpdate: 'Opdater til en rettet version.',
    depAdviceNoFix:
      'Der findes endnu ingen rettet version. Du kan acceptere risikoen eller fjerne pakken.',
    depAdviceUnrated: 'Opdater den, eller vent, til meddelelsen er vurderet.',
    actionFixAndRecheck: 'Ret og tjek igen',
    actionCheckAgain: 'Tjek igen',
    actionReview: 'Gennemse',
    actionPublishAnyway: 'Acceptér risikoen…',
    publishAnywayTitle: 'Acceptér denne kendte sårbarhed?',
    publishAnywayBody:
      '{package} {version} har en {severity} sårbarhed, der endnu ikke er rettet. Folk, der bruger denne app, kan blive berørt. Det registreres med dit navn og tidspunktet.',
    publishAnywayConfirm: 'Acceptér risikoen',
    publishAnywayCancel: 'Annuller',
    publishAnywayAcknowledged: 'Risiko accepteret · bekræftet af {name} · {date}',
    fixRequestSecret:
      'Flyt hemmeligheden ({label}) i {file} til en hemmelig indstilling, og tjek igen',
    fixRequestPackage: 'Opdater {package} til en rettet version, og tjek igen',
    chatHeldSecret: 'Udgivelsestjekket fandt en hemmelighed i koden. Udgivelsen er sat i bero.',
    chatHeldMany: 'Udgivelsestjekket fandt {issues}. Udgivelsen er sat i bero.',
    chatWarnings: 'Udgivelsestjekket fandt {things}. Du kan stadig udgive.',
    chatReview: 'Gennemse',
    checkIssues: 'one:problem|other:problemer',
    checkThings: 'one:ting at se på|other:ting at se på',
  },
  de: {
    title: '{app} v{n} veröffentlichen',
    firstTitle: '{app} veröffentlichen',
    lead: '{team} verwendet künftig v{n}. v{prev} bleibt unter Versionen.',
    firstLead:
      '{team} kann {app} dann öffnen. Sie können die Veröffentlichung jederzeit zurückziehen.',
    close: 'Schließen',
    whatsNew: 'Neu in v{n}',
    whatsNewPlaceholder: 'Eine Zeile, die alle sehen',
    changesHeading: 'Was sich für die Nutzer ändert',
    rowLooks: 'Aussehen',
    rowStores: 'Speichert',
    rowReads: 'Liest',
    rowWrites: 'Schreibt',
    rowRuns: 'Führt aus',
    whoHeading: 'Wer sie nutzen kann',
    whoMeta: '{team} · {people} Personen',
    whoRole: 'Kann nutzen · wie bei v{prev}',
    whoRoleFirst: 'Kann nutzen',
    changeInShare: 'Unter Teilen ändern',
    checksHeading: 'Prüfungen',
    blocksPublishing: 'Verhindert die Veröffentlichung',
    fixInChat: 'Im Chat beheben',
    checksPassed: '{count} Prüfungen bestanden',
    show: 'Anzeigen',
    hide: 'Ausblenden',
    contractionHeading: 'Diese Version entfernt etwas',
    contractionNote:
      'Haken Sie jeden Punkt zur Bestätigung ab. Entfernt wird erst, wenn Sie veröffentlichen.',
    permission:
      'Sie können veröffentlichen, weil Sie bei {company} die Veröffentlichungsberechtigung haben.',
    undoStays: 'Rückgängig bleibt nach dem Veröffentlichen verfügbar, weil nichts entfernt wird.',
    undoGone:
      'Rückgängig ist nach dem Veröffentlichen nicht verfügbar, weil dabei Daten entfernt werden.',
    footerAudience: 'Nur Mitglieder des Unternehmens. Keine öffentlichen Links.',
    footerBlocked: '{count} Prüfung verhindert die Veröffentlichung',
    footerBlockedMany: '{count} Prüfungen verhindern die Veröffentlichung',
    footerContraction: 'Bestätigen Sie, was entfernt wird, um zu veröffentlichen.',
    cancel: 'Abbrechen',
    publish: 'v{n} veröffentlichen',
    publishFirst: 'Veröffentlichen',
    publishing: 'Wird veröffentlicht…',
    doneTitle: 'v{n} ist jetzt live für {team}',
    doneFirstTitle: '{app} ist jetzt live für {team}',
    doneBody:
      '{people} Personen nutzen ab jetzt v{n}. Ihre {entries} Einträge bleiben unverändert.',
    doneBodyUncounted: '{people} Personen nutzen ab jetzt v{n}. Ihre Einträge bleiben unverändert.',
    doneBodyBare: '{people} Personen nutzen ab jetzt v{n}.',
    doneFirstBody: '{people} Personen können sie jetzt öffnen. Ihre Einträge beginnen hier.',
    doneWhere: 'in ihrer Bibliothek und für das Team angeheftet',
    doneThey: 'Sie sehen {what}, wenn sie {app} das nächste Mal öffnen.',
    doneUndoNote: 'Es wurde nichts entfernt, daher bleibt Rückgängig verfügbar.',
    undoPublish: 'Veröffentlichung rückgängig machen',
    publishUnknownTitle: 'Wir wissen nicht, ob v{n} veröffentlicht wurde',
    publishUnknownBody:
      '{team} hat sie vielleicht schon, vielleicht noch nicht. Prüfen Sie das, bevor Sie erneut veröffentlichen, damit nichts doppelt veröffentlicht wird.',
    publishFailedTitle: 'v{n} wurde nicht veröffentlicht',
    publishFailedBody: 'Für {team} hat sich nichts geändert. Versuchen Sie es erneut.',
    checkAgain: 'Erneut prüfen',
    tryAgain: 'Erneut versuchen',
    askOwner: '{owner} fragen',
    restoreAction: 'Wiederherstellen…',
    copyLink: 'Link kopieren',
    copied: 'Link kopiert',
    done: 'Fertig',
    peopleList: '{names} aus {place}',
    peopleAndOthers: '{first}, {second} und {count} weitere',
    peopleAndOneOther: '{first}, {second} und 1 weitere Person',
    agentPublished: 'Veröffentlicht · v{n} von {agent}',
    undo: 'Rückgängig',
    restoreVersion: 'v{prev} wiederherstellen',
    agentPublishedNote:
      'Der Agent hat sie mit seinen Prüfungen veröffentlicht. Machen Sie es rückgängig oder stellen Sie v{prev} unter Versionen wieder her.',
    shareTitle: '{app} teilen',
    shareSearch: 'Personen oder Gruppen bei {company} hinzufügen',
    roleUse: 'Kann nutzen',
    roleUseHint: 'Öffnet die App und fügt Einträge hinzu. Sieht nur, was die App anzeigt.',
    roleEdit: 'Kann bearbeiten',
    roleEditHint:
      'Ändert die App im Chat; sieht Daten, Verlauf und Versionen; kann exportieren. Kann keine Daten löschen oder wiederherstellen.',
    rolePublish: 'Kann veröffentlichen',
    rolePublishHint: 'Nur Personen mit der Veröffentlichungsberechtigung: {names}.',
    roleRemove: 'Entfernen',
    owner: 'Inhaber',
    ownerLine: '{you} (Sie)',
    ownerPublisher: 'Inhaber · darf veröffentlichen',
    throughConnection: 'Andere sehen Daten über Ihre Verbindung',
    throughConnectionBody:
      'Die App zeigt {system} nur über das, was sie selbst anzeigt, nicht Ihre übrigen {system}-Daten.',
    sharePublishDisabledReason: 'Erfordert die Veröffentlichungsberechtigung',
    accessTitle: 'Wie Betrachter auf Unternehmenssysteme zugreifen',
    accessOwnerYours: 'Über Ihre Verbindung · alle sehen dasselbe',
    accessOwnerOther: 'Über die Verbindung von {owner} · alle sehen dasselbe',
    accessViewer: 'Eigene Verbindung jeder Person · alle sehen ihre eigenen Daten',
    accessConnected: '{k} von {n} Personen in der Zielgruppe haben {system} verbunden',
    accessChange: 'Ändern…',
    accessApplies: 'Änderungen gelten, wenn Sie erneut veröffentlichen',
    accessNarrowedPill: 'Nicht mehr erlaubt',
    accessNarrowed:
      '{company} hat geändert, was geteilte Apps aus {system} lesen dürfen. Dieser Teil ist ausgeblendet, bis Sie neu auswählen und erneut veröffentlichen.',
    accessUnavailablePill: 'Verbindung nicht verfügbar',
    accessUnavailable: '{owner} muss {system} neu verbinden',
    accessUnavailableTold: '{owner} wurde benachrichtigt',
    unpublishTitle: 'Veröffentlichung von {app} zurückziehen?',
    unpublishBody:
      '{team} kann sie dann nicht mehr öffnen. Die {entries} Einträge bleiben erhalten und sind wieder da, wenn Sie erneut veröffentlichen.',
    unpublishConfirm: 'Veröffentlichung zurückziehen',
    unpublishBusy: 'Wird zurückgezogen…',
    error: 'Das hat nicht geklappt. Es wurde nichts geändert. Versuchen Sie es erneut.',
    checkChecking: 'Code und Pakete werden geprüft…',
    checkClean:
      'In dieser Version wurden keine Geheimnisse und keine Pakete mit Sicherheitslücken gefunden.',
    checkWarnings: '{things}. Sie können trotzdem veröffentlichen.',
    checkHeldSecret: 'Die Veröffentlichung ist angehalten. Im Code wurde ein Geheimnis gefunden.',
    checkHeldDependency:
      'Die Veröffentlichung ist angehalten. Ein Paket hat eine bekannte schwerwiegende Sicherheitslücke.',
    checkHeldMany: 'Die Veröffentlichung ist angehalten. Sie müssen zuerst {issues} beheben.',
    checkExpired: 'Diese Prüfung ist nicht mehr aktuell.',
    checkIncomplete:
      'Die Prüfung konnte nicht abgeschlossen werden. Die Veröffentlichung bleibt angehalten, bis sie abgeschlossen ist.',
    checkHeldPrivate: 'Die Veröffentlichung wird von einer Sicherheitsprüfung angehalten.',
    checkLastChecked: 'Zuletzt geprüft: {time}',
    severityCritical: 'Kritisch',
    severityHigh: 'Hoch',
    severityModerate: 'Mittel',
    severityLow: 'Niedrig',
    severityUnrated: 'Nicht bewertet',
    severityWordCritical: 'kritische',
    severityWordHigh: 'schwerwiegende',
    severityWordModerate: 'mittelschwere',
    severityWordLow: 'geringfügige',
    secretApiKey: 'API-Schlüssel im Code',
    secretPrivateKey: 'Privater Schlüssel im Code',
    secretAccessToken: 'Zugriffstoken im Code',
    secretPassword: 'Passwort im Code',
    secretUnknown: 'Mögliches Geheimnis im Code',
    secretWhere: '{area} {file}, Zeile {line}',
    secretAreaCode: 'Code:',
    secretAreaBuilt: 'Erstellte Dateien:',
    secretWhy: 'Alle, die die App öffnen können, könnten es lesen und verwenden.',
    secretWhat:
      'Verschieben Sie es in eine Verbindung oder eine geheime Einstellung. Ersetzen Sie den Schlüssel danach beim Anbieter, da er bereits kopiert worden sein könnte.',
    depTitle: '{package} {version} hat eine bekannte {severity} Sicherheitslücke',
    depTitleUnrated: '{package} {version} hat eine Sicherheitslücke, die noch nicht bewertet ist',
    depWhere: 'Von der App verwendetes Paket',
    depWhy: 'Ein veröffentlichter Sicherheitshinweis betrifft diese Version.',
    depViewAdvisory: 'Hinweis ansehen',
    depAdviceUpdate: 'Aktualisieren Sie auf eine Version mit Korrektur.',
    depAdviceNoFix:
      'Es gibt noch keine Version mit behobenem Fehler. Sie können das Risiko akzeptieren oder das Paket entfernen.',
    depAdviceUnrated: 'Aktualisieren Sie das Paket oder warten Sie, bis der Hinweis bewertet ist.',
    actionFixAndRecheck: 'Beheben und erneut prüfen',
    actionCheckAgain: 'Erneut prüfen',
    actionReview: 'Überprüfen',
    actionPublishAnyway: 'Risiko akzeptieren…',
    publishAnywayTitle: 'Diese bekannte Sicherheitslücke akzeptieren?',
    publishAnywayBody:
      '{package} {version} hat eine {severity} Sicherheitslücke, für die es noch keine Korrektur gibt. Personen, die diese App verwenden, könnten betroffen sein. Dies wird mit Ihrem Namen und der Uhrzeit protokolliert.',
    publishAnywayConfirm: 'Risiko akzeptieren',
    publishAnywayCancel: 'Abbrechen',
    publishAnywayAcknowledged: 'Risiko akzeptiert · bestätigt von {name} · {date}',
    fixRequestSecret:
      'Geheimnis ({label}) in {file} in eine geheime Einstellung verschieben und erneut prüfen',
    fixRequestPackage: '{package} auf eine Version mit Korrektur aktualisieren und erneut prüfen',
    chatHeldSecret:
      'Die Veröffentlichungsprüfung hat ein Geheimnis im Code gefunden. Die Veröffentlichung ist angehalten.',
    chatHeldMany:
      'Die Veröffentlichungsprüfung hat {issues} gefunden. Die Veröffentlichung ist angehalten.',
    chatWarnings:
      'Die Veröffentlichungsprüfung hat {things} gefunden. Sie können trotzdem veröffentlichen.',
    chatReview: 'Überprüfen',
    checkIssues: 'one:Problem|other:Probleme',
    checkThings: 'one:Punkt zum Ansehen|other:Punkte zum Ansehen',
  },
  el: {
    title: 'Δημοσίευση {app} v{n}',
    firstTitle: 'Δημοσίευση {app}',
    lead: '{team} θα χρησιμοποιεί την v{n}. Η v{prev} παραμένει στις Εκδόσεις.',
    firstLead:
      '{team} θα μπορεί να ανοίγει την εφαρμογή {app}. Μπορείτε να καταργήσετε τη δημοσίευση οποιαδήποτε στιγμή.',
    close: 'Κλείσιμο',
    whatsNew: 'Τι νέο υπάρχει στην v{n}',
    whatsNewPlaceholder: 'Μία γραμμή που θα βλέπουν οι χρήστες',
    changesHeading: 'Τι αλλάζει για τους χρήστες',
    rowLooks: 'Εμφάνιση',
    rowStores: 'Αποθηκεύει',
    rowReads: 'Διαβάζει',
    rowWrites: 'Γράφει',
    rowRuns: 'Εκτελεί',
    whoHeading: 'Ποιοι μπορούν να τη χρησιμοποιούν',
    whoMeta: '{team} · {people} άτομα',
    whoRole: 'Μπορεί να χρησιμοποιεί · όπως στην v{prev}',
    whoRoleFirst: 'Μπορεί να χρησιμοποιεί',
    changeInShare: 'Αλλαγή στην Κοινοποίηση',
    checksHeading: 'Έλεγχοι',
    blocksPublishing: 'Εμποδίζει τη δημοσίευση',
    fixInChat: 'Διόρθωση στη συνομιλία',
    checksPassed: '{count} έλεγχοι ολοκληρώθηκαν με επιτυχία',
    show: 'Εμφάνιση',
    hide: 'Απόκρυψη',
    contractionHeading: 'Αυτή η έκδοση αφαιρεί κάτι',
    contractionNote:
      'Επιλέξτε το καθένα για επιβεβαίωση. Τίποτα δεν αφαιρείται μέχρι να κάνετε δημοσίευση.',
    permission:
      'Μπορείτε να κάνετε δημοσίευση επειδή έχετε το δικαίωμα δημοσίευσης στην {company}.',
    undoStays: 'Η Αναίρεση παραμένει διαθέσιμη μετά τη δημοσίευση, επειδή δεν αφαιρείται τίποτα.',
    undoGone: 'Η Αναίρεση δεν είναι διαθέσιμη μετά τη δημοσίευση, επειδή αυτή αφαιρεί δεδομένα.',
    footerAudience: 'Μόνο για μέλη της εταιρείας. Χωρίς δημόσιους συνδέσμους.',
    footerBlocked: '{count} έλεγχος εμποδίζει τη δημοσίευση',
    footerBlockedMany: '{count} έλεγχοι εμποδίζουν τη δημοσίευση',
    footerContraction: 'Επιβεβαιώστε τι αφαιρείται για να γίνει δημοσίευση.',
    cancel: 'Ακύρωση',
    publish: 'Δημοσίευση v{n}',
    publishFirst: 'Δημοσίευση',
    publishing: 'Γίνεται δημοσίευση…',
    doneTitle: 'Η v{n} είναι διαθέσιμη για {team}',
    doneFirstTitle: 'Η εφαρμογή {app} είναι διαθέσιμη για {team}',
    doneBody:
      '{people} άτομα χρησιμοποιούν πλέον την v{n}. Οι {entries} εγγραφές τους παραμένουν ως έχουν.',
    doneBodyUncounted:
      '{people} άτομα χρησιμοποιούν πλέον την v{n}. Οι εγγραφές τους παραμένουν ως έχουν.',
    doneBodyBare: '{people} άτομα χρησιμοποιούν πλέον την v{n}.',
    doneFirstBody: '{people} άτομα μπορούν να την ανοίξουν τώρα. Οι εγγραφές τους ξεκινούν εδώ.',
    doneWhere: 'στη Βιβλιοθήκη τους και καρφιτσωμένη για την ομάδα',
    doneThey: 'Θα δουν {what} την επόμενη φορά που θα ανοίξουν την εφαρμογή {app}.',
    doneUndoNote: 'Δεν αφαιρέθηκε τίποτα, οπότε η Αναίρεση παραμένει διαθέσιμη.',
    undoPublish: 'Αναίρεση δημοσίευσης',
    publishUnknownTitle: 'Δεν γνωρίζουμε αν δημοσιεύτηκε η v{n}',
    publishUnknownBody:
      'Μπορεί να είναι ήδη διαθέσιμη για {team} ή όχι. Ελέγξτε πριν δημοσιεύσετε ξανά, ώστε να μη δημοσιευτεί τίποτα δύο φορές.',
    publishFailedTitle: 'Η v{n} δεν δημοσιεύτηκε',
    publishFailedBody: 'Δεν άλλαξε τίποτα για {team}. Δοκιμάστε ξανά.',
    checkAgain: 'Νέος έλεγχος',
    tryAgain: 'Δοκιμάστε ξανά',
    askOwner: 'Ερώτηση προς {owner}',
    restoreAction: 'Επαναφορά…',
    copyLink: 'Αντιγραφή συνδέσμου',
    copied: 'Ο σύνδεσμος αντιγράφηκε',
    done: 'Τέλος',
    peopleList: '{names} – {place}',
    peopleAndOthers: '{first}, {second} και {count} ακόμη',
    peopleAndOneOther: '{first}, {second} και 1 ακόμη',
    agentPublished: 'Δημοσιεύτηκε · v{n} από {agent}',
    undo: 'Αναίρεση',
    restoreVersion: 'Επαναφορά v{prev}',
    agentPublishedNote:
      'Ο πράκτορας τη δημοσίευσε με τους ελέγχους της. Κάντε αναίρεση ή επαναφέρετε την v{prev} από τις Εκδόσεις.',
    shareTitle: 'Κοινοποίηση {app}',
    shareSearch: 'Προσθήκη ατόμων ή ομάδων από την {company}',
    roleUse: 'Μπορεί να χρησιμοποιεί',
    roleUseHint:
      'Ανοίγει την εφαρμογή και προσθέτει εγγραφές. Βλέπει μόνο ό,τι δείχνει η εφαρμογή.',
    roleEdit: 'Μπορεί να επεξεργάζεται',
    roleEditHint:
      'Αλλάζει την εφαρμογή στη συνομιλία· βλέπει Δεδομένα, Ιστορικό και Εκδόσεις· μπορεί να κάνει εξαγωγή. Δεν μπορεί να διαγράψει ή να επαναφέρει δεδομένα.',
    rolePublish: 'Μπορεί να δημοσιεύει',
    rolePublishHint: 'Μόνο άτομα με δικαίωμα δημοσίευσης: {names}.',
    roleRemove: 'Αφαίρεση',
    owner: 'Κάτοχος',
    ownerLine: '{you} (εσείς)',
    ownerPublisher: 'Κάτοχος · εκδότης',
    throughConnection: 'Οι χρήστες βλέπουν δεδομένα μέσω της σύνδεσής σας',
    throughConnectionBody:
      'Η εφαρμογή δείχνει το {system} μόνο μέσα από ό,τι εμφανίζει η ίδια, όχι τα υπόλοιπα δεδομένα σας στο {system}.',
    sharePublishDisabledReason: 'Απαιτεί δικαίωμα δημοσίευσης',
    accessTitle: 'Πώς φτάνουν οι θεατές στα συστήματα της εταιρείας',
    accessOwnerYours: 'Μέσω της σύνδεσής σας · όλοι βλέπουν τα ίδια',
    accessOwnerOther: 'Μέσω της σύνδεσης του χρήστη {owner} · όλοι βλέπουν τα ίδια',
    accessViewer: 'Η σύνδεση του καθενός · ο καθένας βλέπει τα δικά του',
    accessConnected: '{k} από {n} άτομα του κοινού έχουν συνδέσει το {system}',
    accessChange: 'Αλλαγή…',
    accessApplies: 'Οι αλλαγές ισχύουν όταν δημοσιεύσετε ξανά',
    accessNarrowedPill: 'Δεν επιτρέπεται πλέον',
    accessNarrowed:
      'Η {company} άλλαξε τι μπορούν να διαβάζουν οι κοινόχρηστες εφαρμογές από το {system}. Αυτό το μέρος είναι κρυφό μέχρι να επιλέξετε ξανά και να δημοσιεύσετε εκ νέου.',
    accessUnavailablePill: 'Η σύνδεση δεν είναι διαθέσιμη',
    accessUnavailable: 'Ο χρήστης {owner} πρέπει να συνδέσει ξανά το {system}',
    accessUnavailableTold: 'Ο χρήστης {owner} έχει ενημερωθεί',
    unpublishTitle: 'Κατάργηση δημοσίευσης της εφαρμογής {app};',
    unpublishBody:
      '{team} δεν θα μπορεί πλέον να την ανοίγει. Οι {entries} εγγραφές τους διατηρούνται και επιστρέφουν αν τη δημοσιεύσετε ξανά.',
    unpublishConfirm: 'Κατάργηση δημοσίευσης',
    unpublishBusy: 'Κατάργηση δημοσίευσης…',
    error: 'Δεν λειτούργησε. Δεν άλλαξε τίποτα. Δοκιμάστε ξανά.',
    checkChecking: 'Έλεγχος του κώδικα και των πακέτων…',
    checkClean: 'Δεν βρέθηκαν μυστικά ή ευάλωτα πακέτα σε αυτή την έκδοση.',
    checkWarnings: '{things}. Μπορείτε να δημοσιεύσετε κανονικά.',
    checkHeldSecret: 'Η δημοσίευση έχει ανασταλεί. Βρέθηκε ένα μυστικό στον κώδικα.',
    checkHeldDependency: 'Η δημοσίευση έχει ανασταλεί. Ένα πακέτο έχει μια γνωστή σοβαρή ευπάθεια.',
    checkHeldMany: 'Η δημοσίευση έχει ανασταλεί. Πρέπει πρώτα να διορθώσετε {issues}.',
    checkExpired: 'Αυτός ο έλεγχος δεν είναι πλέον ενημερωμένος.',
    checkIncomplete:
      'Ο έλεγχος δεν ολοκληρώθηκε. Η δημοσίευση παραμένει σε αναστολή μέχρι να ολοκληρωθεί.',
    checkHeldPrivate: 'Η δημοσίευση έχει ανασταλεί από έλεγχο ασφαλείας.',
    checkLastChecked: 'Τελευταίος έλεγχος: {time}',
    severityCritical: 'Κρίσιμη',
    severityHigh: 'Υψηλή',
    severityModerate: 'Μέτρια',
    severityLow: 'Χαμηλή',
    severityUnrated: 'Χωρίς αξιολόγηση',
    severityWordCritical: 'κρίσιμη',
    severityWordHigh: 'σοβαρή',
    severityWordModerate: 'μέτρια',
    severityWordLow: 'ήπια',
    secretApiKey: 'Κλειδί API στον κώδικα',
    secretPrivateKey: 'Ιδιωτικό κλειδί στον κώδικα',
    secretAccessToken: 'Διακριτικό πρόσβασης στον κώδικα',
    secretPassword: 'Κωδικός πρόσβασης στον κώδικα',
    secretUnknown: 'Πιθανό μυστικό στον κώδικα',
    secretWhere: '{area} {file}, γραμμή {line}',
    secretAreaCode: 'Κώδικας:',
    secretAreaBuilt: 'Μεταγλωττισμένα αρχεία:',
    secretWhy:
      'Όποιος μπορεί να ανοίξει την εφαρμογή θα μπορούσε να το διαβάσει και να το χρησιμοποιήσει.',
    secretWhat:
      'Μετακινήστε το σε μια σύνδεση ή σε μια μυστική ρύθμιση. Στη συνέχεια αντικαταστήστε το κλειδί στον πάροχό του, επειδή μπορεί να έχει ήδη αντιγραφεί.',
    depTitle: 'Το πακέτο {package} {version} έχει μια γνωστή {severity} ευπάθεια',
    depTitleUnrated:
      'Το πακέτο {package} {version} έχει μια ευπάθεια που δεν έχει αξιολογηθεί ακόμη',
    depWhere: 'Πακέτο που χρησιμοποιεί η εφαρμογή',
    depWhy: 'Μια δημοσιευμένη ανακοίνωση ασφαλείας αφορά αυτή την έκδοση.',
    depViewAdvisory: 'Προβολή ανακοίνωσης',
    depAdviceUpdate: 'Ενημερώστε σε μια διορθωμένη έκδοση.',
    depAdviceNoFix:
      'Δεν υπάρχει ακόμη διορθωμένη έκδοση. Μπορείτε να αποδεχτείτε τον κίνδυνο ή να αφαιρέσετε το πακέτο.',
    depAdviceUnrated: 'Ενημερώστε το ή περιμένετε μέχρι να αξιολογηθεί η ανακοίνωση.',
    actionFixAndRecheck: 'Διόρθωση και νέος έλεγχος',
    actionCheckAgain: 'Νέος έλεγχος',
    actionReview: 'Επισκόπηση',
    actionPublishAnyway: 'Αποδοχή κινδύνου…',
    publishAnywayTitle: 'Αποδοχή αυτής της γνωστής ευπάθειας;',
    publishAnywayBody:
      'Το πακέτο {package} {version} έχει μια {severity} ευπάθεια για την οποία δεν υπάρχει ακόμη διόρθωση. Οι χρήστες αυτής της εφαρμογής μπορεί να επηρεαστούν. Η ενέργεια καταγράφεται με το όνομά σας και την ώρα.',
    publishAnywayConfirm: 'Αποδοχή κινδύνου',
    publishAnywayCancel: 'Ακύρωση',
    publishAnywayAcknowledged: 'Ο κίνδυνος έγινε αποδεκτός · επιβεβαιώθηκε από {name} · {date}',
    fixRequestSecret:
      'Μετακίνηση του μυστικού ({label}) από το {file} σε μυστική ρύθμιση και νέος έλεγχος',
    fixRequestPackage: 'Ενημέρωση του {package} σε διορθωμένη έκδοση και νέος έλεγχος',
    chatHeldSecret:
      'Ο έλεγχος δημοσίευσης βρήκε ένα μυστικό στον κώδικα. Η δημοσίευση έχει ανασταλεί.',
    chatHeldMany: 'Ο έλεγχος δημοσίευσης βρήκε {issues}. Η δημοσίευση έχει ανασταλεί.',
    chatWarnings: 'Ο έλεγχος δημοσίευσης βρήκε {things}. Μπορείτε να δημοσιεύσετε κανονικά.',
    chatReview: 'Επισκόπηση',
    checkIssues: 'one:ζήτημα|other:ζητήματα',
    checkThings: 'one:σημείο προς εξέταση|other:σημεία προς εξέταση',
  },
  es: {
    title: 'Publicar {app} v{n}',
    firstTitle: 'Publicar {app}',
    lead: '{team} usará la v{n}. La v{prev} se queda en Versiones.',
    firstLead: '{team} podrá abrir {app}. Puedes retirar la publicación cuando quieras.',
    close: 'Cerrar',
    whatsNew: 'Novedades de la v{n}',
    whatsNewPlaceholder: 'Una línea que verán todos',
    changesHeading: 'Qué cambia para los usuarios',
    rowLooks: 'Aspecto',
    rowStores: 'Almacena',
    rowReads: 'Lee',
    rowWrites: 'Escribe',
    rowRuns: 'Ejecuta',
    whoHeading: 'Quién puede usarla',
    whoMeta: '{team} · {people} personas',
    whoRole: 'Puede usar · igual que en la v{prev}',
    whoRoleFirst: 'Puede usar',
    changeInShare: 'Cambiar en Compartir',
    checksHeading: 'Comprobaciones',
    blocksPublishing: 'Bloquea la publicación',
    fixInChat: 'Corregir en el chat',
    checksPassed: '{count} comprobaciones superadas',
    show: 'Mostrar',
    hide: 'Ocultar',
    contractionHeading: 'Esta versión quita algo',
    contractionNote: 'Marca cada elemento para confirmarlo. No se quita nada hasta que publiques.',
    permission: 'Puedes publicar porque tienes el permiso de publicación en {company}.',
    undoStays: 'Podrás deshacer después de publicar, porque no se quita nada.',
    undoGone: 'No podrás deshacer después de publicar, porque esto elimina datos.',
    footerAudience: 'Solo miembros de la empresa. Sin enlaces públicos.',
    footerBlocked: '{count} comprobación bloquea la publicación',
    footerBlockedMany: '{count} comprobaciones bloquean la publicación',
    footerContraction: 'Confirma qué se quita para poder publicar.',
    cancel: 'Cancelar',
    publish: 'Publicar v{n}',
    publishFirst: 'Publicar',
    publishing: 'Publicando…',
    doneTitle: 'La v{n} ya está disponible para {team}',
    doneFirstTitle: '{app} ya está disponible para {team}',
    doneBody:
      '{people} personas usan la v{n} a partir de ahora. Sus {entries} entradas se quedan como están.',
    doneBodyUncounted:
      '{people} personas usan la v{n} a partir de ahora. Sus entradas se quedan como están.',
    doneBodyBare: '{people} personas usan la v{n} a partir de ahora.',
    doneFirstBody: '{people} personas ya pueden abrirla. Sus entradas empiezan aquí.',
    doneWhere: 'en su Biblioteca y fijada para el equipo',
    doneThey: 'Verán {what} la próxima vez que abran {app}.',
    doneUndoNote: 'No se ha quitado nada, así que puedes deshacer.',
    undoPublish: 'Deshacer publicación',
    publishUnknownTitle: 'No sabemos si la v{n} se ha publicado',
    publishUnknownBody:
      'Puede que {team} ya la tenga o puede que no. Compruébalo antes de volver a publicar, para que nada se publique dos veces.',
    publishFailedTitle: 'La v{n} no se ha publicado',
    publishFailedBody: 'No ha cambiado nada para {team}. Inténtalo de nuevo.',
    checkAgain: 'Volver a comprobar',
    tryAgain: 'Reintentar',
    askOwner: 'Preguntar a {owner}',
    restoreAction: 'Restaurar…',
    copyLink: 'Copiar enlace',
    copied: 'Enlace copiado',
    done: 'Listo',
    peopleList: '{names} en {place}',
    peopleAndOthers: '{first}, {second} y {count} más',
    peopleAndOneOther: '{first}, {second} y 1 más',
    agentPublished: 'Publicada · v{n} por {agent}',
    undo: 'Deshacer',
    restoreVersion: 'Restaurar v{prev}',
    agentPublishedNote:
      'El agente la publicó con sus comprobaciones. Deshaz o restaura la v{prev} desde Versiones.',
    shareTitle: 'Compartir {app}',
    shareSearch: 'Añadir personas o grupos de {company}',
    roleUse: 'Puede usar',
    roleUseHint: 'Abre la app y añade entradas. Solo ve lo que muestra la app.',
    roleEdit: 'Puede editar',
    roleEditHint:
      'Cambia la app en el chat; ve Datos, Historial y Versiones; puede exportar. No puede borrar ni restaurar datos.',
    rolePublish: 'Puede publicar',
    rolePublishHint: 'Solo personas con el permiso de publicación: {names}.',
    roleRemove: 'Quitar',
    owner: 'Propietario',
    ownerLine: '{you} (tú)',
    ownerPublisher: 'Propietario · puede publicar',
    throughConnection: 'Los demás ven los datos a través de tu conexión',
    throughConnectionBody:
      'La app muestra {system} solo a través de lo que ella muestra, no el resto de tus datos de {system}.',
    sharePublishDisabledReason: 'Requiere el permiso de publicación',
    accessTitle: 'Cómo acceden los lectores a los sistemas de la empresa',
    accessOwnerYours: 'A través de tu conexión · todos ven lo mismo',
    accessOwnerOther: 'A través de la conexión de {owner} · todos ven lo mismo',
    accessViewer: 'La conexión de cada persona · cada uno ve lo suyo',
    accessConnected: '{k} de {n} personas del público han conectado {system}',
    accessChange: 'Cambiar…',
    accessApplies: 'Los cambios se aplican cuando vuelvas a publicar',
    accessNarrowedPill: 'Ya no está permitido',
    accessNarrowed:
      '{company} ha cambiado lo que las apps compartidas pueden leer de {system}. Esa parte queda oculta hasta que vuelvas a elegir y publiques de nuevo.',
    accessUnavailablePill: 'Conexión no disponible',
    accessUnavailable: '{owner} tiene que volver a conectar {system}',
    accessUnavailableTold: 'Se ha avisado a {owner}',
    unpublishTitle: '¿Retirar la publicación de {app}?',
    unpublishBody:
      '{team} ya no podrá abrirla. Sus {entries} entradas se conservan y vuelven si publicas de nuevo.',
    unpublishConfirm: 'Retirar publicación',
    unpublishBusy: 'Retirando la publicación…',
    error: 'No ha funcionado. No se ha cambiado nada. Inténtalo de nuevo.',
    checkChecking: 'Comprobando el código y los paquetes…',
    checkClean: 'No se han encontrado secretos ni paquetes vulnerables en esta versión.',
    checkWarnings: '{things}. Puedes publicar igualmente.',
    checkHeldSecret: 'La publicación está retenida. Se ha encontrado un secreto en el código.',
    checkHeldDependency:
      'La publicación está retenida. Un paquete tiene una vulnerabilidad grave conocida.',
    checkHeldMany: 'La publicación está retenida. Primero hay que corregir {issues}.',
    checkExpired: 'Esta comprobación está desactualizada.',
    checkIncomplete:
      'La comprobación no pudo terminar. La publicación sigue retenida hasta que termine.',
    checkHeldPrivate: 'La publicación está retenida por una comprobación de seguridad.',
    checkLastChecked: 'Última comprobación: {time}',
    severityCritical: 'Crítica',
    severityHigh: 'Alta',
    severityModerate: 'Moderada',
    severityLow: 'Baja',
    severityUnrated: 'Sin clasificar',
    severityWordCritical: 'crítica',
    severityWordHigh: 'grave',
    severityWordModerate: 'moderada',
    severityWordLow: 'menor',
    secretApiKey: 'Clave de API en el código',
    secretPrivateKey: 'Clave privada en el código',
    secretAccessToken: 'Token de acceso en el código',
    secretPassword: 'Contraseña en el código',
    secretUnknown: 'Posible secreto en el código',
    secretWhere: '{area} {file}, línea {line}',
    secretAreaCode: 'Código:',
    secretAreaBuilt: 'Archivos compilados:',
    secretWhy: 'Cualquiera que pueda abrir la app podría leerlo y usarlo.',
    secretWhat:
      'Muévelo a una conexión o a un ajuste secreto. Después sustituye la clave en su proveedor, porque puede que ya se haya copiado.',
    depTitle: '{package} {version} tiene una vulnerabilidad {severity} conocida',
    depTitleUnrated: '{package} {version} tiene una vulnerabilidad que aún no está clasificada',
    depWhere: 'Paquete que usa la app',
    depWhy: 'Un aviso de seguridad publicado afecta a esta versión.',
    depViewAdvisory: 'Ver aviso',
    depAdviceUpdate: 'Actualiza a una versión corregida.',
    depAdviceNoFix:
      'Todavía no existe una versión corregida. Puedes aceptar el riesgo o eliminar el paquete.',
    depAdviceUnrated: 'Actualízalo o espera a que el aviso se clasifique.',
    actionFixAndRecheck: 'Corregir y volver a comprobar',
    actionCheckAgain: 'Volver a comprobar',
    actionReview: 'Revisar',
    actionPublishAnyway: 'Aceptar el riesgo…',
    publishAnywayTitle: '¿Aceptar esta vulnerabilidad conocida?',
    publishAnywayBody:
      '{package} {version} tiene una vulnerabilidad {severity} que aún no tiene solución. Las personas que usan esta app podrían verse afectadas. Esto queda registrado con tu nombre y la hora.',
    publishAnywayConfirm: 'Aceptar el riesgo',
    publishAnywayCancel: 'Cancelar',
    publishAnywayAcknowledged: 'Riesgo aceptado · confirmado por {name} · {date}',
    fixRequestSecret:
      'Mover el secreto ({label}) de {file} a un ajuste secreto y volver a comprobar',
    fixRequestPackage: 'Actualizar {package} a una versión corregida y volver a comprobar',
    chatHeldSecret:
      'La comprobación de publicación encontró un secreto en el código. La publicación está retenida.',
    chatHeldMany: 'La comprobación de publicación encontró {issues}. La publicación está retenida.',
    chatWarnings: 'La comprobación de publicación encontró {things}. Puedes publicar igualmente.',
    chatReview: 'Revisar',
    checkIssues: 'one:problema|many:de problemas|other:problemas',
    checkThings: 'one:cosa que revisar|many:de cosas que revisar|other:cosas que revisar',
  },
  et: {
    title: 'Avalda {app} v{n}',
    firstTitle: 'Avalda {app}',
    lead: '{team} hakkab kasutama versiooni v{n}. v{prev} jääb jaotisesse Versioonid.',
    firstLead: '{team} saab avada rakenduse {app}. Avaldamise saad igal ajal lõpetada.',
    close: 'Sulge',
    whatsNew: 'Mis on uut versioonis v{n}',
    whatsNewPlaceholder: 'Üks rida, mida inimesed näevad',
    changesHeading: 'Mis inimeste jaoks muutub',
    rowLooks: 'Välimus',
    rowStores: 'Salvestab',
    rowReads: 'Loeb',
    rowWrites: 'Kirjutab',
    rowRuns: 'Käivitab',
    whoHeading: 'Kes saavad seda kasutada',
    whoMeta: '{team} · {people} inimest',
    whoRole: 'Saab kasutada · nagu v{prev}',
    whoRoleFirst: 'Saab kasutada',
    changeInShare: 'Muuda jaotises Jaga',
    checksHeading: 'Kontrollid',
    blocksPublishing: 'Takistab avaldamist',
    fixInChat: 'Paranda vestluses',
    checksPassed: '{count} kontrolli läbitud',
    show: 'Näita',
    hide: 'Peida',
    contractionHeading: 'See versioon eemaldab midagi',
    contractionNote: 'Kinnitamiseks märgi igaüks. Midagi ei eemaldata enne, kui avaldad.',
    permission: 'Saad avaldada, sest sul on ettevõttes {company} avaldaja õigus.',
    undoStays: 'Tagasivõtmine jääb pärast avaldamist kättesaadavaks, sest midagi ei eemaldata.',
    undoGone: 'Pärast avaldamist ei saa tagasi võtta, sest see eemaldab andmeid.',
    footerAudience: 'Ainult ettevõtte liikmetele. Avalikke linke pole.',
    footerBlocked: '{count} kontroll takistab avaldamist',
    footerBlockedMany: '{count} kontrolli takistavad avaldamist',
    footerContraction: 'Avaldamiseks kinnita, mida eemaldatakse.',
    cancel: 'Tühista',
    publish: 'Avalda v{n}',
    publishFirst: 'Avalda',
    publishing: 'Avaldamine…',
    doneTitle: 'v{n} on nüüd kasutusel: {team}',
    doneFirstTitle: '{app} on nüüd kasutusel: {team}',
    doneBody:
      'Nüüdsest kasutavad {people} inimest versiooni v{n}. Nende {entries} kirjet jäävad muutmata.',
    doneBodyUncounted:
      'Nüüdsest kasutavad {people} inimest versiooni v{n}. Nende kirjed jäävad muutmata.',
    doneBodyBare: 'Nüüdsest kasutavad {people} inimest versiooni v{n}.',
    doneFirstBody: '{people} inimest saavad selle nüüd avada. Nende kirjed algavad siit.',
    doneWhere: 'oma raamatukogus ja meeskonna jaoks kinnitatuna',
    doneThey: 'Nad näevad {what}, kui avavad järgmisel korral rakenduse {app}.',
    doneUndoNote: 'Midagi ei eemaldatud, seega saab endiselt tagasi võtta.',
    undoPublish: 'Võta avaldamine tagasi',
    publishUnknownTitle: 'Me ei tea, kas v{n} avaldati',
    publishUnknownBody:
      '{team} võib selle juba saanud olla, aga ei pruugi. Kontrolli enne uuesti avaldamist, et midagi ei avaldataks kaks korda.',
    publishFailedTitle: 'v{n} jäi avaldamata',
    publishFailedBody: '{team}: midagi ei muutunud. Proovi uuesti.',
    checkAgain: 'Kontrolli uuesti',
    tryAgain: 'Proovi uuesti',
    askOwner: 'Küsi kasutajalt {owner}',
    restoreAction: 'Taasta…',
    copyLink: 'Kopeeri link',
    copied: 'Link kopeeritud',
    done: 'Valmis',
    peopleList: '{names} – {place}',
    peopleAndOthers: '{first}, {second} ja veel {count}',
    peopleAndOneOther: '{first}, {second} ja veel 1',
    agentPublished: 'Avaldatud · v{n}, avaldaja {agent}',
    undo: 'Võta tagasi',
    restoreVersion: 'Taasta v{prev}',
    agentPublishedNote:
      'Agent avaldas selle oma kontrollidega. Võta tagasi või taasta v{prev} jaotisest Versioonid.',
    shareTitle: 'Jaga rakendust {app}',
    shareSearch: 'Lisa inimesi või rühmi ettevõttest {company}',
    roleUse: 'Saab kasutada',
    roleUseHint: 'Avab rakenduse ja lisab kirjeid. Näeb ainult seda, mida rakendus näitab.',
    roleEdit: 'Saab muuta',
    roleEditHint:
      'Muudab rakendust vestluses; näeb jaotisi Andmed, Ajalugu ja Versioonid; saab eksportida. Ei saa andmeid tühjendada ega taastada.',
    rolePublish: 'Saab avaldada',
    rolePublishHint: 'Ainult avaldaja õigusega inimesed: {names}.',
    roleRemove: 'Eemalda',
    owner: 'Omanik',
    ownerLine: '{you} (sina)',
    ownerPublisher: 'Omanik · avaldaja',
    throughConnection: 'Inimesed näevad andmeid sinu ühenduse kaudu',
    throughConnectionBody:
      'Rakendus näitab süsteemist {system} ainult seda, mida ta ise näitab, mitte sinu muid {system}-andmeid.',
    sharePublishDisabledReason: 'Vajab avaldaja õigust',
    accessTitle: 'Kuidas vaatajad ettevõtte süsteemidele ligi pääsevad',
    accessOwnerYours: 'Sinu ühenduse kaudu · kõik näevad sama',
    accessOwnerOther: 'Kasutaja {owner} ühenduse kaudu · kõik näevad sama',
    accessViewer: 'Igaühe oma ühendus · igaüks näeb enda oma',
    accessConnected: '{k} / {n} sihtrühma inimest on ühendanud süsteemi {system}',
    accessChange: 'Muuda…',
    accessApplies: 'Muudatused rakenduvad, kui avaldad uuesti',
    accessNarrowedPill: 'Pole enam lubatud',
    accessNarrowed:
      '{company} muutis, mida jagatud rakendused tohivad süsteemist {system} lugeda. See osa on peidetud, kuni valid uuesti ja avaldad uuesti.',
    accessUnavailablePill: 'Ühendus pole saadaval',
    accessUnavailable: '{owner} peab süsteemi {system} uuesti ühendama',
    accessUnavailableTold: '{owner} on teavitatud',
    unpublishTitle: 'Kas lõpetada rakenduse {app} avaldamine?',
    unpublishBody:
      '{team} ei saa seda enam avada. Nende {entries} kirjet säilitatakse ja need tulevad tagasi, kui avaldad uuesti.',
    unpublishConfirm: 'Lõpeta avaldamine',
    unpublishBusy: 'Avaldamise lõpetamine…',
    error: 'See ei õnnestunud. Midagi ei muudetud. Proovi uuesti.',
    checkChecking: 'Koodi ja pakettide kontrollimine…',
    checkClean: 'Selles versioonis ei leitud saladusi ega haavatavaid pakette.',
    checkWarnings: '{things}. Saad siiski avaldada.',
    checkHeldSecret: 'Avaldamine on peatatud. Koodist leiti saladus.',
    checkHeldDependency: 'Avaldamine on peatatud. Paketis on teadaolev tõsine turvanõrkus.',
    checkHeldMany: 'Avaldamine on peatatud. Kõigepealt tuleb parandada {issues}.',
    checkExpired: 'See kontroll on aegunud.',
    checkIncomplete:
      'Kontrolli ei õnnestunud lõpetada. Avaldamine jääb peatatuks, kuni see õnnestub.',
    checkHeldPrivate: 'Avaldamise on peatanud turvakontroll.',
    checkLastChecked: 'Viimati kontrollitud {time}',
    severityCritical: 'Kriitiline',
    severityHigh: 'Kõrge',
    severityModerate: 'Mõõdukas',
    severityLow: 'Madal',
    severityUnrated: 'Hindamata',
    severityWordCritical: 'kriitiline',
    severityWordHigh: 'tõsine',
    severityWordModerate: 'mõõdukas',
    severityWordLow: 'väike',
    secretApiKey: 'API-võti koodis',
    secretPrivateKey: 'Privaatvõti koodis',
    secretAccessToken: 'Juurdepääsuluba koodis',
    secretPassword: 'Parool koodis',
    secretUnknown: 'Võimalik saladus koodis',
    secretWhere: '{area} {file}, rida {line}',
    secretAreaCode: 'Kood:',
    secretAreaBuilt: 'Ehitatud failid:',
    secretWhy: 'Igaüks, kes saab rakendust avada, võib seda lugeda ja kasutada.',
    secretWhat:
      'Teisalda see ühendusse või salajasse seadesse. Seejärel asenda võti teenusepakkuja juures, sest see võib olla juba kopeeritud.',
    depTitle: 'Paketis {package} {version} on teadaolev {severity} turvanõrkus',
    depTitleUnrated: 'Paketis {package} {version} on turvanõrkus, mida pole veel hinnatud',
    depWhere: 'Rakenduse kasutatav pakett',
    depWhy: 'Avaldatud turvateade puudutab seda versiooni.',
    depViewAdvisory: 'Vaata teadet',
    depAdviceUpdate: 'Uuenda parandatud versioonile.',
    depAdviceNoFix: 'Parandatud versiooni veel pole. Võid riskiga nõustuda või paketi eemaldada.',
    depAdviceUnrated: 'Uuenda seda või oota, kuni teade on hinnatud.',
    actionFixAndRecheck: 'Paranda ja kontrolli uuesti',
    actionCheckAgain: 'Kontrolli uuesti',
    actionReview: 'Vaata üle',
    actionPublishAnyway: 'Nõustu riskiga…',
    publishAnywayTitle: 'Kas nõustud selle teadaoleva turvanõrkusega?',
    publishAnywayBody:
      'Paketis {package} {version} on {severity} turvanõrkus, millele pole veel parandust. See võib mõjutada selle rakenduse kasutajaid. See salvestatakse koos sinu nime ja kellaajaga.',
    publishAnywayConfirm: 'Nõustu riskiga',
    publishAnywayCancel: 'Tühista',
    publishAnywayAcknowledged: 'Riskiga nõustutud · kinnitas {name} · {date}',
    fixRequestSecret:
      'Teisalda saladus ({label}) failist {file} salajasse seadesse ja kontrolli uuesti',
    fixRequestPackage: 'Uuenda pakett {package} parandatud versioonile ja kontrolli uuesti',
    chatHeldSecret: 'Avaldamiskontroll leidis koodist saladuse. Avaldamine on peatatud.',
    chatHeldMany: 'Avaldamiskontrolli käigus leiti {issues}. Avaldamine on peatatud.',
    chatWarnings: 'Avaldamiskontrolli käigus leiti {things}. Saad siiski avaldada.',
    chatReview: 'Vaata üle',
    checkIssues: 'one:probleem|other:probleemi',
    checkThings: 'one:asi, mida üle vaadata|other:asja, mida üle vaadata',
  },
  fi: {
    title: 'Julkaise {app} v{n}',
    firstTitle: 'Julkaise {app}',
    lead: '{team} käyttää jatkossa versiota v{n}. v{prev} säilyy kohdassa Versiot.',
    firstLead: '{team} voi avata sovelluksen {app}. Voit lopettaa julkaisun milloin tahansa.',
    close: 'Sulje',
    whatsNew: 'Uutta versiossa v{n}',
    whatsNewPlaceholder: 'Yksi rivi, jonka ihmiset näkevät',
    changesHeading: 'Mikä muuttuu käyttäjille',
    rowLooks: 'Ulkoasu',
    rowStores: 'Tallentaa',
    rowReads: 'Lukee',
    rowWrites: 'Kirjoittaa',
    rowRuns: 'Suorittaa',
    whoHeading: 'Kuka voi käyttää',
    whoMeta: '{team} · {people} henkilöä',
    whoRole: 'Voi käyttää · kuten v{prev}',
    whoRoleFirst: 'Voi käyttää',
    changeInShare: 'Muuta kohdassa Jaa',
    checksHeading: 'Tarkistukset',
    blocksPublishing: 'Estää julkaisun',
    fixInChat: 'Korjaa chatissa',
    checksPassed: '{count} tarkistusta läpäisty',
    show: 'Näytä',
    hide: 'Piilota',
    contractionHeading: 'Tämä versio poistaa jotain',
    contractionNote: 'Vahvista valitsemalla jokainen. Mitään ei poisteta ennen kuin julkaiset.',
    permission: 'Voit julkaista, koska sinulla on julkaisijan oikeus organisaatiossa {company}.',
    undoStays: 'Kumoa on käytettävissä julkaisun jälkeen, koska mitään ei poisteta.',
    undoGone: 'Kumoa ei ole käytettävissä julkaisun jälkeen, koska tämä poistaa tietoja.',
    footerAudience: 'Vain yrityksen jäsenille. Ei julkisia linkkejä.',
    footerBlocked: '{count} tarkistus estää julkaisun',
    footerBlockedMany: '{count} tarkistusta estää julkaisun',
    footerContraction: 'Vahvista poistettavat kohteet, niin voit julkaista.',
    cancel: 'Peruuta',
    publish: 'Julkaise v{n}',
    publishFirst: 'Julkaise',
    publishing: 'Julkaistaan…',
    doneTitle: 'v{n} on nyt käytössä: {team}',
    doneFirstTitle: '{app} on nyt käytössä: {team}',
    doneBody:
      '{people} henkilöä käyttää nyt versiota v{n}. Heidän {entries} merkintäänsä säilyy ennallaan.',
    doneBodyUncounted:
      '{people} henkilöä käyttää nyt versiota v{n}. Heidän merkintänsä säilyvät ennallaan.',
    doneBodyBare: '{people} henkilöä käyttää nyt versiota v{n}.',
    doneFirstBody: '{people} henkilöä voi avata sen nyt. Heidän merkintänsä alkavat tästä.',
    doneWhere: 'kirjastossaan ja tiimille kiinnitettynä',
    doneThey: 'He näkevät {what}, kun he seuraavan kerran avaavat sovelluksen {app}.',
    doneUndoNote: 'Mitään ei poistettu, joten Kumoa on yhä käytettävissä.',
    undoPublish: 'Kumoa julkaisu',
    publishUnknownTitle: 'Emme tiedä, julkaistiinko v{n}',
    publishUnknownBody:
      '{team} ei ehkä ole vielä saanut sitä. Tarkista ennen kuin julkaiset uudelleen, jotta mitään ei julkaista kahdesti.',
    publishFailedTitle: 'Versiota v{n} ei julkaistu',
    publishFailedBody: '{team}: mikään ei muuttunut. Yritä uudelleen.',
    checkAgain: 'Tarkista uudelleen',
    tryAgain: 'Yritä uudelleen',
    askOwner: 'Kysy käyttäjältä {owner}',
    restoreAction: 'Palauta…',
    copyLink: 'Kopioi linkki',
    copied: 'Linkki kopioitu',
    done: 'Valmis',
    peopleList: '{names} – {place}',
    peopleAndOthers: '{first}, {second} ja {count} muuta',
    peopleAndOneOther: '{first}, {second} ja 1 muu',
    agentPublished: 'Julkaistu · v{n}, julkaisija {agent}',
    undo: 'Kumoa',
    restoreVersion: 'Palauta v{prev}',
    agentPublishedNote:
      'Agentti julkaisi sen omine tarkistuksineen. Kumoa tai palauta v{prev} kohdasta Versiot.',
    shareTitle: 'Jaa {app}',
    shareSearch: 'Lisää henkilöitä tai ryhmiä organisaatiosta {company}',
    roleUse: 'Voi käyttää',
    roleUseHint: 'Avaa sovelluksen ja lisää merkintöjä. Näkee vain sen, mitä sovellus näyttää.',
    roleEdit: 'Voi muokata',
    roleEditHint:
      'Muuttaa sovellusta chatissa; näkee kohdat Tiedot, Historia ja Versiot; voi viedä tietoja. Ei voi tyhjentää eikä palauttaa tietoja.',
    rolePublish: 'Voi julkaista',
    rolePublishHint: 'Vain henkilöt, joilla on julkaisijan oikeus: {names}.',
    roleRemove: 'Poista',
    owner: 'Omistaja',
    ownerLine: '{you} (sinä)',
    ownerPublisher: 'Omistaja · julkaisija',
    throughConnection: 'Ihmiset näkevät tiedot yhteytesi kautta',
    throughConnectionBody:
      'Sovellus näyttää järjestelmästä {system} vain sen, mitä se itse näyttää, ei muita {system}-tietojasi.',
    sharePublishDisabledReason: 'Vaatii julkaisijan oikeuden',
    accessTitle: 'Miten katsojat pääsevät yrityksen järjestelmiin',
    accessOwnerYours: 'Sinun yhteytesi kautta · kaikki näkevät saman',
    accessOwnerOther: 'Käyttäjän {owner} yhteyden kautta · kaikki näkevät saman',
    accessViewer: 'Jokaisen oma yhteys · kukin näkee omansa',
    accessConnected: '{k}/{n} kohdeyleisön henkilöstä on yhdistänyt järjestelmän {system}',
    accessChange: 'Muuta…',
    accessApplies: 'Muutokset tulevat voimaan, kun julkaiset uudelleen',
    accessNarrowedPill: 'Ei enää sallittu',
    accessNarrowed:
      '{company} muutti sitä, mitä jaetut sovellukset saavat lukea järjestelmästä {system}. Se osa on piilotettu, kunnes valitset uudelleen ja julkaiset uudelleen.',
    accessUnavailablePill: 'Yhteys ei ole käytettävissä',
    accessUnavailable: '{owner} joutuu yhdistämään järjestelmän {system} uudelleen',
    accessUnavailableTold: '{owner} on saanut tiedon',
    unpublishTitle: 'Lopetetaanko sovelluksen {app} julkaisu?',
    unpublishBody:
      '{team} ei voi enää avata sitä. Heidän {entries} merkintäänsä säilyy, ja ne palaavat, jos julkaiset uudelleen.',
    unpublishConfirm: 'Lopeta julkaisu',
    unpublishBusy: 'Lopetetaan julkaisua…',
    error: 'Se ei onnistunut. Mitään ei muutettu. Yritä uudelleen.',
    checkChecking: 'Tarkistetaan koodia ja paketteja…',
    checkClean: 'Tästä versiosta ei löytynyt salaisuuksia eikä haavoittuvia paketteja.',
    checkWarnings: '{things}. Voit silti julkaista.',
    checkHeldSecret: 'Julkaisu on pysäytetty. Koodista löytyi salaisuus.',
    checkHeldDependency: 'Julkaisu on pysäytetty. Paketissa on tunnettu vakava haavoittuvuus.',
    checkHeldMany: 'Julkaisu on pysäytetty. {issues} on korjattava ensin.',
    checkExpired: 'Tämä tarkistus on vanhentunut.',
    checkIncomplete:
      'Tarkistusta ei saatu valmiiksi. Julkaisu pysyy pysäytettynä, kunnes se valmistuu.',
    checkHeldPrivate: 'Tietoturvatarkistus on pysäyttänyt julkaisun.',
    checkLastChecked: 'Tarkistettu viimeksi {time}',
    severityCritical: 'Kriittinen',
    severityHigh: 'Korkea',
    severityModerate: 'Kohtalainen',
    severityLow: 'Matala',
    severityUnrated: 'Ei luokiteltu',
    severityWordCritical: 'kriittinen',
    severityWordHigh: 'vakava',
    severityWordModerate: 'kohtalainen',
    severityWordLow: 'vähäinen',
    secretApiKey: 'API-avain koodissa',
    secretPrivateKey: 'Yksityinen avain koodissa',
    secretAccessToken: 'Käyttöoikeustunnus koodissa',
    secretPassword: 'Salasana koodissa',
    secretUnknown: 'Mahdollinen salaisuus koodissa',
    secretWhere: '{area} {file}, rivi {line}',
    secretAreaCode: 'Koodi:',
    secretAreaBuilt: 'Käännetyt tiedostot:',
    secretWhy: 'Kuka tahansa, joka voi avata sovelluksen, voisi lukea ja käyttää sitä.',
    secretWhat:
      'Siirrä se yhteyteen tai salaiseen asetukseen. Vaihda sitten avain palveluntarjoajalla, koska se on ehkä jo kopioitu.',
    depTitle: 'Paketissa {package} {version} on tunnettu {severity} haavoittuvuus',
    depTitleUnrated: 'Paketissa {package} {version} on haavoittuvuus, jota ei ole vielä luokiteltu',
    depWhere: 'Sovelluksen käyttämä paketti',
    depWhy: 'Julkaistu tietoturvatiedote koskee tätä versiota.',
    depViewAdvisory: 'Näytä tiedote',
    depAdviceUpdate: 'Päivitä korjattuun versioon.',
    depAdviceNoFix: 'Korjattua versiota ei vielä ole. Voit hyväksyä riskin tai poistaa paketin.',
    depAdviceUnrated: 'Päivitä se tai odota, kunnes tiedote on luokiteltu.',
    actionFixAndRecheck: 'Korjaa ja tarkista uudelleen',
    actionCheckAgain: 'Tarkista uudelleen',
    actionReview: 'Tarkastele',
    actionPublishAnyway: 'Hyväksy riski…',
    publishAnywayTitle: 'Hyväksytäänkö tämä tunnettu haavoittuvuus?',
    publishAnywayBody:
      'Paketissa {package} {version} on {severity} haavoittuvuus, johon ei ole vielä korjausta. Se voi vaikuttaa tämän sovelluksen käyttäjiin. Tästä tallennetaan merkintä nimelläsi ja kellonajalla.',
    publishAnywayConfirm: 'Hyväksy riski',
    publishAnywayCancel: 'Peruuta',
    publishAnywayAcknowledged: 'Riski hyväksytty · vahvistanut {name} · {date}',
    fixRequestSecret:
      'Siirrä salaisuus ({label}) tiedostosta {file} salaiseen asetukseen ja tarkista uudelleen',
    fixRequestPackage: 'Päivitä paketti {package} korjattuun versioon ja tarkista uudelleen',
    chatHeldSecret: 'Julkaisutarkistus löysi koodista salaisuuden. Julkaisu on pysäytetty.',
    chatHeldMany: 'Julkaisutarkistuksessa löytyi {issues}. Julkaisu on pysäytetty.',
    chatWarnings: 'Julkaisutarkistuksessa löytyi {things}. Voit silti julkaista.',
    chatReview: 'Tarkastele',
    checkIssues: 'one:ongelma|other:ongelmaa',
    checkThings: 'one:tarkasteltava kohta|other:tarkasteltavaa kohtaa',
  },
  fr: {
    title: 'Publier {app} v{n}',
    firstTitle: 'Publier {app}',
    lead: '{team} utilisera la v{n}. La v{prev} reste dans Versions.',
    firstLead: '{team} pourra ouvrir {app}. Vous pouvez retirer la publication à tout moment.',
    close: 'Fermer',
    whatsNew: 'Nouveautés de la v{n}',
    whatsNewPlaceholder: 'Une ligne que tout le monde verra',
    changesHeading: 'Ce qui change pour les utilisateurs',
    rowLooks: 'Apparence',
    rowStores: 'Stocke',
    rowReads: 'Lit',
    rowWrites: 'Écrit',
    rowRuns: 'Exécute',
    whoHeading: 'Qui peut l’utiliser',
    whoMeta: '{team} · {people} personnes',
    whoRole: 'Peut utiliser · comme pour la v{prev}',
    whoRoleFirst: 'Peut utiliser',
    changeInShare: 'Modifier dans Partager',
    checksHeading: 'Vérifications',
    blocksPublishing: 'Bloque la publication',
    fixInChat: 'Corriger dans le chat',
    checksPassed: '{count} vérifications réussies',
    show: 'Afficher',
    hide: 'Masquer',
    contractionHeading: 'Cette version supprime quelque chose',
    contractionNote:
      'Cochez chaque élément pour confirmer. Rien n’est supprimé avant la publication.',
    permission: 'Vous pouvez publier, car vous avez l’autorisation de publication chez {company}.',
    undoStays: 'L’annulation reste possible après la publication, car rien n’est supprimé.',
    undoGone:
      'L’annulation ne sera pas possible après la publication, car des données sont supprimées.',
    footerAudience: 'Membres de l’entreprise uniquement. Aucun lien public.',
    footerBlocked: '{count} vérification bloque la publication',
    footerBlockedMany: '{count} vérifications bloquent la publication',
    footerContraction: 'Confirmez ce qui est supprimé pour publier.',
    cancel: 'Annuler',
    publish: 'Publier la v{n}',
    publishFirst: 'Publier',
    publishing: 'Publication…',
    doneTitle: 'La v{n} est en ligne pour {team}',
    doneFirstTitle: '{app} est en ligne pour {team}',
    doneBody:
      '{people} personnes utilisent désormais la v{n}. Leurs {entries} entrées restent inchangées.',
    doneBodyUncounted:
      '{people} personnes utilisent désormais la v{n}. Leurs entrées restent inchangées.',
    doneBodyBare: '{people} personnes utilisent désormais la v{n}.',
    doneFirstBody:
      '{people} personnes peuvent l’ouvrir dès maintenant. Leurs entrées commencent ici.',
    doneWhere: 'dans leur Bibliothèque et épinglée pour l’équipe',
    doneThey: 'Ils verront {what} la prochaine fois qu’ils ouvriront {app}.',
    doneUndoNote: 'Rien n’a été supprimé : l’annulation reste donc possible.',
    undoPublish: 'Annuler la publication',
    publishUnknownTitle: 'Nous ne savons pas si la v{n} a été publiée',
    publishUnknownBody:
      '{team} l’a peut-être déjà, peut-être pas. Vérifiez avant de publier à nouveau, pour que rien ne soit publié deux fois.',
    publishFailedTitle: 'La v{n} n’a pas été publiée',
    publishFailedBody: 'Rien n’a changé pour {team}. Réessayez.',
    checkAgain: 'Vérifier à nouveau',
    tryAgain: 'Réessayer',
    askOwner: 'Demander à {owner}',
    restoreAction: 'Restaurer…',
    copyLink: 'Copier le lien',
    copied: 'Lien copié',
    done: 'Terminé',
    peopleList: '{names} dans {place}',
    peopleAndOthers: '{first}, {second} et {count} autres',
    peopleAndOneOther: '{first}, {second} et 1 autre',
    agentPublished: 'Publiée · v{n} par {agent}',
    undo: 'Annuler',
    restoreVersion: 'Restaurer la v{prev}',
    agentPublishedNote:
      'L’agent l’a publiée avec ses vérifications. Annulez ou restaurez la v{prev} depuis Versions.',
    shareTitle: 'Partager {app}',
    shareSearch: 'Ajouter des personnes ou des groupes de {company}',
    roleUse: 'Peut utiliser',
    roleUseHint: 'Ouvre l’app et ajoute des entrées. Ne voit que ce que l’app affiche.',
    roleEdit: 'Peut modifier',
    roleEditHint:
      'Modifie l’app dans le chat ; voit Données, Historique et Versions ; peut exporter. Ne peut pas effacer ni restaurer les données.',
    rolePublish: 'Peut publier',
    rolePublishHint: 'Uniquement les personnes ayant l’autorisation de publication : {names}.',
    roleRemove: 'Retirer',
    owner: 'Propriétaire',
    ownerLine: '{you} (vous)',
    ownerPublisher: 'Propriétaire · peut publier',
    throughConnection: 'Les utilisateurs voient les données via votre connexion',
    throughConnectionBody:
      'L’app montre {system} uniquement à travers ce qu’elle affiche, pas vos autres données {system}.',
    sharePublishDisabledReason: 'Nécessite l’autorisation de publication',
    accessTitle: 'Comment les lecteurs accèdent aux systèmes de l’entreprise',
    accessOwnerYours: 'Via votre connexion · tout le monde voit la même chose',
    accessOwnerOther: 'Via la connexion de {owner} · tout le monde voit la même chose',
    accessViewer: 'La connexion de chaque personne · chacun voit ses propres données',
    accessConnected: '{k} personnes sur {n} dans l’audience ont connecté {system}',
    accessChange: 'Modifier…',
    accessApplies: 'Les modifications s’appliquent quand vous publiez à nouveau',
    accessNarrowedPill: 'Plus autorisé',
    accessNarrowed:
      '{company} a modifié ce que les apps partagées peuvent lire dans {system}. Cette partie reste masquée tant que vous n’avez pas refait votre choix et republié.',
    accessUnavailablePill: 'Connexion indisponible',
    accessUnavailable: '{owner} doit reconnecter {system}',
    accessUnavailableTold: '{owner} a été prévenu',
    unpublishTitle: 'Retirer la publication de {app} ?',
    unpublishBody:
      '{team} ne pourra plus l’ouvrir. Les {entries} entrées sont conservées et reviennent si vous publiez à nouveau.',
    unpublishConfirm: 'Retirer la publication',
    unpublishBusy: 'Retrait de la publication…',
    error: 'Cela n’a pas fonctionné. Rien n’a été modifié. Réessayez.',
    checkChecking: 'Vérification du code et des paquets…',
    checkClean: 'Aucun secret ni paquet vulnérable trouvé dans cette version.',
    checkWarnings: '{things}. Vous pouvez quand même publier.',
    checkHeldSecret: 'La publication est suspendue. Un secret a été trouvé dans le code.',
    checkHeldDependency:
      'La publication est suspendue. Un paquet présente une vulnérabilité grave connue.',
    checkHeldMany: 'La publication est suspendue. Vous devez d’abord corriger {issues}.',
    checkExpired: 'Cette vérification n’est plus à jour.',
    checkIncomplete:
      'La vérification n’a pas pu aboutir. La publication reste suspendue jusqu’à ce qu’elle aboutisse.',
    checkHeldPrivate: 'La publication est suspendue par une vérification de sécurité.',
    checkLastChecked: 'Dernière vérification : {time}',
    severityCritical: 'Critique',
    severityHigh: 'Élevée',
    severityModerate: 'Modérée',
    severityLow: 'Faible',
    severityUnrated: 'Non évaluée',
    severityWordCritical: 'critique',
    severityWordHigh: 'grave',
    severityWordModerate: 'modérée',
    severityWordLow: 'mineure',
    secretApiKey: 'Clé d’API dans le code',
    secretPrivateKey: 'Clé privée dans le code',
    secretAccessToken: 'Jeton d’accès dans le code',
    secretPassword: 'Mot de passe dans le code',
    secretUnknown: 'Secret possible dans le code',
    secretWhere: '{area} {file}, ligne {line}',
    secretAreaCode: 'Code :',
    secretAreaBuilt: 'Fichiers compilés :',
    secretWhy: 'Toute personne pouvant ouvrir l’app pourrait le lire et l’utiliser.',
    secretWhat:
      'Déplacez-le dans une connexion ou un paramètre secret. Remplacez ensuite la clé auprès de son fournisseur, car elle a peut-être déjà été copiée.',
    depTitle: '{package} {version} présente une vulnérabilité {severity} connue',
    depTitleUnrated: '{package} {version} présente une vulnérabilité qui n’est pas encore évaluée',
    depWhere: 'Paquet utilisé par l’app',
    depWhy: 'Un avis de sécurité publié concerne cette version.',
    depViewAdvisory: 'Voir l’avis',
    depAdviceUpdate: 'Mettez à jour vers une version corrigée.',
    depAdviceNoFix:
      'Aucune version corrigée n’existe encore. Vous pouvez accepter le risque ou supprimer le paquet.',
    depAdviceUnrated: 'Mettez-le à jour ou attendez que l’avis soit évalué.',
    actionFixAndRecheck: 'Corriger et vérifier à nouveau',
    actionCheckAgain: 'Vérifier à nouveau',
    actionReview: 'Examiner',
    actionPublishAnyway: 'Accepter le risque…',
    publishAnywayTitle: 'Accepter cette vulnérabilité connue ?',
    publishAnywayBody:
      '{package} {version} présente une vulnérabilité {severity} sans correctif pour l’instant. Les personnes qui utilisent cette app pourraient être concernées. Cette action est enregistrée avec votre nom et l’heure.',
    publishAnywayConfirm: 'Accepter le risque',
    publishAnywayCancel: 'Annuler',
    publishAnywayAcknowledged: 'Risque accepté · confirmé par {name} · {date}',
    fixRequestSecret:
      'Déplacer le secret ({label}) de {file} dans un paramètre secret et vérifier à nouveau',
    fixRequestPackage: 'Mettre à jour {package} vers une version corrigée et vérifier à nouveau',
    chatHeldSecret:
      'La vérification avant publication a trouvé un secret dans le code. La publication est suspendue.',
    chatHeldMany:
      'La vérification avant publication a trouvé {issues}. La publication est suspendue.',
    chatWarnings:
      'La vérification avant publication a trouvé {things}. Vous pouvez quand même publier.',
    chatReview: 'Examiner',
    checkIssues: 'one:problème|many:de problèmes|other:problèmes',
    checkThings: 'one:point à examiner|many:de points à examiner|other:points à examiner',
  },
  ga: {
    title: 'Foilsigh {app} v{n}',
    firstTitle: 'Foilsigh {app}',
    lead: 'Úsáidfidh {team} v{n}. Fanfaidh v{prev} i Leaganacha.',
    firstLead: 'Beidh {team} in ann {app} a oscailt. Is féidir leat í a dhífhoilsiú am ar bith.',
    close: 'Dún',
    whatsNew: 'Cad atá nua in v{n}',
    whatsNewPlaceholder: 'Líne amháin a fheicfidh daoine',
    changesHeading: 'Cad a athraíonn do dhaoine',
    rowLooks: 'Cuma',
    rowStores: 'Stórálann',
    rowReads: 'Léann',
    rowWrites: 'Scríobhann',
    rowRuns: 'Ritheann',
    whoHeading: 'Cé atá in ann í a úsáid',
    whoMeta: '{team} · daoine: {people}',
    whoRole: 'Cead úsáide · mar a bhí in v{prev}',
    whoRoleFirst: 'Cead úsáide',
    changeInShare: 'Athraigh in Comhroinn',
    checksHeading: 'Seiceálacha',
    blocksPublishing: 'Cuireann bac ar fhoilsiú',
    fixInChat: 'Ceartaigh sa chomhrá',
    checksPassed: 'Seiceálacha a d’éirigh leo: {count}',
    show: 'Taispeáin',
    hide: 'Folaigh',
    contractionHeading: 'Baineann an leagan seo rud éigin',
    contractionNote:
      'Cuir tic le gach ceann acu chun é a dheimhniú. Ní bhainfear aon rud go bhfoilsíonn tú.',
    permission: 'Is féidir leat foilsiú mar go bhfuil cead foilsitheora agat in {company}.',
    undoStays: 'Beidh Cealaigh ar fáil fós tar éis foilsithe, mar nach mbaintear aon rud.',
    undoGone: 'Ní bheidh Cealaigh ar fáil tar éis foilsithe, mar go mbaineann sé seo sonraí.',
    footerAudience: 'Baill an chomhlachta amháin. Gan naisc phoiblí.',
    footerBlocked: 'Seiceálacha a chuireann bac ar fhoilsiú: {count}',
    footerBlockedMany: 'Seiceálacha a chuireann bac ar fhoilsiú: {count}',
    footerContraction: 'Deimhnigh cad a bhaintear chun foilsiú.',
    cancel: 'Cuir ar ceal',
    publish: 'Foilsigh v{n}',
    publishFirst: 'Foilsigh',
    publishing: 'Á fhoilsiú…',
    doneTitle: 'Tá v{n} beo do {team}',
    doneFirstTitle: 'Tá {app} beo do {team}',
    doneBody:
      'Daoine a úsáidfidh v{n} as seo amach: {people}. Fanann a gcuid iontrálacha ({entries}) mar atá.',
    doneBodyUncounted:
      'Daoine a úsáidfidh v{n} as seo amach: {people}. Fanann a gcuid iontrálacha mar atá.',
    doneBodyBare: 'Daoine a úsáidfidh v{n} as seo amach: {people}.',
    doneFirstBody:
      'Daoine atá in ann í a oscailt anois: {people}. Tosaíonn a gcuid iontrálacha anseo.',
    doneWhere: 'ina Leabharlann agus greamaithe don fhoireann',
    doneThey: 'Feicfidh siad {what} an chéad uair eile a osclóidh siad {app}.',
    doneUndoNote: 'Níor baineadh aon rud, mar sin tá Cealaigh ar fáil fós.',
    undoPublish: 'Cealaigh an foilsiú',
    publishUnknownTitle: 'Níl a fhios againn ar foilsíodh v{n}',
    publishUnknownBody:
      'B’fhéidir go bhfuil sé ag {team} cheana, nó b’fhéidir nach bhfuil. Seiceáil sula bhfoilsíonn tú arís, ionas nach bhfoilseofar aon rud faoi dhó.',
    publishFailedTitle: 'Níor foilsíodh v{n}',
    publishFailedBody: 'Níor athraigh aon rud do {team}. Bain triail eile as.',
    checkAgain: 'Seiceáil arís',
    tryAgain: 'Bain triail eile as',
    askOwner: 'Fiafraigh de {owner}',
    restoreAction: 'Athchóirigh…',
    copyLink: 'Cóipeáil an nasc',
    copied: 'Cóipeáladh an nasc',
    done: 'Déanta',
    peopleList: '{names} ({place})',
    peopleAndOthers: '{first}, {second} agus daoine eile ({count})',
    peopleAndOneOther: '{first}, {second} agus duine amháin eile',
    agentPublished: 'Foilsithe · v{n} ag {agent}',
    undo: 'Cealaigh',
    restoreVersion: 'Athchóirigh v{prev}',
    agentPublishedNote:
      'D’fhoilsigh an gníomhaire í lena chuid seiceálacha. Cealaigh nó athchóirigh v{prev} ó Leaganacha.',
    shareTitle: 'Comhroinn {app}',
    shareSearch: 'Cuir daoine nó grúpaí ó {company} leis',
    roleUse: 'Cead úsáide',
    roleUseHint:
      'Osclaíonn sé an aip agus cuireann sé iontrálacha léi. Ní fheiceann sé ach a dtaispeánann an aip.',
    roleEdit: 'Cead eagarthóireachta',
    roleEditHint:
      'Athraíonn sé an aip sa chomhrá; feiceann sé Sonraí, Stair agus Leaganacha; is féidir leis easpórtáil. Ní féidir leis sonraí a ghlanadh ná a athchóiriú.',
    rolePublish: 'Cead foilsithe',
    rolePublishHint: 'Daoine a bhfuil cead foilsitheora acu amháin: {names}.',
    roleRemove: 'Bain',
    owner: 'Úinéir',
    ownerLine: '{you} (tusa)',
    ownerPublisher: 'Úinéir · foilsitheoir',
    throughConnection: 'Feiceann daoine sonraí trí do cheangal',
    throughConnectionBody:
      'Ní thaispeánann an aip {system} ach tríd an méid a thaispeánann sí, ní do chuid sonraí eile {system}.',
    sharePublishDisabledReason: 'Teastaíonn cead foilsitheora',
    accessTitle: 'Conas a shroicheann breathnóirí córais an chomhlachta',
    accessOwnerYours: 'Trí do cheangal · feiceann gach duine an rud céanna',
    accessOwnerOther: 'Trí cheangal {owner} · feiceann gach duine an rud céanna',
    accessViewer: 'Ceangal gach duine féin · feiceann gach duine a chuid féin',
    accessConnected: 'Daoine sa lucht féachana a cheangail {system}: {k} as {n}',
    accessChange: 'Athraigh…',
    accessApplies: 'Beidh na hathruithe i bhfeidhm nuair a fhoilseoidh tú arís',
    accessNarrowedPill: 'Níl cead ann a thuilleadh',
    accessNarrowed:
      'D’athraigh {company} cad is féidir le haipeanna comhroinnte a léamh ó {system}. Tá an chuid sin i bhfolach go roghnóidh tú arís agus go bhfoilseoidh tú arís.',
    accessUnavailablePill: 'Níl an ceangal ar fáil',
    accessUnavailable: 'Caithfidh {owner} {system} a athcheangal',
    accessUnavailableTold: 'Cuireadh {owner} ar an eolas',
    unpublishTitle: 'Dífhoilsigh {app}?',
    unpublishBody:
      'Ní bheidh {team} in ann í a oscailt a thuilleadh. Coinneofar a gcuid iontrálacha ({entries}) agus tiocfaidh siad ar ais má fhoilsíonn tú arís.',
    unpublishConfirm: 'Dífhoilsigh',
    unpublishBusy: 'Á dhífhoilsiú…',
    error: 'Níor oibrigh sé sin. Níor athraíodh aon rud. Bain triail eile as.',
    checkChecking: 'Ag seiceáil an chóid agus na bpacáistí…',
    checkClean: 'Níor aimsíodh aon rúin ná pacáistí leochaileacha sa leagan seo.',
    checkWarnings: '{things}. Is féidir leat foilsiú fós.',
    checkHeldSecret: 'Tá an foilsiú ar fionraí. Aimsíodh rún sa chód.',
    checkHeldDependency:
      'Tá an foilsiú ar fionraí. Tá leochaileacht thromchúiseach aitheanta i bpacáiste.',
    checkHeldMany: 'Tá an foilsiú ar fionraí. Le ceartú ar dtús: {issues}.',
    checkExpired: 'Tá an tseiceáil seo as dáta.',
    checkIncomplete:
      'Níorbh fhéidir an tseiceáil a chríochnú. Fanfaidh an foilsiú ar fionraí go dtí go gcríochnóidh sí.',
    checkHeldPrivate: 'Tá an foilsiú ar fionraí mar gheall ar sheiceáil slándála.',
    checkLastChecked: 'Seiceáil dheireanach: {time}',
    severityCritical: 'Criticiúil',
    severityHigh: 'Ard',
    severityModerate: 'Measartha',
    severityLow: 'Íseal',
    severityUnrated: 'Gan rátáil',
    severityWordCritical: 'chriticiúil',
    severityWordHigh: 'thromchúiseach',
    severityWordModerate: 'mheasartha',
    severityWordLow: 'bheag',
    secretApiKey: 'Eochair API sa chód',
    secretPrivateKey: 'Eochair phríobháideach sa chód',
    secretAccessToken: 'Comhartha rochtana sa chód',
    secretPassword: 'Pasfhocal sa chód',
    secretUnknown: 'Rún féideartha sa chód',
    secretWhere: '{area} {file}, líne {line}',
    secretAreaCode: 'Cód:',
    secretAreaBuilt: 'Comhaid tógtha:',
    secretWhy: 'D’fhéadfadh duine ar bith atá in ann an aip a oscailt é a léamh agus a úsáid.',
    secretWhat:
      'Bog é chuig ceangal nó chuig socrú rúnda. Ansin cuir eochair nua ina háit leis an soláthraí, mar b’fhéidir gur cóipeáladh í cheana.',
    depTitle: 'Tá leochaileacht {severity} aitheanta sa phacáiste {package} {version}',
    depTitleUnrated:
      'Tá leochaileacht nach bhfuil rátáil uirthi fós sa phacáiste {package} {version}',
    depWhere: 'Pacáiste a úsáideann an aip',
    depWhy: 'Baineann comhairle slándála foilsithe leis an leagan seo.',
    depViewAdvisory: 'Féach ar an gcomhairle',
    depAdviceUpdate: 'Nuashonraigh go leagan ceartaithe.',
    depAdviceNoFix:
      'Níl leagan ceartaithe ann fós. Is féidir leat glacadh leis an riosca nó an pacáiste a bhaint.',
    depAdviceUnrated: 'Nuashonraigh é, nó fan go dtí go mbeidh rátáil ar an gcomhairle.',
    actionFixAndRecheck: 'Ceartaigh agus seiceáil arís',
    actionCheckAgain: 'Seiceáil arís',
    actionReview: 'Athbhreithnigh',
    actionPublishAnyway: 'Glac leis an riosca…',
    publishAnywayTitle: 'Glac leis an leochaileacht aitheanta seo?',
    publishAnywayBody:
      'Tá leochaileacht {severity} sa phacáiste {package} {version} nach bhfuil aon cheartú uirthi fós. D’fhéadfadh sé seo dul i bhfeidhm ar dhaoine a úsáideann an aip seo. Taifeadtar é seo le d’ainm agus leis an am.',
    publishAnywayConfirm: 'Glac leis an riosca',
    publishAnywayCancel: 'Cuir ar ceal',
    publishAnywayAcknowledged: 'Glacadh leis an riosca · deimhnithe ag {name} · {date}',
    fixRequestSecret: 'Bog an rún ({label}) sa chomhad {file} chuig socrú rúnda agus seiceáil arís',
    fixRequestPackage: 'Nuashonraigh an pacáiste {package} go leagan ceartaithe agus seiceáil arís',
    chatHeldSecret: 'D’aimsigh an tseiceáil foilsithe rún sa chód. Tá an foilsiú ar fionraí.',
    chatHeldMany: 'D’aimsigh an tseiceáil foilsithe {issues}. Tá an foilsiú ar fionraí.',
    chatWarnings: 'D’aimsigh an tseiceáil foilsithe {things}. Is féidir leat foilsiú fós.',
    chatReview: 'Athbhreithnigh',
    checkIssues: 'one:fhadhb|two:fhadhb|few:fhadhb|many:bhfadhb|other:fadhb',
    checkThings:
      'one:rud le féachaint air|two:rud le féachaint orthu|few:rud le féachaint orthu|many:rud le féachaint orthu|other:rud le féachaint orthu',
  },
  hr: {
    title: 'Objavi {app} v{n}',
    firstTitle: 'Objavi {app}',
    lead: '{team} će koristiti v{n}. v{prev} ostaje u Verzijama.',
    firstLead:
      '{team} će moći otvoriti aplikaciju {app}. Objavu možete povući u bilo kojem trenutku.',
    close: 'Zatvori',
    whatsNew: 'Novo u v{n}',
    whatsNewPlaceholder: 'Jedan redak koji će korisnici vidjeti',
    changesHeading: 'Što se mijenja za korisnike',
    rowLooks: 'Izgled',
    rowStores: 'Pohranjuje',
    rowReads: 'Čita',
    rowWrites: 'Zapisuje',
    rowRuns: 'Pokreće',
    whoHeading: 'Tko je može koristiti',
    whoMeta: '{team} · broj osoba: {people}',
    whoRole: 'Može koristiti · kao u v{prev}',
    whoRoleFirst: 'Može koristiti',
    changeInShare: 'Promijeni u Dijeljenju',
    checksHeading: 'Provjere',
    blocksPublishing: 'Blokira objavu',
    fixInChat: 'Popravi u chatu',
    checksPassed: 'Uspješne provjere: {count}',
    show: 'Prikaži',
    hide: 'Sakrij',
    contractionHeading: 'Ova verzija nešto uklanja',
    contractionNote:
      'Označite svaku stavku kako biste potvrdili. Ništa se ne uklanja dok ne objavite.',
    permission: 'Možete objavljivati jer imate dopuštenje za objavljivanje u tvrtki {company}.',
    undoStays: 'Poništavanje ostaje dostupno nakon objave jer se ništa ne uklanja.',
    undoGone: 'Poništavanje nije dostupno nakon objave jer se time uklanjaju podaci.',
    footerAudience: 'Samo članovi tvrtke. Bez javnih poveznica.',
    footerBlocked: 'Provjere koje blokiraju objavu: {count}',
    footerBlockedMany: 'Provjere koje blokiraju objavu: {count}',
    footerContraction: 'Za objavu potvrdite što će biti uklonjeno.',
    cancel: 'Odustani',
    publish: 'Objavi v{n}',
    publishFirst: 'Objavi',
    publishing: 'Objavljivanje…',
    doneTitle: '{team} sada ima v{n}',
    doneFirstTitle: '{team} sada ima aplikaciju {app}',
    doneBody:
      'Broj osoba koje od sada koriste v{n}: {people}. Njihovi unosi ({entries}) ostaju kakvi jesu.',
    doneBodyUncounted:
      'Broj osoba koje od sada koriste v{n}: {people}. Njihovi unosi ostaju kakvi jesu.',
    doneBodyBare: 'Broj osoba koje od sada koriste v{n}: {people}.',
    doneFirstBody: 'Broj osoba koje je sada mogu otvoriti: {people}. Njihovi unosi počinju ovdje.',
    doneWhere: 'u njihovoj Knjižnici i među prikvačenima za tim',
    doneThey: 'Vidjet će {what} kad sljedeći put otvore aplikaciju {app}.',
    doneUndoNote: 'Ništa nije uklonjeno pa poništavanje ostaje dostupno.',
    undoPublish: 'Poništi objavu',
    publishUnknownTitle: 'Ne znamo je li v{n} objavljena',
    publishUnknownBody:
      '{team} je možda već ima, a možda i nema. Provjerite prije ponovne objave kako se ništa ne bi objavilo dvaput.',
    publishFailedTitle: 'v{n} nije objavljena',
    publishFailedBody: 'Za {team} ništa se nije promijenilo. Pokušajte ponovno.',
    checkAgain: 'Provjeri ponovno',
    tryAgain: 'Pokušaj ponovno',
    askOwner: 'Pitaj: {owner}',
    restoreAction: 'Vrati…',
    copyLink: 'Kopiraj poveznicu',
    copied: 'Poveznica je kopirana',
    done: 'Gotovo',
    peopleList: '{names} u {place}',
    peopleAndOthers: '{first}, {second} i drugi ({count})',
    peopleAndOneOther: '{first}, {second} i još 1 osoba',
    agentPublished: 'Objavljeno · v{n} · agent {agent}',
    undo: 'Poništi',
    restoreVersion: 'Vrati v{prev}',
    agentPublishedNote:
      'Agent ju je objavio nakon vlastitih provjera. Poništite ili vratite v{prev} u Verzijama.',
    shareTitle: 'Dijeli {app}',
    shareSearch: 'Dodajte osobe ili grupe iz tvrtke {company}',
    roleUse: 'Može koristiti',
    roleUseHint: 'Otvara aplikaciju i dodaje unose. Vidi samo ono što aplikacija prikazuje.',
    roleEdit: 'Može uređivati',
    roleEditHint:
      'Mijenja aplikaciju u chatu; vidi Podatke, Povijest i Verzije; može izvoziti. Ne može brisati ni vraćati podatke.',
    rolePublish: 'Može objavljivati',
    rolePublishHint: 'Samo osobe s dopuštenjem za objavljivanje: {names}.',
    roleRemove: 'Ukloni',
    owner: 'Vlasnik',
    ownerLine: '{you} (vi)',
    ownerPublisher: 'Vlasnik · objavljivač',
    throughConnection: 'Korisnici vide podatke putem vaše veze',
    throughConnectionBody:
      'Aplikacija prikazuje podatke iz sustava {system} samo u opsegu onoga što sama prikazuje, a ne vaše ostale podatke iz sustava {system}.',
    sharePublishDisabledReason: 'Potrebno je dopuštenje za objavljivanje',
    accessTitle: 'Kako korisnici pristupaju sustavima tvrtke',
    accessOwnerYours: 'Putem vaše veze · svi vide isto',
    accessOwnerOther: 'Putem veze korisnika {owner} · svi vide isto',
    accessViewer: 'Vlastita veza svake osobe · svatko vidi svoje',
    accessConnected: 'Osobe u publici koje su povezale sustav {system}: {k} od {n}',
    accessChange: 'Promijeni…',
    accessApplies: 'Promjene se primjenjuju kad ponovno objavite',
    accessNarrowedPill: 'Više nije dopušteno',
    accessNarrowed:
      'Tvrtka {company} promijenila je što dijeljene aplikacije smiju čitati iz sustava {system}. Taj je dio skriven dok ponovno ne odaberete i ponovno ne objavite.',
    accessUnavailablePill: 'Veza nije dostupna',
    accessUnavailable: '{owner} mora ponovno povezati {system}',
    accessUnavailableTold: 'Obavijestili smo korisnika {owner}',
    unpublishTitle: 'Povući objavu aplikacije {app}?',
    unpublishBody:
      '{team} je više neće moći otvoriti. Njihovi unosi ({entries}) čuvaju se i vraćaju ako je ponovno objavite.',
    unpublishConfirm: 'Povuci objavu',
    unpublishBusy: 'Povlačenje objave…',
    error: 'Nije uspjelo. Ništa nije promijenjeno. Pokušajte ponovno.',
    checkChecking: 'Provjera koda i paketa…',
    checkClean: 'U ovoj verziji nisu pronađene tajne ni ranjivi paketi.',
    checkWarnings: '{things}. Svejedno možete objaviti.',
    checkHeldSecret: 'Objava je zaustavljena. U kodu je pronađena tajna.',
    checkHeldDependency: 'Objava je zaustavljena. Paket ima poznatu ozbiljnu ranjivost.',
    checkHeldMany: 'Objava je zaustavljena. Najprije treba popraviti {issues}.',
    checkExpired: 'Ova provjera više nije ažurna.',
    checkIncomplete: 'Provjera nije dovršena. Objava ostaje zaustavljena dok se ne dovrši.',
    checkHeldPrivate: 'Objavu je zaustavila sigurnosna provjera.',
    checkLastChecked: 'Posljednja provjera: {time}',
    severityCritical: 'Kritično',
    severityHigh: 'Visoko',
    severityModerate: 'Umjereno',
    severityLow: 'Nisko',
    severityUnrated: 'Bez ocjene',
    severityWordCritical: 'kritičnu',
    severityWordHigh: 'ozbiljnu',
    severityWordModerate: 'umjerenu',
    severityWordLow: 'manju',
    secretApiKey: 'API ključ u kodu',
    secretPrivateKey: 'Privatni ključ u kodu',
    secretAccessToken: 'Pristupni token u kodu',
    secretPassword: 'Lozinka u kodu',
    secretUnknown: 'Moguća tajna u kodu',
    secretWhere: '{area} {file}, redak {line}',
    secretAreaCode: 'Kod:',
    secretAreaBuilt: 'Izgrađene datoteke:',
    secretWhy: 'Svatko tko može otvoriti aplikaciju mogao bi je pročitati i upotrijebiti.',
    secretWhat:
      'Premjestite je u vezu ili tajnu postavku. Zatim zamijenite ključ kod njegova pružatelja jer je možda već kopiran.',
    depTitle: '{package} {version} ima poznatu {severity} ranjivost',
    depTitleUnrated: '{package} {version} ima ranjivost koja još nije ocijenjena',
    depWhere: 'Paket koji aplikacija koristi',
    depWhy: 'Objavljeno sigurnosno upozorenje odnosi se na ovu verziju.',
    depViewAdvisory: 'Prikaži upozorenje',
    depAdviceUpdate: 'Ažurirajte na ispravljenu verziju.',
    depAdviceNoFix:
      'Ispravljena verzija još ne postoji. Možete prihvatiti rizik ili ukloniti paket.',
    depAdviceUnrated: 'Ažurirajte ga ili pričekajte da upozorenje bude ocijenjeno.',
    actionFixAndRecheck: 'Popravi i provjeri ponovno',
    actionCheckAgain: 'Provjeri ponovno',
    actionReview: 'Pregledaj',
    actionPublishAnyway: 'Prihvati rizik…',
    publishAnywayTitle: 'Prihvatiti ovu poznatu ranjivost?',
    publishAnywayBody:
      '{package} {version} ima {severity} ranjivost za koju još nema ispravka. To može utjecati na osobe koje koriste ovu aplikaciju. To se bilježi s vašim imenom i vremenom.',
    publishAnywayConfirm: 'Prihvati rizik',
    publishAnywayCancel: 'Odustani',
    publishAnywayAcknowledged: 'Rizik prihvaćen · potvrdio/la {name} · {date}',
    fixRequestSecret:
      'Premjesti tajnu ({label}) iz datoteke {file} u tajnu postavku i provjeri ponovno',
    fixRequestPackage: 'Ažuriraj {package} na ispravljenu verziju i provjeri ponovno',
    chatHeldSecret: 'Provjera prije objave pronašla je tajnu u kodu. Objava je zaustavljena.',
    chatHeldMany: 'Provjera prije objave pronašla je {issues}. Objava je zaustavljena.',
    chatWarnings: 'Rezultat provjere prije objave: {things}. Svejedno možete objaviti.',
    chatReview: 'Pregledaj',
    checkIssues: 'one:problem|few:problema|other:problema',
    checkThings: 'one:stavka za pregled|few:stavke za pregled|other:stavki za pregled',
  },
  hu: {
    title: '{app} v{n} közzététele',
    firstTitle: '{app} közzététele',
    lead: '{team} a v{n} verziót fogja használni. A v{prev} megmarad a Verziók között.',
    firstLead: '{team} meg tudja majd nyitni ezt: {app}. A közzétételt bármikor megszüntetheti.',
    close: 'Bezárás',
    whatsNew: 'A v{n} újdonságai',
    whatsNewPlaceholder: 'Egy sor, amelyet mások látni fognak',
    changesHeading: 'Mi változik a felhasználók számára',
    rowLooks: 'Megjelenés',
    rowStores: 'Tárol',
    rowReads: 'Olvas',
    rowWrites: 'Ír',
    rowRuns: 'Futtat',
    whoHeading: 'Kik használhatják',
    whoMeta: '{team} · {people} fő',
    whoRole: 'Használhatja · mint a v{prev}',
    whoRoleFirst: 'Használhatja',
    changeInShare: 'Módosítás a Megosztásban',
    checksHeading: 'Ellenőrzések',
    blocksPublishing: 'Akadályozza a közzétételt',
    fixInChat: 'Javítás a csevegésben',
    checksPassed: '{count} ellenőrzés sikeres',
    show: 'Megjelenítés',
    hide: 'Elrejtés',
    contractionHeading: 'Ez a verzió eltávolít valamit',
    contractionNote:
      'A megerősítéshez jelölje be mindegyiket. Semmi sem törlődik, amíg közzé nem teszi.',
    permission: 'Közzéteheti, mert közzétevői jogosultsága van itt: {company}.',
    undoStays: 'A Visszavonás a közzététel után is elérhető marad, mert semmi sem törlődik.',
    undoGone: 'A Visszavonás a közzététel után nem érhető el, mert ez adatokat töröl.',
    footerAudience: 'Csak a cég tagjai számára. Nincsenek nyilvános linkek.',
    footerBlocked: '{count} ellenőrzés akadályozza a közzétételt',
    footerBlockedMany: '{count} ellenőrzés akadályozza a közzétételt',
    footerContraction: 'A közzétételhez erősítse meg, mi törlődik.',
    cancel: 'Mégse',
    publish: 'v{n} közzététele',
    publishFirst: 'Közzététel',
    publishing: 'Közzététel…',
    doneTitle: 'A v{n} már elérhető {team} számára',
    doneFirstTitle: '{app} már elérhető {team} számára',
    doneBody:
      'Mostantól {people} fő használja a v{n} verziót. Meglévő {entries} bejegyzésük változatlan marad.',
    doneBodyUncounted:
      'Mostantól {people} fő használja a v{n} verziót. Bejegyzéseik változatlanok maradnak.',
    doneBodyBare: 'Mostantól {people} fő használja a v{n} verziót.',
    doneFirstBody: 'Már {people} fő megnyithatja. Bejegyzéseik itt kezdődnek.',
    doneWhere: 'a Könyvtárukban, a csapat számára kitűzve',
    doneThey: 'Az alkalmazás ({app}) következő megnyitásakor ezt látják: {what}.',
    doneUndoNote: 'Semmi sem törlődött, így a Visszavonás elérhető marad.',
    undoPublish: 'Közzététel visszavonása',
    publishUnknownTitle: 'Nem tudjuk, hogy a v{n} közzé lett-e téve',
    publishUnknownBody:
      'Lehet, hogy {team} már megkapta, lehet, hogy még nem. Az újbóli közzététel előtt ellenőrizze, hogy semmi ne kerüljön kétszer közzétételre.',
    publishFailedTitle: 'A v{n} közzététele nem sikerült',
    publishFailedBody: '{team} számára semmi sem változott. Próbálja újra.',
    checkAgain: 'Ellenőrzés újra',
    tryAgain: 'Próbálja újra',
    askOwner: 'Kérdezze meg: {owner}',
    restoreAction: 'Visszaállítás…',
    copyLink: 'Link másolása',
    copied: 'Link kimásolva',
    done: 'Kész',
    peopleList: '{names} – {place}',
    peopleAndOthers: '{first}, {second} és további {count} fő',
    peopleAndOneOther: '{first}, {second} és további 1 fő',
    agentPublished: 'Közzétéve · v{n}, közzétevő: {agent}',
    undo: 'Visszavonás',
    restoreVersion: 'v{prev} visszaállítása',
    agentPublishedNote:
      'Az ügynök a saját ellenőrzéseivel tette közzé. Vonja vissza, vagy állítsa vissza a v{prev} verziót a Verziók közül.',
    shareTitle: '{app} megosztása',
    shareSearch: 'Személyek vagy csoportok hozzáadása innen: {company}',
    roleUse: 'Használhatja',
    roleUseHint:
      'Megnyitja az alkalmazást, és bejegyzéseket ad hozzá. Csak azt látja, amit az alkalmazás megjelenít.',
    roleEdit: 'Szerkesztheti',
    roleEditHint:
      'A csevegésben módosítja az alkalmazást; látja az Adatok, Előzmények és Verziók részt; exportálhat. Adatokat nem törölhet és nem állíthat vissza.',
    rolePublish: 'Közzéteheti',
    rolePublishHint: 'Csak közzétevői jogosultsággal rendelkezők: {names}.',
    roleRemove: 'Eltávolítás',
    owner: 'Tulajdonos',
    ownerLine: '{you} (Ön)',
    ownerPublisher: 'Tulajdonos · közzétevő',
    throughConnection: 'Mások az Ön kapcsolatán keresztül látják az adatokat',
    throughConnectionBody:
      'Az alkalmazás csak azt mutatja meg ebből: {system}, amit maga megjelenít, a többi {system}-adatát nem.',
    sharePublishDisabledReason: 'Közzétevői jogosultság szükséges',
    accessTitle: 'Hogyan érik el a megtekintők a céges rendszereket',
    accessOwnerYours: 'Az Ön kapcsolatán keresztül · mindenki ugyanazt látja',
    accessOwnerOther: '{owner} kapcsolatán keresztül · mindenki ugyanazt látja',
    accessViewer: 'Mindenki saját kapcsolata · mindenki a sajátját látja',
    accessConnected: 'A közönségből {n} főből {k} csatlakoztatta ezt: {system}',
    accessChange: 'Módosítás…',
    accessApplies: 'A módosítások az újbóli közzétételkor lépnek érvénybe',
    accessNarrowedPill: 'Már nem engedélyezett',
    accessNarrowed:
      '{company} módosította, mit olvashatnak a megosztott alkalmazások innen: {system}. Ez a rész rejtve marad, amíg újra nem választ, és újra közzé nem teszi.',
    accessUnavailablePill: 'A kapcsolat nem érhető el',
    accessUnavailable: '{owner} csatlakoztassa újra ezt: {system}',
    accessUnavailableTold: '{owner} értesítést kapott',
    unpublishTitle: '{app} közzétételének megszüntetése?',
    unpublishBody:
      '{team} többé nem tudja megnyitni. Meglévő {entries} bejegyzésük megmarad, és újbóli közzétételkor visszatér.',
    unpublishConfirm: 'Közzététel megszüntetése',
    unpublishBusy: 'Közzététel megszüntetése…',
    error: 'Ez nem sikerült. Semmi sem változott. Próbálja újra.',
    checkChecking: 'A kód és a csomagok ellenőrzése…',
    checkClean: 'Ebben a verzióban nem található titkos adat vagy sebezhető csomag.',
    checkWarnings: '{things}. Ettől még közzéteheti.',
    checkHeldSecret: 'A közzététel vissza van tartva. Titkos adat található a kódban.',
    checkHeldDependency:
      'A közzététel vissza van tartva. Egy csomagban ismert súlyos sebezhetőség van.',
    checkHeldMany: 'A közzététel vissza van tartva. Előbb {issues} kell kijavítani.',
    checkExpired: 'Ez az ellenőrzés elavult.',
    checkIncomplete:
      'Az ellenőrzés nem tudott befejeződni. A közzététel addig vissza van tartva, amíg be nem fejeződik.',
    checkHeldPrivate: 'A közzétételt egy biztonsági ellenőrzés visszatartja.',
    checkLastChecked: 'Utolsó ellenőrzés: {time}',
    severityCritical: 'Kritikus',
    severityHigh: 'Magas',
    severityModerate: 'Közepes',
    severityLow: 'Alacsony',
    severityUnrated: 'Nincs besorolva',
    severityWordCritical: 'kritikus',
    severityWordHigh: 'súlyos',
    severityWordModerate: 'közepes',
    severityWordLow: 'kisebb',
    secretApiKey: 'API-kulcs a kódban',
    secretPrivateKey: 'Privát kulcs a kódban',
    secretAccessToken: 'Hozzáférési token a kódban',
    secretPassword: 'Jelszó a kódban',
    secretUnknown: 'Lehetséges titkos adat a kódban',
    secretWhere: '{area} {file}, {line}. sor',
    secretAreaCode: 'Kód:',
    secretAreaBuilt: 'Lefordított fájlok:',
    secretWhy: 'Bárki, aki meg tudja nyitni az alkalmazást, elolvashatja és használhatja.',
    secretWhat:
      'Helyezze át egy kapcsolatba vagy egy titkos beállításba. Ezután cserélje le a kulcsot a szolgáltatójánál, mert lehet, hogy már lemásolták.',
    depTitle: 'A csomagban ({package} {version}) ismert {severity} sebezhetőség van',
    depTitleUnrated:
      'A csomagban ({package} {version}) olyan sebezhetőség van, amely még nincs besorolva',
    depWhere: 'Az alkalmazás által használt csomag',
    depWhy: 'Egy közzétett biztonsági közlemény érinti ezt a verziót.',
    depViewAdvisory: 'Közlemény megtekintése',
    depAdviceUpdate: 'Frissítsen egy javított verzióra.',
    depAdviceNoFix:
      'Még nincs javított verzió. Elfogadhatja a kockázatot, vagy eltávolíthatja a csomagot.',
    depAdviceUnrated: 'Frissítse, vagy várja meg, amíg a közleményt besorolják.',
    actionFixAndRecheck: 'Javítás és újraellenőrzés',
    actionCheckAgain: 'Ellenőrzés újra',
    actionReview: 'Áttekintés',
    actionPublishAnyway: 'Kockázat elfogadása…',
    publishAnywayTitle: 'Elfogadja ezt az ismert sérülékenységet?',
    publishAnywayBody:
      'A csomagban ({package} {version}) {severity} sebezhetőség van, amelyre még nincs javítás. Ez érintheti az alkalmazás felhasználóit. A műveletet a nevével és az időponttal együtt rögzítjük.',
    publishAnywayConfirm: 'Kockázat elfogadása',
    publishAnywayCancel: 'Mégse',
    publishAnywayAcknowledged: 'Kockázat elfogadva · megerősítette: {name} · {date}',
    fixRequestSecret:
      'A titkos adat ({label}) áthelyezése a fájlból ({file}) egy titkos beállításba, majd újraellenőrzés',
    fixRequestPackage: 'A csomag ({package}) frissítése javított verzióra, majd újraellenőrzés',
    chatHeldSecret:
      'A közzététel előtti ellenőrzés titkos adatot talált a kódban. A közzététel vissza van tartva.',
    chatHeldMany: 'A közzététel előtti ellenőrzés {issues} talált. A közzététel vissza van tartva.',
    chatWarnings: 'A közzététel előtti ellenőrzés eredménye: {things}. Ettől még közzéteheti.',
    chatReview: 'Áttekintés',
    checkIssues: 'one:problémát|other:problémát',
    checkThings: 'one:átnézendő tétel|other:átnézendő tétel',
  },
  it: {
    title: 'Pubblica {app} v{n}',
    firstTitle: 'Pubblica {app}',
    lead: '{team} userà la v{n}. La v{prev} resta in Versioni.',
    firstLead: '{team} potrà aprire {app}. Puoi ritirare la pubblicazione in qualsiasi momento.',
    close: 'Chiudi',
    whatsNew: 'Novità della v{n}',
    whatsNewPlaceholder: 'Una riga che tutti vedranno',
    changesHeading: 'Cosa cambia per gli utenti',
    rowLooks: 'Aspetto',
    rowStores: 'Memorizza',
    rowReads: 'Legge',
    rowWrites: 'Scrive',
    rowRuns: 'Esegue',
    whoHeading: 'Chi può usarla',
    whoMeta: '{team} · {people} persone',
    whoRole: 'Può usare · come nella v{prev}',
    whoRoleFirst: 'Può usare',
    changeInShare: 'Modifica in Condividi',
    checksHeading: 'Controlli',
    blocksPublishing: 'Blocca la pubblicazione',
    fixInChat: 'Correggi in chat',
    checksPassed: '{count} controlli superati',
    show: 'Mostra',
    hide: 'Nascondi',
    contractionHeading: 'Questa versione rimuove qualcosa',
    contractionNote:
      'Spunta ogni voce per confermare. Non viene rimosso nulla finché non pubblichi.',
    permission: 'Puoi pubblicare perché hai l’autorizzazione alla pubblicazione in {company}.',
    undoStays: 'Annulla resta disponibile dopo la pubblicazione, perché non viene rimosso nulla.',
    undoGone: 'Annulla non è disponibile dopo la pubblicazione, perché questa rimuove dati.',
    footerAudience: 'Solo membri dell’azienda. Nessun link pubblico.',
    footerBlocked: '{count} controllo blocca la pubblicazione',
    footerBlockedMany: '{count} controlli bloccano la pubblicazione',
    footerContraction: 'Conferma cosa viene rimosso per pubblicare.',
    cancel: 'Annulla',
    publish: 'Pubblica v{n}',
    publishFirst: 'Pubblica',
    publishing: 'Pubblicazione in corso…',
    doneTitle: 'La v{n} è attiva per {team}',
    doneFirstTitle: '{app} è attiva per {team}',
    doneBody: 'Da ora {people} persone usano la v{n}. Le loro {entries} voci restano come sono.',
    doneBodyUncounted: 'Da ora {people} persone usano la v{n}. Le loro voci restano come sono.',
    doneBodyBare: 'Da ora {people} persone usano la v{n}.',
    doneFirstBody: '{people} persone possono aprirla adesso. Le loro voci iniziano qui.',
    doneWhere: 'nella loro Libreria e fissata per il team',
    doneThey: 'Vedranno {what} la prossima volta che apriranno {app}.',
    doneUndoNote: 'Non è stato rimosso nulla, quindi Annulla resta disponibile.',
    undoPublish: 'Annulla pubblicazione',
    publishUnknownTitle: 'Non sappiamo se la v{n} è stata pubblicata',
    publishUnknownBody:
      '{team} potrebbe averla già oppure no. Controlla prima di pubblicare di nuovo, così nulla viene pubblicato due volte.',
    publishFailedTitle: 'La v{n} non è stata pubblicata',
    publishFailedBody: 'Non è cambiato nulla per {team}. Riprova.',
    checkAgain: 'Controlla di nuovo',
    tryAgain: 'Riprova',
    askOwner: 'Chiedi a {owner}',
    restoreAction: 'Ripristina…',
    copyLink: 'Copia link',
    copied: 'Link copiato',
    done: 'Fatto',
    peopleList: '{names} di {place}',
    peopleAndOthers: '{first}, {second} e altri {count}',
    peopleAndOneOther: '{first}, {second} e 1 altro',
    agentPublished: 'Pubblicata · v{n} da {agent}',
    undo: 'Annulla',
    restoreVersion: 'Ripristina v{prev}',
    agentPublishedNote:
      'L’agente l’ha pubblicata con i suoi controlli. Annulla o ripristina la v{prev} da Versioni.',
    shareTitle: 'Condividi {app}',
    shareSearch: 'Aggiungi persone o gruppi di {company}',
    roleUse: 'Può usare',
    roleUseHint: 'Apre l’app e aggiunge voci. Vede solo ciò che l’app mostra.',
    roleEdit: 'Può modificare',
    roleEditHint:
      'Modifica l’app in chat; vede Dati, Cronologia e Versioni; può esportare. Non può cancellare né ripristinare i dati.',
    rolePublish: 'Può pubblicare',
    rolePublishHint: 'Solo le persone con l’autorizzazione alla pubblicazione: {names}.',
    roleRemove: 'Rimuovi',
    owner: 'Proprietario',
    ownerLine: '{you} (tu)',
    ownerPublisher: 'Proprietario · può pubblicare',
    throughConnection: 'Gli altri vedono i dati tramite la tua connessione',
    throughConnectionBody:
      'L’app mostra {system} solo attraverso ciò che visualizza, non gli altri tuoi dati di {system}.',
    sharePublishDisabledReason: 'Richiede l’autorizzazione alla pubblicazione',
    accessTitle: 'Come chi visualizza accede ai sistemi aziendali',
    accessOwnerYours: 'Tramite la tua connessione · tutti vedono le stesse cose',
    accessOwnerOther: 'Tramite la connessione di {owner} · tutti vedono le stesse cose',
    accessViewer: 'La connessione di ciascuno · ognuno vede i propri dati',
    accessConnected: '{k} persone su {n} del pubblico hanno connesso {system}',
    accessChange: 'Modifica…',
    accessApplies: 'Le modifiche si applicano quando pubblichi di nuovo',
    accessNarrowedPill: 'Non più consentito',
    accessNarrowed:
      '{company} ha cambiato ciò che le app condivise possono leggere da {system}. Questa parte resta nascosta finché non scegli di nuovo e ripubblichi.',
    accessUnavailablePill: 'Connessione non disponibile',
    accessUnavailable: '{owner} deve riconnettere {system}',
    accessUnavailableTold: '{owner} è stato avvisato',
    unpublishTitle: 'Ritirare la pubblicazione di {app}?',
    unpublishBody:
      '{team} non potrà più aprirla. Le loro {entries} voci vengono conservate e tornano se pubblichi di nuovo.',
    unpublishConfirm: 'Ritira pubblicazione',
    unpublishBusy: 'Ritiro della pubblicazione…',
    error: 'Non ha funzionato. Non è stato modificato nulla. Riprova.',
    checkChecking: 'Controllo del codice e dei pacchetti in corso…',
    checkClean: 'Nessun segreto o pacchetto vulnerabile trovato in questa versione.',
    checkWarnings: '{things}. Puoi pubblicare comunque.',
    checkHeldSecret: 'La pubblicazione è sospesa. Nel codice è stato trovato un segreto.',
    checkHeldDependency:
      'La pubblicazione è sospesa. Un pacchetto ha una vulnerabilità grave nota.',
    checkHeldMany: 'La pubblicazione è sospesa. Prima devi risolvere {issues}.',
    checkExpired: 'Questo controllo non è più aggiornato.',
    checkIncomplete:
      'Il controllo non è riuscito a terminare. La pubblicazione resta sospesa finché non termina.',
    checkHeldPrivate: 'La pubblicazione è sospesa da un controllo di sicurezza.',
    checkLastChecked: 'Ultimo controllo: {time}',
    severityCritical: 'Critica',
    severityHigh: 'Alta',
    severityModerate: 'Moderata',
    severityLow: 'Bassa',
    severityUnrated: 'Non classificata',
    severityWordCritical: 'critica',
    severityWordHigh: 'grave',
    severityWordModerate: 'moderata',
    severityWordLow: 'lieve',
    secretApiKey: 'Chiave API nel codice',
    secretPrivateKey: 'Chiave privata nel codice',
    secretAccessToken: 'Token di accesso nel codice',
    secretPassword: 'Password nel codice',
    secretUnknown: 'Possibile segreto nel codice',
    secretWhere: '{area} {file}, riga {line}',
    secretAreaCode: 'Codice:',
    secretAreaBuilt: 'File compilati:',
    secretWhy: 'Chiunque possa aprire l’App potrebbe leggerlo e usarlo.',
    secretWhat:
      'Spostalo in una connessione o in un’impostazione segreta. Poi sostituisci la chiave presso il suo fornitore, perché potrebbe essere già stata copiata.',
    depTitle: '{package} {version} ha una vulnerabilità {severity} nota',
    depTitleUnrated: '{package} {version} ha una vulnerabilità non ancora classificata',
    depWhere: 'Pacchetto usato dall’App',
    depWhy: 'Un avviso di sicurezza pubblicato riguarda questa versione.',
    depViewAdvisory: 'Vedi avviso',
    depAdviceUpdate: 'Aggiorna a una versione corretta.',
    depAdviceNoFix:
      'Non esiste ancora una versione corretta. Puoi accettare il rischio o rimuovere il pacchetto.',
    depAdviceUnrated: 'Aggiornalo o attendi che l’avviso venga classificato.',
    actionFixAndRecheck: 'Correggi e ricontrolla',
    actionCheckAgain: 'Controlla di nuovo',
    actionReview: 'Rivedi',
    actionPublishAnyway: 'Accetta il rischio…',
    publishAnywayTitle: 'Accettare questa vulnerabilità nota?',
    publishAnywayBody:
      '{package} {version} ha una vulnerabilità {severity} ancora senza correzione. Chi usa questa App potrebbe esserne colpito. L’operazione viene registrata con il tuo nome e l’ora.',
    publishAnywayConfirm: 'Accetta il rischio',
    publishAnywayCancel: 'Annulla',
    publishAnywayAcknowledged: 'Rischio accettato · confermato da {name} · {date}',
    fixRequestSecret:
      'Sposta il segreto ({label}) di {file} in un’impostazione segreta e ricontrolla',
    fixRequestPackage: 'Aggiorna {package} a una versione corretta e ricontrolla',
    chatHeldSecret:
      'Il controllo di pubblicazione ha trovato un segreto nel codice. La pubblicazione è sospesa.',
    chatHeldMany: 'Il controllo di pubblicazione ha trovato {issues}. La pubblicazione è sospesa.',
    chatWarnings: 'Il controllo di pubblicazione ha trovato {things}. Puoi pubblicare comunque.',
    chatReview: 'Rivedi',
    checkIssues: 'one:problema|many:di problemi|other:problemi',
    checkThings: 'one:elemento da guardare|many:di elementi da guardare|other:elementi da guardare',
  },
  lt: {
    title: 'Paskelbti {app} v{n}',
    firstTitle: 'Paskelbti {app}',
    lead: '{team} naudos v{n}. v{prev} liks skiltyje „Versijos“.',
    firstLead: '{team} galės atidaryti {app}. Paskelbimą galite atšaukti bet kada.',
    close: 'Uždaryti',
    whatsNew: 'Kas naujo versijoje v{n}',
    whatsNewPlaceholder: 'Viena eilutė, kurią matys žmonės',
    changesHeading: 'Kas keičiasi žmonėms',
    rowLooks: 'Išvaizda',
    rowStores: 'Saugo',
    rowReads: 'Skaito',
    rowWrites: 'Rašo',
    rowRuns: 'Vykdo',
    whoHeading: 'Kas gali naudoti',
    whoMeta: '{team} · žmonių: {people}',
    whoRole: 'Gali naudoti · kaip v{prev}',
    whoRoleFirst: 'Gali naudoti',
    changeInShare: 'Keisti skiltyje „Bendrinti“',
    checksHeading: 'Patikros',
    blocksPublishing: 'Stabdo paskelbimą',
    fixInChat: 'Taisyti pokalbyje',
    checksPassed: 'Sėkmingų patikrų: {count}',
    show: 'Rodyti',
    hide: 'Slėpti',
    contractionHeading: 'Ši versija kai ką pašalina',
    contractionNote:
      'Pažymėkite kiekvieną, kad patvirtintumėte. Niekas nepašalinama, kol nepaskelbsite.',
    permission: 'Galite skelbti, nes turite skelbėjo teisę įmonėje {company}.',
    undoStays: 'Po paskelbimo anuliuoti vis dar bus galima, nes niekas nepašalinama.',
    undoGone: 'Po paskelbimo anuliuoti nebus galima, nes pašalinami duomenys.',
    footerAudience: 'Tik įmonės nariams. Jokių viešų nuorodų.',
    footerBlocked: 'Paskelbimą stabdančių patikrų: {count}',
    footerBlockedMany: 'Paskelbimą stabdančių patikrų: {count}',
    footerContraction: 'Kad paskelbtumėte, patvirtinkite, kas pašalinama.',
    cancel: 'Atšaukti',
    publish: 'Paskelbti v{n}',
    publishFirst: 'Paskelbti',
    publishing: 'Skelbiama…',
    doneTitle: '{team} jau gali naudoti v{n}',
    doneFirstTitle: '{team} jau gali naudoti {app}',
    doneBody:
      'Nuo dabar žmonės naudoja v{n} (iš viso {people}). Jų įrašai ({entries}) lieka tokie, kokie yra.',
    doneBodyUncounted:
      'Nuo dabar žmonės naudoja v{n} (iš viso {people}). Jų įrašai lieka tokie, kokie yra.',
    doneBodyBare: 'Nuo dabar žmonės naudoja v{n} (iš viso {people}).',
    doneFirstBody: 'Žmonės jau gali ją atidaryti (iš viso {people}). Jų įrašai prasideda čia.',
    doneWhere: 'savo bibliotekoje ir prisegta komandai',
    doneThey: 'Jie matys {what} kitą kartą atidarę {app}.',
    doneUndoNote: 'Niekas nebuvo pašalinta, todėl anuliuoti vis dar galima.',
    undoPublish: 'Anuliuoti paskelbimą',
    publishUnknownTitle: 'Nežinome, ar v{n} buvo paskelbta',
    publishUnknownBody:
      '{team} gali ją jau turėti, o gal ir ne. Patikrinkite prieš skelbdami dar kartą, kad niekas nebūtų paskelbta du kartus.',
    publishFailedTitle: 'v{n} nepaskelbta',
    publishFailedBody: 'Komandai {team} niekas nepasikeitė. Bandykite dar kartą.',
    checkAgain: 'Tikrinti dar kartą',
    tryAgain: 'Bandyti dar kartą',
    askOwner: 'Paklausti: {owner}',
    restoreAction: 'Atkurti…',
    copyLink: 'Kopijuoti nuorodą',
    copied: 'Nuoroda nukopijuota',
    done: 'Atlikta',
    peopleList: '{names} ({place})',
    peopleAndOthers: '{first}, {second} ir kiti ({count})',
    peopleAndOneOther: '{first}, {second} ir dar 1 asmuo',
    agentPublished: 'Paskelbta · v{n}, paskelbė: {agent}',
    undo: 'Anuliuoti',
    restoreVersion: 'Atkurti v{prev}',
    agentPublishedNote:
      'Agentas ją paskelbė su savo patikromis. Anuliuokite arba atkurkite v{prev} skiltyje „Versijos“.',
    shareTitle: 'Bendrinti {app}',
    shareSearch: 'Pridėti žmones ar grupes iš įmonės {company}',
    roleUse: 'Gali naudoti',
    roleUseHint: 'Atidaro programėlę ir prideda įrašų. Mato tik tai, ką rodo programėlė.',
    roleEdit: 'Gali redaguoti',
    roleEditHint:
      'Keičia programėlę pokalbyje; mato skiltis „Duomenys“, „Istorija“ ir „Versijos“; gali eksportuoti. Negali išvalyti ar atkurti duomenų.',
    rolePublish: 'Gali skelbti',
    rolePublishHint: 'Tik žmonės, turintys skelbėjo teisę: {names}.',
    roleRemove: 'Pašalinti',
    owner: 'Savininkas',
    ownerLine: '{you} (jūs)',
    ownerPublisher: 'Savininkas · skelbėjas',
    throughConnection: 'Žmonės mato duomenis per jūsų jungtį',
    throughConnectionBody:
      'Iš {system} programėlė rodo tik tai, ką pati rodo, o ne kitus jūsų {system} duomenis.',
    sharePublishDisabledReason: 'Reikia skelbėjo teisės',
    accessTitle: 'Kaip žiūrintieji pasiekia įmonės sistemas',
    accessOwnerYours: 'Per jūsų jungtį · visi mato tą patį',
    accessOwnerOther: 'Per naudotojo {owner} jungtį · visi mato tą patį',
    accessViewer: 'Kiekvieno asmens jungtis · kiekvienas mato savo',
    accessConnected: 'Auditorijos žmonės, prijungę {system}: {k} iš {n}',
    accessChange: 'Keisti…',
    accessApplies: 'Pakeitimai įsigalios, kai paskelbsite dar kartą',
    accessNarrowedPill: 'Nebeleidžiama',
    accessNarrowed:
      '{company} pakeitė, ką bendrinamos programėlės gali skaityti iš {system}. Ta dalis paslėpta, kol vėl pasirinksite ir paskelbsite iš naujo.',
    accessUnavailablePill: 'Jungtis nepasiekiama',
    accessUnavailable: '{owner} turi iš naujo prijungti {system}',
    accessUnavailableTold: 'Pranešta: {owner}',
    unpublishTitle: 'Atšaukti {app} paskelbimą?',
    unpublishBody:
      '{team} nebegalės jos atidaryti. Jų įrašai ({entries}) išsaugomi ir sugrįš, jei paskelbsite dar kartą.',
    unpublishConfirm: 'Atšaukti paskelbimą',
    unpublishBusy: 'Atšaukiamas paskelbimas…',
    error: 'Nepavyko. Niekas nepakeista. Bandykite dar kartą.',
    checkChecking: 'Tikrinamas kodas ir paketai…',
    checkClean: 'Šioje versijoje nerasta slaptų duomenų ar pažeidžiamų paketų.',
    checkWarnings: '{things}. Vis tiek galite paskelbti.',
    checkHeldSecret: 'Paskelbimas sustabdytas. Kode rasta slaptų duomenų.',
    checkHeldDependency: 'Paskelbimas sustabdytas. Pakete yra žinomas rimtas pažeidžiamumas.',
    checkHeldMany: 'Paskelbimas sustabdytas. Pirmiausia reikia ištaisyti {issues}.',
    checkExpired: 'Ši patikra nebeaktuali.',
    checkIncomplete:
      'Patikros nepavyko užbaigti. Paskelbimas lieka sustabdytas, kol ji bus užbaigta.',
    checkHeldPrivate: 'Paskelbimą sustabdė saugos patikra.',
    checkLastChecked: 'Paskutinį kartą tikrinta {time}',
    severityCritical: 'Kritinis',
    severityHigh: 'Aukštas',
    severityModerate: 'Vidutinis',
    severityLow: 'Žemas',
    severityUnrated: 'Neįvertintas',
    severityWordCritical: 'kritinį',
    severityWordHigh: 'rimtą',
    severityWordModerate: 'vidutinį',
    severityWordLow: 'nedidelį',
    secretApiKey: 'API raktas kode',
    secretPrivateKey: 'Privatus raktas kode',
    secretAccessToken: 'Prieigos žetonas kode',
    secretPassword: 'Slaptažodis kode',
    secretUnknown: 'Galimi slapti duomenys kode',
    secretWhere: '{area} {file}, {line} eilutė',
    secretAreaCode: 'Kodas:',
    secretAreaBuilt: 'Sukurti failai:',
    secretWhy: 'Bet kas, galintis atidaryti programėlę, galėtų tai perskaityti ir panaudoti.',
    secretWhat:
      'Perkelkite tai į jungtį arba slaptą nustatymą. Tada pakeiskite raktą pas jo teikėją, nes jis jau galėjo būti nukopijuotas.',
    depTitle: '{package} {version} turi žinomą {severity} pažeidžiamumą',
    depTitleUnrated: '{package} {version} turi pažeidžiamumą, kuris dar neįvertintas',
    depWhere: 'Programėlės naudojamas paketas',
    depWhy: 'Paskelbtas saugos pranešimas taikomas šiai versijai.',
    depViewAdvisory: 'Peržiūrėti pranešimą',
    depAdviceUpdate: 'Atnaujinkite į pataisytą versiją.',
    depAdviceNoFix: 'Pataisytos versijos dar nėra. Galite prisiimti riziką arba pašalinti paketą.',
    depAdviceUnrated: 'Atnaujinkite jį arba palaukite, kol pranešimas bus įvertintas.',
    actionFixAndRecheck: 'Ištaisyti ir tikrinti dar kartą',
    actionCheckAgain: 'Tikrinti dar kartą',
    actionReview: 'Peržiūrėti',
    actionPublishAnyway: 'Prisiimti riziką…',
    publishAnywayTitle: 'Prisiimti šios žinomos spragos riziką?',
    publishAnywayBody:
      '{package} {version} turi {severity} pažeidžiamumą, kurio pataisymo dar nėra. Tai gali paveikti šios programėlės naudotojus. Tai įrašoma su jūsų vardu ir laiku.',
    publishAnywayConfirm: 'Prisiimti riziką',
    publishAnywayCancel: 'Atšaukti',
    publishAnywayAcknowledged: 'Rizika prisiimta · patvirtino {name} · {date}',
    fixRequestSecret:
      'Perkelti slaptus duomenis ({label}) iš {file} į slaptą nustatymą ir tikrinti dar kartą',
    fixRequestPackage: 'Atnaujinti {package} į pataisytą versiją ir tikrinti dar kartą',
    chatHeldSecret: 'Paskelbimo patikra kode rado slaptų duomenų. Paskelbimas sustabdytas.',
    chatHeldMany: 'Paskelbimo patikra rado {issues}. Paskelbimas sustabdytas.',
    chatWarnings: 'Paskelbimo patikros rezultatas: {things}. Vis tiek galite paskelbti.',
    chatReview: 'Peržiūrėti',
    checkIssues: 'one:problemą|few:problemas|many:problemos|other:problemų',
    checkThings:
      'one:pastaba peržiūrėti|few:pastabos peržiūrėti|many:pastabos peržiūrėti|other:pastabų peržiūrėti',
  },
  lv: {
    title: 'Publicēt {app} v{n}',
    firstTitle: 'Publicēt {app}',
    lead: '{team} izmantos v{n}. v{prev} paliek sadaļā „Versijas”.',
    firstLead: '{team} varēs atvērt {app}. Publicēšanu varat atcelt jebkurā laikā.',
    close: 'Aizvērt',
    whatsNew: 'Jaunumi versijā v{n}',
    whatsNewPlaceholder: 'Viena rinda, ko redzēs cilvēki',
    changesHeading: 'Kas mainās cilvēkiem',
    rowLooks: 'Izskats',
    rowStores: 'Glabā',
    rowReads: 'Lasa',
    rowWrites: 'Raksta',
    rowRuns: 'Izpilda',
    whoHeading: 'Kas var to izmantot',
    whoMeta: '{team} · cilvēki: {people}',
    whoRole: 'Var izmantot · tāpat kā v{prev}',
    whoRoleFirst: 'Var izmantot',
    changeInShare: 'Mainīt sadaļā „Kopīgot”',
    checksHeading: 'Pārbaudes',
    blocksPublishing: 'Bloķē publicēšanu',
    fixInChat: 'Labot tērzēšanā',
    checksPassed: 'Izturētās pārbaudes: {count}',
    show: 'Rādīt',
    hide: 'Paslēpt',
    contractionHeading: 'Šī versija kaut ko noņem',
    contractionNote:
      'Atzīmējiet katru, lai apstiprinātu. Nekas netiek noņemts, kamēr nepublicējat.',
    permission: 'Jūs varat publicēt, jo jums ir publicētāja atļauja uzņēmumā {company}.',
    undoStays: 'Pēc publicēšanas atsaukšana joprojām būs pieejama, jo nekas netiek noņemts.',
    undoGone: 'Pēc publicēšanas atsaukšana nebūs pieejama, jo tiek noņemti dati.',
    footerAudience: 'Tikai uzņēmuma darbiniekiem. Bez publiskām saitēm.',
    footerBlocked: 'Pārbaudes, kas bloķē publicēšanu: {count}',
    footerBlockedMany: 'Pārbaudes, kas bloķē publicēšanu: {count}',
    footerContraction: 'Lai publicētu, apstipriniet, kas tiek noņemts.',
    cancel: 'Atcelt',
    publish: 'Publicēt v{n}',
    publishFirst: 'Publicēt',
    publishing: 'Publicē…',
    doneTitle: '{team} tagad var izmantot v{n}',
    doneFirstTitle: '{team} tagad var izmantot {app}',
    doneBody:
      'No šī brīža v{n} izmanto cilvēki (kopā {people}). Viņu ieraksti ({entries}) paliek tādi, kādi ir.',
    doneBodyUncounted:
      'No šī brīža v{n} izmanto cilvēki (kopā {people}). Viņu ieraksti paliek tādi, kādi ir.',
    doneBodyBare: 'No šī brīža v{n} izmanto cilvēki (kopā {people}).',
    doneFirstBody: 'Cilvēki to jau var atvērt (kopā {people}). Viņu ieraksti sākas šeit.',
    doneWhere: 'savā bibliotēkā un piesprausta komandai',
    doneThey: 'Viņi redzēs {what} nākamreiz, kad atvērs {app}.',
    doneUndoNote: 'Nekas netika noņemts, tāpēc atsaukšana joprojām ir pieejama.',
    undoPublish: 'Atsaukt publicēšanu',
    publishUnknownTitle: 'Mēs nezinām, vai v{n} tika publicēta',
    publishUnknownBody:
      'Nav zināms, vai tā jau ir pieejama: {team}. Pārbaudiet, pirms publicējat vēlreiz, lai nekas netiktu publicēts divreiz.',
    publishFailedTitle: 'v{n} netika publicēta',
    publishFailedBody: 'Komandai {team} nekas nemainījās. Mēģiniet vēlreiz.',
    checkAgain: 'Pārbaudīt vēlreiz',
    tryAgain: 'Mēģināt vēlreiz',
    askOwner: 'Jautāt: {owner}',
    restoreAction: 'Atjaunot…',
    copyLink: 'Kopēt saiti',
    copied: 'Saite nokopēta',
    done: 'Gatavs',
    peopleList: '{names} ({place})',
    peopleAndOthers: '{first}, {second} un citi ({count})',
    peopleAndOneOther: '{first}, {second} un vēl 1 cilvēks',
    agentPublished: 'Publicēts · v{n}, publicēja: {agent}',
    undo: 'Atsaukt',
    restoreVersion: 'Atjaunot v{prev}',
    agentPublishedNote:
      'Aģents to publicēja ar savām pārbaudēm. Atsauciet vai atjaunojiet v{prev} sadaļā „Versijas”.',
    shareTitle: 'Kopīgot {app}',
    shareSearch: 'Pievienot cilvēkus vai grupas no uzņēmuma {company}',
    roleUse: 'Var izmantot',
    roleUseHint: 'Atver lietotni un pievieno ierakstus. Redz tikai to, ko rāda lietotne.',
    roleEdit: 'Var rediģēt',
    roleEditHint:
      'Maina lietotni tērzēšanā; redz sadaļas „Dati”, „Vēsture” un „Versijas”; var eksportēt. Nevar notīrīt vai atjaunot datus.',
    rolePublish: 'Var publicēt',
    rolePublishHint: 'Tikai cilvēki ar publicētāja atļauju: {names}.',
    roleRemove: 'Noņemt',
    owner: 'Īpašnieks',
    ownerLine: '{you} (jūs)',
    ownerPublisher: 'Īpašnieks · publicētājs',
    throughConnection: 'Cilvēki redz datus caur jūsu savienojumu',
    throughConnectionBody:
      'No {system} lietotne rāda tikai to, ko tā parāda, nevis citus jūsu {system} datus.',
    sharePublishDisabledReason: 'Nepieciešama publicētāja atļauja',
    accessTitle: 'Kā skatītāji piekļūst uzņēmuma sistēmām',
    accessOwnerYours: 'Caur jūsu savienojumu · visi redz vienu un to pašu',
    accessOwnerOther: 'Caur lietotāja {owner} savienojumu · visi redz vienu un to pašu',
    accessViewer: 'Katra cilvēka paša savienojums · katrs redz savus datus',
    accessConnected: 'Auditorijā {system} ir pievienojuši: {k} no {n}',
    accessChange: 'Mainīt…',
    accessApplies: 'Izmaiņas stāsies spēkā, kad publicēsiet vēlreiz',
    accessNarrowedPill: 'Vairs nav atļauts',
    accessNarrowed:
      '{company} mainīja, ko kopīgotās lietotnes drīkst lasīt no {system}. Šī daļa ir paslēpta, līdz jūs atkal izvēlaties un publicējat vēlreiz.',
    accessUnavailablePill: 'Savienojums nav pieejams',
    accessUnavailable: 'Lietotājam {owner} no jauna jāpievieno {system}',
    accessUnavailableTold: 'Paziņots: {owner}',
    unpublishTitle: 'Atcelt {app} publicēšanu?',
    unpublishBody:
      '{team} vairs nevarēs to atvērt. Viņu ieraksti ({entries}) tiek saglabāti un atgriezīsies, ja publicēsiet vēlreiz.',
    unpublishConfirm: 'Atcelt publicēšanu',
    unpublishBusy: 'Atceļ publicēšanu…',
    error: 'Neizdevās. Nekas netika mainīts. Mēģiniet vēlreiz.',
    checkChecking: 'Notiek koda un pakotņu pārbaude…',
    checkClean: 'Šajā versijā nav atrasti noslēpumi vai ievainojamas pakotnes.',
    checkWarnings: '{things}. Joprojām varat publicēt.',
    checkHeldSecret: 'Publicēšana ir apturēta. Kodā atrasts noslēpums.',
    checkHeldDependency: 'Publicēšana ir apturēta. Pakotnei ir zināma nopietna ievainojamība.',
    checkHeldMany: 'Publicēšana ir apturēta. Vispirms novērsiet {issues}.',
    checkExpired: 'Šī pārbaude vairs nav aktuāla.',
    checkIncomplete:
      'Pārbaudi neizdevās pabeigt. Publicēšana paliek apturēta, līdz tā tiks pabeigta.',
    checkHeldPrivate: 'Publicēšanu aptur drošības pārbaude.',
    checkLastChecked: 'Pēdējā pārbaude: {time}',
    severityCritical: 'Kritiska',
    severityHigh: 'Augsta',
    severityModerate: 'Vidēja',
    severityLow: 'Zema',
    severityUnrated: 'Nav novērtēta',
    severityWordCritical: 'kritiska',
    severityWordHigh: 'nopietna',
    severityWordModerate: 'vidēja',
    severityWordLow: 'neliela',
    secretApiKey: 'API atslēga kodā',
    secretPrivateKey: 'Privātā atslēga kodā',
    secretAccessToken: 'Piekļuves marķieris kodā',
    secretPassword: 'Parole kodā',
    secretUnknown: 'Iespējams noslēpums kodā',
    secretWhere: '{area} {file}, {line}. rinda',
    secretAreaCode: 'Kods:',
    secretAreaBuilt: 'Būvētie faili:',
    secretWhy: 'Ikviens, kas var atvērt lietotni, varētu to izlasīt un izmantot.',
    secretWhat:
      'Pārvietojiet to uz savienojumu vai slepenu iestatījumu. Pēc tam nomainiet atslēgu pie tās nodrošinātāja, jo tā jau var būt nokopēta.',
    depTitle: 'Pakotnei {package} {version} ir zināma {severity} ievainojamība',
    depTitleUnrated: 'Pakotnei {package} {version} ir ievainojamība, kas vēl nav novērtēta',
    depWhere: 'Lietotnes izmantotā pakotne',
    depWhy: 'Publicēts drošības paziņojums attiecas uz šo versiju.',
    depViewAdvisory: 'Skatīt paziņojumu',
    depAdviceUpdate: 'Atjauniniet uz laboto versiju.',
    depAdviceNoFix: 'Labotas versijas vēl nav. Varat uzņemties risku vai noņemt pakotni.',
    depAdviceUnrated: 'Atjauniniet to vai gaidiet, līdz paziņojums tiks novērtēts.',
    actionFixAndRecheck: 'Labot un pārbaudīt vēlreiz',
    actionCheckAgain: 'Pārbaudīt vēlreiz',
    actionReview: 'Pārskatīt',
    actionPublishAnyway: 'Uzņemties risku…',
    publishAnywayTitle: 'Uzņemties šīs zināmās ievainojamības risku?',
    publishAnywayBody:
      'Pakotnei {package} {version} ir {severity} ievainojamība, kurai vēl nav labojuma. Tas var ietekmēt cilvēkus, kuri izmanto šo lietotni. Tas tiek reģistrēts ar jūsu vārdu un laiku.',
    publishAnywayConfirm: 'Uzņemties risku',
    publishAnywayCancel: 'Atcelt',
    publishAnywayAcknowledged: 'Risks uzņemts · apstiprināja {name} · {date}',
    fixRequestSecret:
      'Pārvietot noslēpumu ({label}) no {file} uz slepenu iestatījumu un pārbaudīt vēlreiz',
    fixRequestPackage: 'Atjaunināt pakotni {package} uz laboto versiju un pārbaudīt vēlreiz',
    chatHeldSecret: 'Publicēšanas pārbaude kodā atrada noslēpumu. Publicēšana ir apturēta.',
    chatHeldMany: 'Publicēšanas pārbaude atrada {issues}. Publicēšana ir apturēta.',
    chatWarnings: 'Publicēšanas pārbaudes rezultāts: {things}. Joprojām varat publicēt.',
    chatReview: 'Pārskatīt',
    checkIssues: 'zero:problēmas|one:problēmu|other:problēmas',
    checkThings:
      'zero:piezīmes pārskatīšanai|one:piezīme pārskatīšanai|other:piezīmes pārskatīšanai',
  },
  mt: {
    title: 'Ippubblika {app} v{n}',
    firstTitle: 'Ippubblika {app}',
    lead: '{team} se juża v{n}. v{prev} tibqa’ f’Verżjonijiet.',
    firstLead: '{team} se jkun jista’ jiftaħ {app}. Tista’ tneħħiha mill-pubblikazzjoni meta trid.',
    close: 'Agħlaq',
    whatsNew: 'X’hemm ġdid f’v{n}',
    whatsNewPlaceholder: 'Linja waħda li n-nies se jaraw',
    changesHeading: 'X’jinbidel għan-nies',
    rowLooks: 'Dehra',
    rowStores: 'Taħżen',
    rowReads: 'Taqra',
    rowWrites: 'Tikteb',
    rowRuns: 'Tħaddem',
    whoHeading: 'Min jista’ jużaha',
    whoMeta: '{team} · nies: {people}',
    whoRole: 'Jista’ juża · bħal v{prev}',
    whoRoleFirst: 'Jista’ juża',
    changeInShare: 'Ibdel f’Aqsam',
    checksHeading: 'Kontrolli',
    blocksPublishing: 'Iwaqqaf il-pubblikazzjoni',
    fixInChat: 'Irranġa fiċ-chat',
    checksPassed: 'Kontrolli li għaddew: {count}',
    show: 'Uri',
    hide: 'Aħbi',
    contractionHeading: 'Din il-verżjoni tneħħi xi ħaġa',
    contractionNote: 'Immarka kull waħda biex tikkonferma. Xejn ma jitneħħa qabel ma tippubblika.',
    permission: 'Tista’ tippubblika għax għandek il-permess ta’ pubblikatur f’{company}.',
    undoStays: 'Tista’ terġa’ lura anke wara l-pubblikazzjoni, għax xejn ma jitneħħa.',
    undoGone: 'Ma tistax terġa’ lura wara l-pubblikazzjoni, għax din tneħħi d-data.',
    footerAudience: 'Membri tal-kumpanija biss. L-ebda link pubbliku.',
    footerBlocked: 'Kontrolli li jwaqqfu l-pubblikazzjoni: {count}',
    footerBlockedMany: 'Kontrolli li jwaqqfu l-pubblikazzjoni: {count}',
    footerContraction: 'Ikkonferma x’se jitneħħa biex tippubblika.',
    cancel: 'Ikkanċella',
    publish: 'Ippubblika v{n}',
    publishFirst: 'Ippubblika',
    publishing: 'Qed tiġi ppubblikata…',
    doneTitle: 'v{n} hija attiva għal {team}',
    doneFirstTitle: '{app} hija attiva għal {team}',
    doneBody:
      'Nies li minn issa jużaw v{n}: {people}. L-entrati tagħhom ({entries}) jibqgħu kif inhuma.',
    doneBodyUncounted:
      'Nies li minn issa jużaw v{n}: {people}. L-entrati tagħhom jibqgħu kif inhuma.',
    doneBodyBare: 'Nies li minn issa jużaw v{n}: {people}.',
    doneFirstBody: 'Nies li jistgħu jiftħuha issa: {people}. L-entrati tagħhom jibdew hawn.',
    doneWhere: 'fil-Librerija tagħhom u ppinjata għat-tim',
    doneThey: 'Se jaraw {what} il-darba li jmiss li jiftħu {app}.',
    doneUndoNote: 'Xejn ma tneħħa, għalhekk xorta tista’ terġa’ lura.',
    undoPublish: 'Erġa’ lura mill-pubblikazzjoni',
    publishUnknownTitle: 'Ma nafux jekk v{n} ġietx ippubblikata',
    publishUnknownBody:
      'Jista’ jkun li {team} diġà għandu din il-verżjoni, jew le. Iċċekkja qabel terġa’ tippubblika, biex xejn ma jiġi ppubblikat darbtejn.',
    publishFailedTitle: 'v{n} ma ġietx ippubblikata',
    publishFailedBody: 'Xejn ma nbidel għal {team}. Erġa’ pprova.',
    checkAgain: 'Erġa’ ċċekkja',
    tryAgain: 'Erġa’ pprova',
    askOwner: 'Staqsi lil {owner}',
    restoreAction: 'Irrestawra…',
    copyLink: 'Ikkopja l-link',
    copied: 'Il-link ġie kkupjat',
    done: 'Lest',
    peopleList: '{names} f’{place}',
    peopleAndOthers: '{first}, {second} u oħrajn ({count})',
    peopleAndOneOther: '{first}, {second} u persuna oħra',
    agentPublished: 'Ippubblikata · v{n} minn {agent}',
    undo: 'Erġa’ lura',
    restoreVersion: 'Irrestawra v{prev}',
    agentPublishedNote:
      'L-aġent ippubblikaha bil-kontrolli tiegħu. Erġa’ lura jew irrestawra v{prev} minn Verżjonijiet.',
    shareTitle: 'Aqsam {app}',
    shareSearch: 'Żid nies jew gruppi f’{company}',
    roleUse: 'Jista’ juża',
    roleUseHint: 'Jiftaħ l-app u jżid entrati. Jara biss dak li turi l-app.',
    roleEdit: 'Jista’ jeditja',
    roleEditHint:
      'Ibiddel l-app fiċ-chat; jara Data, Storja u Verżjonijiet; jista’ jesporta. Ma jistax iħassar jew jirrestawra d-data.',
    rolePublish: 'Jista’ jippubblika',
    rolePublishHint: 'Nies bil-permess ta’ pubblikatur biss: {names}.',
    roleRemove: 'Neħħi',
    owner: 'Sid',
    ownerLine: '{you} (int)',
    ownerPublisher: 'Sid · pubblikatur',
    throughConnection: 'In-nies jaraw id-data permezz tal-konnessjoni tiegħek',
    throughConnectionBody:
      'L-app turi {system} biss permezz ta’ dak li turi, mhux id-data l-oħra tiegħek ta’ {system}.',
    sharePublishDisabledReason: 'Jeħtieġ il-permess ta’ pubblikatur',
    accessTitle: 'Kif min jara jilħaq is-sistemi tal-kumpanija',
    accessOwnerYours: 'Permezz tal-konnessjoni tiegħek · kulħadd jara l-istess',
    accessOwnerOther: 'Permezz tal-konnessjoni ta’ {owner} · kulħadd jara l-istess',
    accessViewer: 'Il-konnessjoni ta’ kull persuna · kull wieħed jara tiegħu',
    accessConnected: 'Nies fl-udjenza li qabbdu {system}: {k} minn {n}',
    accessChange: 'Ibdel…',
    accessApplies: 'Il-bidliet japplikaw meta terġa’ tippubblika',
    accessNarrowedPill: 'M’għadux permess',
    accessNarrowed:
      '{company} biddlet x’jistgħu jaqraw l-apps maqsuma minn {system}. Dik il-parti hija moħbija sakemm terġa’ tagħżel u terġa’ tippubblika.',
    accessUnavailablePill: 'Il-konnessjoni mhix disponibbli',
    accessUnavailable: '{owner} irid jerġa’ jqabbad {system}',
    accessUnavailableTold: 'Infurmajna lil {owner}',
    unpublishTitle: 'Tneħħi {app} mill-pubblikazzjoni?',
    unpublishBody:
      '{team} ma jkunx jista’ jiftaħha aktar. L-entrati tagħhom ({entries}) jinżammu u jerġgħu lura jekk terġa’ tippubblika.',
    unpublishConfirm: 'Neħħi mill-pubblikazzjoni',
    unpublishBusy: 'Qed titneħħa mill-pubblikazzjoni…',
    error: 'Ma ħadimx. Xejn ma nbidel. Erġa’ pprova.',
    checkChecking: 'Qed niċċekkjaw il-kodiċi u l-pakketti…',
    checkClean: 'Ma nstabu l-ebda sigrieti jew pakketti vulnerabbli f’din il-verżjoni.',
    checkWarnings: '{things}. Xorta tista’ tippubblika.',
    checkHeldSecret: 'Il-pubblikazzjoni hija miżmuma. Instab sigriet fil-kodiċi.',
    checkHeldDependency:
      'Il-pubblikazzjoni hija miżmuma. Pakkett għandu vulnerabbiltà serja magħrufa.',
    checkHeldMany: 'Il-pubblikazzjoni hija miżmuma. L-ewwel trid issolvi {issues}.',
    checkExpired: 'Dan il-kontroll mhuwiex aġġornat.',
    checkIncomplete:
      'Il-kontroll ma setax jitlesta. Il-pubblikazzjoni tibqa’ miżmuma sakemm jitlesta.',
    checkHeldPrivate: 'Il-pubblikazzjoni hija miżmuma minn kontroll tas-sigurtà.',
    checkLastChecked: 'L-aħħar kontroll: {time}',
    severityCritical: 'Kritika',
    severityHigh: 'Għolja',
    severityModerate: 'Moderata',
    severityLow: 'Baxxa',
    severityUnrated: 'Mhux ikklassifikata',
    severityWordCritical: 'kritika',
    severityWordHigh: 'serja',
    severityWordModerate: 'moderata',
    severityWordLow: 'żgħira',
    secretApiKey: 'Ċavetta tal-API fil-kodiċi',
    secretPrivateKey: 'Ċavetta privata fil-kodiċi',
    secretAccessToken: 'Token tal-aċċess fil-kodiċi',
    secretPassword: 'Password fil-kodiċi',
    secretUnknown: 'Sigriet possibbli fil-kodiċi',
    secretWhere: '{area} {file}, linja {line}',
    secretAreaCode: 'Kodiċi:',
    secretAreaBuilt: 'Fajls mibnija:',
    secretWhy: 'Kull min jista’ jiftaħ l-app jista’ jaqrah u jużah.',
    secretWhat:
      'Ċaqilqu f’konnessjoni jew f’setting sigriet. Imbagħad ibdel iċ-ċavetta mal-fornitur tagħha, għax setgħet diġà ġiet ikkupjata.',
    depTitle: '{package} {version} għandu vulnerabbiltà {severity} magħrufa',
    depTitleUnrated: '{package} {version} għandu vulnerabbiltà li għadha mhix ikklassifikata',
    depWhere: 'Pakkett li tuża l-app',
    depWhy: 'Avviż tas-sigurtà ppubblikat jaffettwa din il-verżjoni.',
    depViewAdvisory: 'Ara l-avviż',
    depAdviceUpdate: 'Aġġorna għal verżjoni rranġata.',
    depAdviceNoFix:
      'Għad m’hemmx verżjoni kkoreġuta. Tista’ taċċetta r-riskju jew tneħħi l-pakkett.',
    depAdviceUnrated: 'Aġġornah, jew stenna sakemm l-avviż jiġi kklassifikat.',
    actionFixAndRecheck: 'Irranġa u erġa’ ċċekkja',
    actionCheckAgain: 'Erġa’ ċċekkja',
    actionReview: 'Irrevedi',
    actionPublishAnyway: 'Aċċetta r-riskju…',
    publishAnywayTitle: 'Taċċetta din il-vulnerabbiltà magħrufa?',
    publishAnywayBody:
      '{package} {version} għandu vulnerabbiltà {severity} li għad m’għandhiex soluzzjoni. In-nies li jużaw din l-app jistgħu jiġu affettwati. Dan jiġi rreġistrat b’ismek u l-ħin.',
    publishAnywayConfirm: 'Aċċetta r-riskju',
    publishAnywayCancel: 'Ikkanċella',
    publishAnywayAcknowledged: 'Riskju aċċettat · ikkonfermat minn {name} · {date}',
    fixRequestSecret:
      'Ċaqlaq is-sigriet ({label}) minn {file} għal setting sigriet u erġa’ ċċekkja',
    fixRequestPackage: 'Aġġorna {package} għal verżjoni rranġata u erġa’ ċċekkja',
    chatHeldSecret:
      'Il-kontroll tal-pubblikazzjoni sab sigriet fil-kodiċi. Il-pubblikazzjoni hija miżmuma.',
    chatHeldMany: 'Il-kontroll tal-pubblikazzjoni sab {issues}. Il-pubblikazzjoni hija miżmuma.',
    chatWarnings: 'Il-kontroll tal-pubblikazzjoni sab {things}. Xorta tista’ tippubblika.',
    chatReview: 'Irrevedi',
    checkIssues: 'one:problema|two:problemi|few:problemi|many:problema|other:problema',
    checkThings:
      'one:ħaġa li għandek tħares lejha|two:affarijiet li għandek tħares lejhom|few:affarijiet li għandek tħares lejhom|many:ħaġa li għandek tħares lejha|other:ħaġa li għandek tħares lejha',
  },
  nl: {
    title: '{app} v{n} publiceren',
    firstTitle: '{app} publiceren',
    lead: '{team} gaat v{n} gebruiken. v{prev} blijft staan onder Versies.',
    firstLead: '{team} kan {app} dan openen. Je kunt de publicatie altijd weer intrekken.',
    close: 'Sluiten',
    whatsNew: 'Nieuw in v{n}',
    whatsNewPlaceholder: 'Eén regel die iedereen ziet',
    changesHeading: 'Wat er verandert voor gebruikers',
    rowLooks: 'Uiterlijk',
    rowStores: 'Slaat op',
    rowReads: 'Leest',
    rowWrites: 'Schrijft',
    rowRuns: 'Voert uit',
    whoHeading: 'Wie hem kan gebruiken',
    whoMeta: '{team} · {people} personen',
    whoRole: 'Kan gebruiken · zelfde als v{prev}',
    whoRoleFirst: 'Kan gebruiken',
    changeInShare: 'Wijzigen bij Delen',
    checksHeading: 'Controles',
    blocksPublishing: 'Blokkeert publicatie',
    fixInChat: 'Oplossen in de chat',
    checksPassed: '{count} controles geslaagd',
    show: 'Tonen',
    hide: 'Verbergen',
    contractionHeading: 'Deze versie verwijdert iets',
    contractionNote:
      'Vink elk onderdeel aan om te bevestigen. Er wordt pas iets verwijderd als je publiceert.',
    permission: 'Je kunt publiceren omdat je bij {company} het publicatierecht hebt.',
    undoStays:
      'Ongedaan maken blijft beschikbaar na het publiceren, omdat er niets wordt verwijderd.',
    undoGone:
      'Ongedaan maken is niet beschikbaar na het publiceren, omdat hierbij gegevens worden verwijderd.',
    footerAudience: 'Alleen leden van het bedrijf. Geen openbare links.',
    footerBlocked: '{count} controle blokkeert publicatie',
    footerBlockedMany: '{count} controles blokkeren publicatie',
    footerContraction: 'Bevestig wat er wordt verwijderd om te publiceren.',
    cancel: 'Annuleren',
    publish: 'v{n} publiceren',
    publishFirst: 'Publiceren',
    publishing: 'Bezig met publiceren…',
    doneTitle: 'v{n} is live voor {team}',
    doneFirstTitle: '{app} is live voor {team}',
    doneBody:
      '{people} personen gebruiken vanaf nu v{n}. Hun {entries} items blijven zoals ze zijn.',
    doneBodyUncounted:
      '{people} personen gebruiken vanaf nu v{n}. Hun items blijven zoals ze zijn.',
    doneBodyBare: '{people} personen gebruiken vanaf nu v{n}.',
    doneFirstBody: '{people} personen kunnen hem nu openen. Hun items beginnen hier.',
    doneWhere: 'in hun Bibliotheek en vastgezet voor het team',
    doneThey: 'Ze zien {what} de volgende keer dat ze {app} openen.',
    doneUndoNote: 'Er is niets verwijderd, dus Ongedaan maken blijft beschikbaar.',
    undoPublish: 'Publiceren ongedaan maken',
    publishUnknownTitle: 'We weten niet of v{n} is gepubliceerd',
    publishUnknownBody:
      '{team} heeft hem misschien al, misschien nog niet. Controleer dit voordat je opnieuw publiceert, zodat er niets twee keer wordt gepubliceerd.',
    publishFailedTitle: 'v{n} is niet gepubliceerd',
    publishFailedBody: 'Er is niets veranderd voor {team}. Probeer het opnieuw.',
    checkAgain: 'Opnieuw controleren',
    tryAgain: 'Opnieuw proberen',
    askOwner: '{owner} vragen',
    restoreAction: 'Herstellen…',
    copyLink: 'Link kopiëren',
    copied: 'Link gekopieerd',
    done: 'Klaar',
    peopleList: '{names} uit {place}',
    peopleAndOthers: '{first}, {second} en {count} anderen',
    peopleAndOneOther: '{first}, {second} en 1 andere',
    agentPublished: 'Gepubliceerd · v{n} door {agent}',
    undo: 'Ongedaan maken',
    restoreVersion: 'v{prev} herstellen',
    agentPublishedNote:
      'De agent heeft hem met zijn controles gepubliceerd. Maak het ongedaan of herstel v{prev} via Versies.',
    shareTitle: '{app} delen',
    shareSearch: 'Personen of groepen bij {company} toevoegen',
    roleUse: 'Kan gebruiken',
    roleUseHint: 'Opent de app en voegt items toe. Ziet alleen wat de app toont.',
    roleEdit: 'Kan bewerken',
    roleEditHint:
      'Wijzigt de app in de chat; ziet Gegevens, Geschiedenis en Versies; kan exporteren. Kan geen gegevens wissen of herstellen.',
    rolePublish: 'Kan publiceren',
    rolePublishHint: 'Alleen personen met publicatierecht: {names}.',
    roleRemove: 'Verwijderen',
    owner: 'Eigenaar',
    ownerLine: '{you} (jij)',
    ownerPublisher: 'Eigenaar · mag publiceren',
    throughConnection: 'Anderen zien gegevens via jouw verbinding',
    throughConnectionBody:
      'De app toont {system} alleen via wat de app zelf laat zien, niet je andere {system}-gegevens.',
    sharePublishDisabledReason: 'Vereist publicatierecht',
    accessTitle: 'Hoe kijkers bedrijfssystemen bereiken',
    accessOwnerYours: 'Via jouw verbinding · iedereen ziet hetzelfde',
    accessOwnerOther: 'Via de verbinding van {owner} · iedereen ziet hetzelfde',
    accessViewer: 'Ieders eigen verbinding · iedereen ziet zijn eigen gegevens',
    accessConnected: '{k} van {n} personen in de doelgroep hebben {system} gekoppeld',
    accessChange: 'Wijzigen…',
    accessApplies: 'Wijzigingen gelden zodra je opnieuw publiceert',
    accessNarrowedPill: 'Niet meer toegestaan',
    accessNarrowed:
      '{company} heeft gewijzigd wat gedeelde apps uit {system} mogen lezen. Dat deel blijft verborgen tot je opnieuw kiest en opnieuw publiceert.',
    accessUnavailablePill: 'Verbinding niet beschikbaar',
    accessUnavailable: '{owner} moet {system} opnieuw koppelen',
    accessUnavailableTold: '{owner} is op de hoogte gebracht',
    unpublishTitle: 'Publicatie van {app} intrekken?',
    unpublishBody:
      '{team} kan hem dan niet meer openen. De {entries} items blijven bewaard en komen terug als je opnieuw publiceert.',
    unpublishConfirm: 'Publicatie intrekken',
    unpublishBusy: 'Publicatie wordt ingetrokken…',
    error: 'Dat is niet gelukt. Er is niets gewijzigd. Probeer het opnieuw.',
    checkChecking: 'Code en pakketten worden gecontroleerd…',
    checkClean: 'Geen geheimen of kwetsbare pakketten gevonden in deze versie.',
    checkWarnings: '{things}. Je kunt nog steeds publiceren.',
    checkHeldSecret: 'Publiceren is tegengehouden. Er is een geheim gevonden in de code.',
    checkHeldDependency:
      'Publiceren is tegengehouden. Een pakket heeft een bekende ernstige kwetsbaarheid.',
    checkHeldMany: 'Publiceren is tegengehouden. Je moet eerst {issues} oplossen.',
    checkExpired: 'Deze controle is verouderd.',
    checkIncomplete:
      'De controle kon niet worden voltooid. Publiceren blijft tegengehouden tot dat wel lukt.',
    checkHeldPrivate: 'Publiceren is tegengehouden door een beveiligingscontrole.',
    checkLastChecked: 'Laatst gecontroleerd: {time}',
    severityCritical: 'Kritiek',
    severityHigh: 'Hoog',
    severityModerate: 'Gemiddeld',
    severityLow: 'Laag',
    severityUnrated: 'Niet beoordeeld',
    severityWordCritical: 'kritieke',
    severityWordHigh: 'ernstige',
    severityWordModerate: 'matige',
    severityWordLow: 'kleine',
    secretApiKey: 'API-sleutel in de code',
    secretPrivateKey: 'Privésleutel in de code',
    secretAccessToken: 'Toegangstoken in de code',
    secretPassword: 'Wachtwoord in de code',
    secretUnknown: 'Mogelijk geheim in de code',
    secretWhere: '{area} {file}, regel {line}',
    secretAreaCode: 'Code:',
    secretAreaBuilt: 'Gebouwde bestanden:',
    secretWhy: 'Iedereen die de app kan openen, kan het lezen en gebruiken.',
    secretWhat:
      'Zet het in een verbinding of een geheime instelling. Vervang daarna de sleutel bij de aanbieder, want die is mogelijk al gekopieerd.',
    depTitle: '{package} {version} heeft een bekende {severity} kwetsbaarheid',
    depTitleUnrated: '{package} {version} heeft een kwetsbaarheid die nog niet is beoordeeld',
    depWhere: 'Pakket dat de app gebruikt',
    depWhy: 'Een gepubliceerd beveiligingsadvies is van toepassing op deze versie.',
    depViewAdvisory: 'Advies bekijken',
    depAdviceUpdate: 'Werk bij naar een versie met een oplossing.',
    depAdviceNoFix:
      'Er is nog geen verholpen versie. Je kunt het risico accepteren of het pakket verwijderen.',
    depAdviceUnrated: 'Werk het bij, of wacht tot het advies is beoordeeld.',
    actionFixAndRecheck: 'Oplossen en opnieuw controleren',
    actionCheckAgain: 'Opnieuw controleren',
    actionReview: 'Bekijken',
    actionPublishAnyway: 'Risico accepteren…',
    publishAnywayTitle: 'Deze bekende kwetsbaarheid accepteren?',
    publishAnywayBody:
      '{package} {version} heeft een {severity} kwetsbaarheid waarvoor nog geen oplossing is. Mensen die deze app gebruiken, kunnen hierdoor worden getroffen. Dit wordt vastgelegd met je naam en het tijdstip.',
    publishAnywayConfirm: 'Risico accepteren',
    publishAnywayCancel: 'Annuleren',
    publishAnywayAcknowledged: 'Risico geaccepteerd · bevestigd door {name} · {date}',
    fixRequestSecret:
      'Geheim ({label}) in {file} naar een geheime instelling verplaatsen en opnieuw controleren',
    fixRequestPackage:
      '{package} bijwerken naar een versie met een oplossing en opnieuw controleren',
    chatHeldSecret:
      'De publicatiecontrole heeft een geheim in de code gevonden. Publiceren is tegengehouden.',
    chatHeldMany: 'De publicatiecontrole heeft {issues} gevonden. Publiceren is tegengehouden.',
    chatWarnings: 'De publicatiecontrole heeft {things} gevonden. Je kunt nog steeds publiceren.',
    chatReview: 'Bekijken',
    checkIssues: 'one:probleem|other:problemen',
    checkThings: 'one:punt om te bekijken|other:punten om te bekijken',
  },
  pl: {
    title: 'Opublikuj {app} v{n}',
    firstTitle: 'Opublikuj {app}',
    lead: '{team} będzie korzystać z v{n}. v{prev} pozostanie w Wersjach.',
    firstLead:
      '{team} będzie mieć dostęp do aplikacji {app}. Publikację możesz wycofać w każdej chwili.',
    close: 'Zamknij',
    whatsNew: 'Co nowego w v{n}',
    whatsNewPlaceholder: 'Jedna linijka, którą zobaczą użytkownicy',
    changesHeading: 'Co zmienia się dla użytkowników',
    rowLooks: 'Wygląd',
    rowStores: 'Przechowuje',
    rowReads: 'Odczytuje',
    rowWrites: 'Zapisuje',
    rowRuns: 'Uruchamia',
    whoHeading: 'Kto może z niej korzystać',
    whoMeta: '{team} · liczba osób: {people}',
    whoRole: 'Może używać · tak jak w v{prev}',
    whoRoleFirst: 'Może używać',
    changeInShare: 'Zmień w Udostępnianiu',
    checksHeading: 'Kontrole',
    blocksPublishing: 'Blokuje publikację',
    fixInChat: 'Napraw na czacie',
    checksPassed: 'Zaliczone kontrole: {count}',
    show: 'Pokaż',
    hide: 'Ukryj',
    contractionHeading: 'Ta wersja coś usuwa',
    contractionNote:
      'Zaznacz każdą pozycję, aby potwierdzić. Nic nie zostanie usunięte, dopóki nie opublikujesz.',
    permission: 'Możesz publikować, bo masz uprawnienie do publikowania w firmie {company}.',
    undoStays: 'Cofnięcie pozostanie dostępne po publikacji, bo nic nie zostanie usunięte.',
    undoGone: 'Cofnięcie nie będzie dostępne po publikacji, bo usuwa to dane.',
    footerAudience: 'Tylko członkowie firmy. Bez linków publicznych.',
    footerBlocked: 'Kontrole blokujące publikację: {count}',
    footerBlockedMany: 'Kontrole blokujące publikację: {count}',
    footerContraction: 'Potwierdź, co zostanie usunięte, aby opublikować.',
    cancel: 'Anuluj',
    publish: 'Opublikuj v{n}',
    publishFirst: 'Opublikuj',
    publishing: 'Publikowanie…',
    doneTitle: '{team} ma już v{n}',
    doneFirstTitle: '{team} ma już dostęp do aplikacji {app}',
    doneBody:
      'Liczba osób, które od teraz korzystają z v{n}: {people}. Ich wpisy ({entries}) pozostają bez zmian.',
    doneBodyUncounted:
      'Liczba osób, które od teraz korzystają z v{n}: {people}. Ich wpisy pozostają bez zmian.',
    doneBodyBare: 'Liczba osób, które od teraz korzystają z v{n}: {people}.',
    doneFirstBody:
      'Liczba osób, które mogą ją już otworzyć: {people}. Ich wpisy zaczynają się tutaj.',
    doneWhere: 'w ich Bibliotece i wśród przypiętych dla zespołu',
    doneThey: 'Zobaczą {what} przy następnym otwarciu aplikacji {app}.',
    doneUndoNote: 'Nic nie zostało usunięte, więc cofnięcie pozostaje dostępne.',
    undoPublish: 'Cofnij publikację',
    publishUnknownTitle: 'Nie wiemy, czy v{n} została opublikowana',
    publishUnknownBody:
      '{team} może już ją mieć albo nie. Sprawdź przed ponowną publikacją, aby nic nie zostało opublikowane dwa razy.',
    publishFailedTitle: 'Nie opublikowano v{n}',
    publishFailedBody: '{team} – bez zmian. Spróbuj ponownie.',
    checkAgain: 'Sprawdź ponownie',
    tryAgain: 'Spróbuj ponownie',
    askOwner: 'Zapytaj: {owner}',
    restoreAction: 'Przywróć…',
    copyLink: 'Kopiuj link',
    copied: 'Skopiowano link',
    done: 'Gotowe',
    peopleList: '{names} w {place}',
    peopleAndOthers: '{first}, {second} i inni ({count})',
    peopleAndOneOther: '{first}, {second} i jeszcze 1 osoba',
    agentPublished: 'Opublikowano · v{n} przez agenta {agent}',
    undo: 'Cofnij',
    restoreVersion: 'Przywróć v{prev}',
    agentPublishedNote:
      'Agent opublikował ją po własnych kontrolach. Cofnij lub przywróć v{prev} w Wersjach.',
    shareTitle: 'Udostępnij {app}',
    shareSearch: 'Dodaj osoby lub grupy z firmy {company}',
    roleUse: 'Może używać',
    roleUseHint: 'Otwiera aplikację i dodaje wpisy. Widzi tylko to, co pokazuje aplikacja.',
    roleEdit: 'Może edytować',
    roleEditHint:
      'Zmienia aplikację na czacie; widzi Dane, Historię i Wersje; może eksportować. Nie może czyścić ani przywracać danych.',
    rolePublish: 'Może publikować',
    rolePublishHint: 'Tylko osoby z uprawnieniem do publikowania: {names}.',
    roleRemove: 'Usuń',
    owner: 'Właściciel',
    ownerLine: '{you} (Ty)',
    ownerPublisher: 'Właściciel · publikujący',
    throughConnection: 'Użytkownicy widzą dane przez Twoje połączenie',
    throughConnectionBody:
      'Aplikacja pokazuje dane z systemu {system} tylko w zakresie tego, co sama wyświetla, a nie Twoje pozostałe dane z systemu {system}.',
    sharePublishDisabledReason: 'Wymaga uprawnienia do publikowania',
    accessTitle: 'Jak odbiorcy łączą się z systemami firmy',
    accessOwnerYours: 'Przez Twoje połączenie · wszyscy widzą to samo',
    accessOwnerOther: 'Przez połączenie użytkownika {owner} · wszyscy widzą to samo',
    accessViewer: 'Własne połączenie każdej osoby · każdy widzi swoje dane',
    accessConnected: 'Odbiorcy połączeni z systemem {system}: {k} z {n}',
    accessChange: 'Zmień…',
    accessApplies: 'Zmiany obowiązują po ponownej publikacji',
    accessNarrowedPill: 'Już niedozwolone',
    accessNarrowed:
      'Firma {company} zmieniła, co udostępnione aplikacje mogą odczytywać z systemu {system}. Ta część jest ukryta, dopóki ponownie nie wybierzesz i nie opublikujesz.',
    accessUnavailablePill: 'Połączenie niedostępne',
    accessUnavailable: '{owner} musi ponownie połączyć system {system}',
    accessUnavailableTold: 'Powiadomiliśmy użytkownika {owner}',
    unpublishTitle: 'Wycofać publikację aplikacji {app}?',
    unpublishBody:
      '{team} nie będzie już mieć do niej dostępu. Ich wpisy ({entries}) zostaną zachowane i wrócą, jeśli opublikujesz ją ponownie.',
    unpublishConfirm: 'Wycofaj publikację',
    unpublishBusy: 'Wycofywanie publikacji…',
    error: 'To się nie udało. Nic nie zostało zmienione. Spróbuj ponownie.',
    checkChecking: 'Sprawdzanie kodu i pakietów…',
    checkClean: 'W tej wersji nie znaleziono sekretów ani podatnych pakietów.',
    checkWarnings: '{things}. Nadal możesz opublikować.',
    checkHeldSecret: 'Publikacja jest wstrzymana. W kodzie znaleziono sekret.',
    checkHeldDependency:
      'Publikacja jest wstrzymana. Pakiet ma znaną poważną lukę w zabezpieczeniach.',
    checkHeldMany: 'Publikacja jest wstrzymana. Najpierw trzeba naprawić {issues}.',
    checkExpired: 'Ta kontrola jest nieaktualna.',
    checkIncomplete:
      'Nie udało się dokończyć kontroli. Publikacja pozostaje wstrzymana, dopóki się nie zakończy.',
    checkHeldPrivate: 'Publikacja jest wstrzymana przez kontrolę bezpieczeństwa.',
    checkLastChecked: 'Ostatnio sprawdzono: {time}',
    severityCritical: 'Krytyczna',
    severityHigh: 'Wysoka',
    severityModerate: 'Umiarkowana',
    severityLow: 'Niska',
    severityUnrated: 'Bez oceny',
    severityWordCritical: 'krytyczną',
    severityWordHigh: 'poważną',
    severityWordModerate: 'umiarkowaną',
    severityWordLow: 'drobną',
    secretApiKey: 'Klucz API w kodzie',
    secretPrivateKey: 'Klucz prywatny w kodzie',
    secretAccessToken: 'Token dostępu w kodzie',
    secretPassword: 'Hasło w kodzie',
    secretUnknown: 'Możliwy sekret w kodzie',
    secretWhere: '{area} {file}, wiersz {line}',
    secretAreaCode: 'Kod:',
    secretAreaBuilt: 'Zbudowane pliki:',
    secretWhy: 'Każdy, kto może otworzyć aplikację, mógłby go odczytać i użyć.',
    secretWhat:
      'Przenieś go do połączenia lub ustawienia tajnego. Następnie wymień klucz u jego dostawcy, bo mógł już zostać skopiowany.',
    depTitle: '{package} {version} ma znaną {severity} lukę w zabezpieczeniach',
    depTitleUnrated: '{package} {version} ma lukę w zabezpieczeniach, która nie ma jeszcze oceny',
    depWhere: 'Pakiet używany przez aplikację',
    depWhy: 'Opublikowany komunikat bezpieczeństwa dotyczy tej wersji.',
    depViewAdvisory: 'Zobacz komunikat',
    depAdviceUpdate: 'Zaktualizuj do poprawionej wersji.',
    depAdviceNoFix:
      'Nie ma jeszcze poprawionej wersji. Możesz zaakceptować ryzyko lub usunąć pakiet.',
    depAdviceUnrated: 'Zaktualizuj go lub poczekaj, aż komunikat otrzyma ocenę.',
    actionFixAndRecheck: 'Napraw i sprawdź ponownie',
    actionCheckAgain: 'Sprawdź ponownie',
    actionReview: 'Przejrzyj',
    actionPublishAnyway: 'Zaakceptuj ryzyko…',
    publishAnywayTitle: 'Zaakceptować tę znaną podatność?',
    publishAnywayBody:
      '{package} {version} ma {severity} lukę w zabezpieczeniach, dla której nie ma jeszcze poprawki. Może to dotyczyć osób korzystających z tej aplikacji. Zostanie to zapisane z Twoim imieniem i nazwiskiem oraz godziną.',
    publishAnywayConfirm: 'Zaakceptuj ryzyko',
    publishAnywayCancel: 'Anuluj',
    publishAnywayAcknowledged: 'Ryzyko zaakceptowane · potwierdzenie: {name} · {date}',
    fixRequestSecret:
      'Przenieś sekret ({label}) z pliku {file} do ustawienia tajnego i sprawdź ponownie',
    fixRequestPackage: 'Zaktualizuj {package} do poprawionej wersji i sprawdź ponownie',
    chatHeldSecret: 'Kontrola publikacji znalazła sekret w kodzie. Publikacja jest wstrzymana.',
    chatHeldMany: 'Kontrola publikacji znalazła {issues}. Publikacja jest wstrzymana.',
    chatWarnings: 'Kontrola publikacji znalazła {things}. Nadal możesz opublikować.',
    chatReview: 'Przejrzyj',
    checkIssues: 'one:problem|few:problemy|many:problemów|other:problemu',
    checkThings:
      'one:rzecz do przejrzenia|few:rzeczy do przejrzenia|many:rzeczy do przejrzenia|other:rzeczy do przejrzenia',
  },
  pt: {
    title: 'Publicar {app} v{n}',
    firstTitle: 'Publicar {app}',
    lead: '{team} vai utilizar a v{n}. A v{prev} fica em Versões.',
    firstLead: '{team} vai poder abrir {app}. Pode retirar a publicação a qualquer momento.',
    close: 'Fechar',
    whatsNew: 'Novidades da v{n}',
    whatsNewPlaceholder: 'Uma linha que todos vão ver',
    changesHeading: 'O que muda para os utilizadores',
    rowLooks: 'Aspeto',
    rowStores: 'Armazena',
    rowReads: 'Lê',
    rowWrites: 'Escreve',
    rowRuns: 'Executa',
    whoHeading: 'Quem pode utilizá-la',
    whoMeta: '{team} · {people} pessoas',
    whoRole: 'Pode utilizar · igual à v{prev}',
    whoRoleFirst: 'Pode utilizar',
    changeInShare: 'Alterar em Partilhar',
    checksHeading: 'Verificações',
    blocksPublishing: 'Bloqueia a publicação',
    fixInChat: 'Corrigir no chat',
    checksPassed: '{count} verificações aprovadas',
    show: 'Mostrar',
    hide: 'Ocultar',
    contractionHeading: 'Esta versão remove algo',
    contractionNote: 'Assinale cada item para confirmar. Nada é removido até publicar.',
    permission: 'Pode publicar porque tem a permissão de publicação em {company}.',
    undoStays: 'Anular continua disponível depois de publicar, porque nada é removido.',
    undoGone: 'Anular não fica disponível depois de publicar, porque isto remove dados.',
    footerAudience: 'Apenas membros da empresa. Sem ligações públicas.',
    footerBlocked: '{count} verificação bloqueia a publicação',
    footerBlockedMany: '{count} verificações bloqueiam a publicação',
    footerContraction: 'Confirme o que é removido para publicar.',
    cancel: 'Cancelar',
    publish: 'Publicar v{n}',
    publishFirst: 'Publicar',
    publishing: 'A publicar…',
    doneTitle: 'A v{n} já está disponível para {team}',
    doneFirstTitle: '{app} já está disponível para {team}',
    doneBody:
      '{people} pessoas utilizam a v{n} a partir de agora. Os seus {entries} registos ficam como estão.',
    doneBodyUncounted:
      '{people} pessoas utilizam a v{n} a partir de agora. Os seus registos ficam como estão.',
    doneBodyBare: '{people} pessoas utilizam a v{n} a partir de agora.',
    doneFirstBody: '{people} pessoas já a podem abrir. Os seus registos começam aqui.',
    doneWhere: 'na Biblioteca delas e afixada para a equipa',
    doneThey: 'Vão ver {what} da próxima vez que abrirem {app}.',
    doneUndoNote: 'Nada foi removido, por isso Anular continua disponível.',
    undoPublish: 'Anular publicação',
    publishUnknownTitle: 'Não sabemos se a v{n} foi publicada',
    publishUnknownBody:
      '{team} pode já a ter ou não. Verifique antes de publicar novamente, para que nada seja publicado duas vezes.',
    publishFailedTitle: 'A v{n} não foi publicada',
    publishFailedBody: 'Nada mudou para {team}. Tente novamente.',
    checkAgain: 'Verificar novamente',
    tryAgain: 'Tentar novamente',
    askOwner: 'Perguntar a {owner}',
    restoreAction: 'Restaurar…',
    copyLink: 'Copiar ligação',
    copied: 'Ligação copiada',
    done: 'Concluído',
    peopleList: '{names} em {place}',
    peopleAndOthers: '{first}, {second} e mais {count}',
    peopleAndOneOther: '{first}, {second} e mais 1',
    agentPublished: 'Publicada · v{n} por {agent}',
    undo: 'Anular',
    restoreVersion: 'Restaurar v{prev}',
    agentPublishedNote:
      'O agente publicou-a com as suas verificações. Anule ou restaure a v{prev} em Versões.',
    shareTitle: 'Partilhar {app}',
    shareSearch: 'Adicionar pessoas ou grupos de {company}',
    roleUse: 'Pode utilizar',
    roleUseHint: 'Abre a app e adiciona registos. Só vê o que a app mostra.',
    roleEdit: 'Pode editar',
    roleEditHint:
      'Altera a app no chat; vê Dados, Histórico e Versões; pode exportar. Não pode limpar nem restaurar dados.',
    rolePublish: 'Pode publicar',
    rolePublishHint: 'Apenas pessoas com a permissão de publicação: {names}.',
    roleRemove: 'Remover',
    owner: 'Proprietário',
    ownerLine: '{you} (você)',
    ownerPublisher: 'Proprietário · pode publicar',
    throughConnection: 'As pessoas veem os dados através da sua ligação',
    throughConnectionBody:
      'A app mostra {system} apenas através do que ela própria mostra, não os seus outros dados de {system}.',
    sharePublishDisabledReason: 'Requer a permissão de publicação',
    accessTitle: 'Como os leitores acedem aos sistemas da empresa',
    accessOwnerYours: 'Através da sua ligação · todos veem o mesmo',
    accessOwnerOther: 'Através da ligação de {owner} · todos veem o mesmo',
    accessViewer: 'A ligação de cada pessoa · cada um vê o seu',
    accessConnected: '{k} de {n} pessoas do público ligaram {system}',
    accessChange: 'Alterar…',
    accessApplies: 'As alterações aplicam-se quando publicar novamente',
    accessNarrowedPill: 'Já não é permitido',
    accessNarrowed:
      '{company} alterou o que as apps partilhadas podem ler de {system}. Essa parte fica oculta até escolher novamente e voltar a publicar.',
    accessUnavailablePill: 'Ligação indisponível',
    accessUnavailable: '{owner} tem de voltar a ligar {system}',
    accessUnavailableTold: '{owner} foi avisado',
    unpublishTitle: 'Retirar a publicação de {app}?',
    unpublishBody:
      '{team} deixa de a poder abrir. Os {entries} registos são mantidos e voltam se publicar novamente.',
    unpublishConfirm: 'Retirar publicação',
    unpublishBusy: 'A retirar a publicação…',
    error: 'Não funcionou. Nada foi alterado. Tente novamente.',
    checkChecking: 'A verificar o código e os pacotes…',
    checkClean: 'Não foram encontrados segredos nem pacotes vulneráveis nesta versão.',
    checkWarnings: '{things}. Pode publicar na mesma.',
    checkHeldSecret: 'A publicação está retida. Foi encontrado um segredo no código.',
    checkHeldDependency:
      'A publicação está retida. Um pacote tem uma vulnerabilidade grave conhecida.',
    checkHeldMany: 'A publicação está retida. É preciso corrigir primeiro {issues}.',
    checkExpired: 'Esta verificação está desatualizada.',
    checkIncomplete:
      'Não foi possível concluir a verificação. A publicação fica retida até que seja concluída.',
    checkHeldPrivate: 'A publicação está retida por uma verificação de segurança.',
    checkLastChecked: 'Última verificação: {time}',
    severityCritical: 'Crítica',
    severityHigh: 'Alta',
    severityModerate: 'Moderada',
    severityLow: 'Baixa',
    severityUnrated: 'Sem classificação',
    severityWordCritical: 'crítica',
    severityWordHigh: 'grave',
    severityWordModerate: 'moderada',
    severityWordLow: 'ligeira',
    secretApiKey: 'Chave de API no código',
    secretPrivateKey: 'Chave privada no código',
    secretAccessToken: 'Token de acesso no código',
    secretPassword: 'Palavra-passe no código',
    secretUnknown: 'Possível segredo no código',
    secretWhere: '{area} {file}, linha {line}',
    secretAreaCode: 'Código:',
    secretAreaBuilt: 'Ficheiros compilados:',
    secretWhy: 'Qualquer pessoa que consiga abrir a App poderia lê-lo e usá-lo.',
    secretWhat:
      'Mova-o para uma ligação ou uma definição secreta. Depois substitua a chave junto do respetivo fornecedor, porque pode já ter sido copiada.',
    depTitle: '{package} {version} tem uma vulnerabilidade {severity} conhecida',
    depTitleUnrated: '{package} {version} tem uma vulnerabilidade que ainda não foi classificada',
    depWhere: 'Pacote usado pela App',
    depWhy: 'Um aviso de segurança publicado afeta esta versão.',
    depViewAdvisory: 'Ver aviso',
    depAdviceUpdate: 'Atualize para uma versão corrigida.',
    depAdviceNoFix:
      'Ainda não existe uma versão corrigida. Pode aceitar o risco ou remover o pacote.',
    depAdviceUnrated: 'Atualize-o ou aguarde até que o aviso seja classificado.',
    actionFixAndRecheck: 'Corrigir e verificar novamente',
    actionCheckAgain: 'Verificar novamente',
    actionReview: 'Rever',
    actionPublishAnyway: 'Aceitar o risco…',
    publishAnywayTitle: 'Aceitar esta vulnerabilidade conhecida?',
    publishAnywayBody:
      '{package} {version} tem uma vulnerabilidade {severity} ainda sem correção. As pessoas que usam esta App podem ser afetadas. Isto fica registado com o seu nome e a hora.',
    publishAnywayConfirm: 'Aceitar o risco',
    publishAnywayCancel: 'Cancelar',
    publishAnywayAcknowledged: 'Risco aceite · confirmado por {name} · {date}',
    fixRequestSecret:
      'Mover o segredo ({label}) de {file} para uma definição secreta e verificar novamente',
    fixRequestPackage: 'Atualizar {package} para uma versão corrigida e verificar novamente',
    chatHeldSecret:
      'A verificação de publicação encontrou um segredo no código. A publicação está retida.',
    chatHeldMany: 'A verificação de publicação encontrou {issues}. A publicação está retida.',
    chatWarnings: 'A verificação de publicação encontrou {things}. Pode publicar na mesma.',
    chatReview: 'Rever',
    checkIssues: 'one:problema|many:de problemas|other:problemas',
    checkThings: 'one:ponto a rever|many:de pontos a rever|other:pontos a rever',
  },
  ro: {
    title: 'Publicați {app} v{n}',
    firstTitle: 'Publicați {app}',
    lead: '{team} va folosi v{n}. v{prev} rămâne în Versiuni.',
    firstLead: '{team} va putea deschide {app}. Puteți retrage publicarea oricând.',
    close: 'Închideți',
    whatsNew: 'Ce este nou în v{n}',
    whatsNewPlaceholder: 'Un rând pe care îl vor vedea oamenii',
    changesHeading: 'Ce se schimbă pentru oameni',
    rowLooks: 'Aspect',
    rowStores: 'Stochează',
    rowReads: 'Citește',
    rowWrites: 'Scrie',
    rowRuns: 'Rulează',
    whoHeading: 'Cine o poate folosi',
    whoMeta: '{team} · persoane: {people}',
    whoRole: 'Poate folosi · la fel ca v{prev}',
    whoRoleFirst: 'Poate folosi',
    changeInShare: 'Modificați în Partajare',
    checksHeading: 'Verificări',
    blocksPublishing: 'Blochează publicarea',
    fixInChat: 'Remediați în chat',
    checksPassed: 'Verificări trecute: {count}',
    show: 'Afișați',
    hide: 'Ascundeți',
    contractionHeading: 'Această versiune elimină ceva',
    contractionNote:
      'Bifați fiecare element pentru a confirma. Nu se elimină nimic până nu publicați.',
    permission: 'Puteți publica deoarece aveți permisiunea de publicare la {company}.',
    undoStays: 'Anularea rămâne disponibilă după publicare, deoarece nu se elimină nimic.',
    undoGone: 'Anularea nu este disponibilă după publicare, deoarece se elimină date.',
    footerAudience: 'Doar membrii companiei. Fără linkuri publice.',
    footerBlocked: 'Verificări care blochează publicarea: {count}',
    footerBlockedMany: 'Verificări care blochează publicarea: {count}',
    footerContraction: 'Confirmați ce se elimină pentru a publica.',
    cancel: 'Renunțați',
    publish: 'Publicați v{n}',
    publishFirst: 'Publicați',
    publishing: 'Se publică…',
    doneTitle: 'v{n} este activă pentru {team}',
    doneFirstTitle: '{team} poate folosi acum {app}',
    doneBody:
      'Persoane care folosesc v{n} de acum: {people}. Înregistrările lor ({entries}) rămân așa cum sunt.',
    doneBodyUncounted:
      'Persoane care folosesc v{n} de acum: {people}. Înregistrările lor rămân așa cum sunt.',
    doneBodyBare: 'Persoane care folosesc v{n} de acum: {people}.',
    doneFirstBody: 'Persoane care o pot deschide acum: {people}. Înregistrările lor încep aici.',
    doneWhere: 'în Biblioteca lor și fixată pentru echipă',
    doneThey: 'Vor vedea {what} data viitoare când deschid {app}.',
    doneUndoNote: 'Nu s-a eliminat nimic, deci anularea rămâne disponibilă.',
    undoPublish: 'Anulați publicarea',
    publishUnknownTitle: 'Nu știm dacă v{n} a fost publicată',
    publishUnknownBody:
      'Este posibil ca {team} să o aibă deja sau nu. Verificați înainte de a publica din nou, ca nimic să nu fie publicat de două ori.',
    publishFailedTitle: 'v{n} nu a fost publicată',
    publishFailedBody: 'Nu s-a schimbat nimic pentru {team}. Încercați din nou.',
    checkAgain: 'Verificați din nou',
    tryAgain: 'Încercați din nou',
    askOwner: 'Întrebați: {owner}',
    restoreAction: 'Restaurați…',
    copyLink: 'Copiați linkul',
    copied: 'Link copiat',
    done: 'Gata',
    peopleList: '{names} în {place}',
    peopleAndOthers: '{first}, {second} și alții ({count})',
    peopleAndOneOther: '{first}, {second} și încă 1 persoană',
    agentPublished: 'Publicată · v{n} de către {agent}',
    undo: 'Anulați',
    restoreVersion: 'Restaurați v{prev}',
    agentPublishedNote:
      'Agentul a publicat-o cu verificările sale. Anulați sau restaurați v{prev} din Versiuni.',
    shareTitle: 'Partajați {app}',
    shareSearch: 'Adăugați persoane sau grupuri din {company}',
    roleUse: 'Poate folosi',
    roleUseHint: 'Deschide aplicația și adaugă înregistrări. Vede doar ce afișează aplicația.',
    roleEdit: 'Poate edita',
    roleEditHint:
      'Modifică aplicația în chat; vede Date, Istoric și Versiuni; poate exporta. Nu poate șterge sau restaura date.',
    rolePublish: 'Poate publica',
    rolePublishHint: 'Doar persoanele cu permisiunea de publicare: {names}.',
    roleRemove: 'Eliminați',
    owner: 'Proprietar',
    ownerLine: '{you} (dvs.)',
    ownerPublisher: 'Proprietar · publicator',
    throughConnection: 'Oamenii văd datele prin conexiunea dvs.',
    throughConnectionBody:
      'Aplicația afișează {system} doar prin ceea ce arată, nu și celelalte date {system} ale dvs.',
    sharePublishDisabledReason: 'Necesită permisiunea de publicare',
    accessTitle: 'Cum ajung utilizatorii la sistemele companiei',
    accessOwnerYours: 'Prin conexiunea dvs. · toți văd același lucru',
    accessOwnerOther: 'Prin conexiunea utilizatorului {owner} · toți văd același lucru',
    accessViewer: 'Conexiunea proprie a fiecăruia · fiecare își vede propriile date',
    accessConnected: 'Membri ai audienței care au conectat {system}: {k} din {n}',
    accessChange: 'Modificați…',
    accessApplies: 'Modificările se aplică atunci când publicați din nou',
    accessNarrowedPill: 'Nu mai este permis',
    accessNarrowed:
      '{company} a modificat ce pot citi aplicațiile partajate din {system}. Partea respectivă este ascunsă până când alegeți din nou și republicați.',
    accessUnavailablePill: 'Conexiune indisponibilă',
    accessUnavailable: '{owner} trebuie să reconecteze {system}',
    accessUnavailableTold: 'S-a trimis o notificare către {owner}',
    unpublishTitle: 'Retrageți publicarea aplicației {app}?',
    unpublishBody:
      '{team} nu o va mai putea deschide. Înregistrările lor ({entries}) se păstrează și revin dacă publicați din nou.',
    unpublishConfirm: 'Retrageți publicarea',
    unpublishBusy: 'Se retrage publicarea…',
    error: 'Nu a funcționat. Nu s-a schimbat nimic. Încercați din nou.',
    checkChecking: 'Se verifică codul și pachetele…',
    checkClean: 'Nu s-au găsit secrete sau pachete vulnerabile în această versiune.',
    checkWarnings: '{things}. Puteți publica oricum.',
    checkHeldSecret: 'Publicarea este suspendată. S-a găsit un secret în cod.',
    checkHeldDependency:
      'Publicarea este suspendată. Un pachet are o vulnerabilitate gravă cunoscută.',
    checkHeldMany: 'Publicarea este suspendată. Mai întâi trebuie să remediați {issues}.',
    checkExpired: 'Această verificare nu mai este actuală.',
    checkIncomplete:
      'Verificarea nu s-a putut finaliza. Publicarea rămâne suspendată până când se finalizează.',
    checkHeldPrivate: 'Publicarea este suspendată de o verificare de securitate.',
    checkLastChecked: 'Ultima verificare: {time}',
    severityCritical: 'Critică',
    severityHigh: 'Ridicată',
    severityModerate: 'Moderată',
    severityLow: 'Scăzută',
    severityUnrated: 'Neevaluată',
    severityWordCritical: 'critică',
    severityWordHigh: 'gravă',
    severityWordModerate: 'moderată',
    severityWordLow: 'minoră',
    secretApiKey: 'Cheie API în cod',
    secretPrivateKey: 'Cheie privată în cod',
    secretAccessToken: 'Token de acces în cod',
    secretPassword: 'Parolă în cod',
    secretUnknown: 'Posibil secret în cod',
    secretWhere: '{area} {file}, linia {line}',
    secretAreaCode: 'Cod:',
    secretAreaBuilt: 'Fișiere generate:',
    secretWhy: 'Oricine poate deschide aplicația l-ar putea citi și folosi.',
    secretWhat:
      'Mutați-l într-o conexiune sau într-o setare secretă. Apoi înlocuiți cheia la furnizorul ei, deoarece este posibil să fi fost deja copiată.',
    depTitle: '{package} {version} are o vulnerabilitate {severity} cunoscută',
    depTitleUnrated: '{package} {version} are o vulnerabilitate care nu este încă evaluată',
    depWhere: 'Pachet folosit de aplicație',
    depWhy: 'Un aviz de securitate publicat afectează această versiune.',
    depViewAdvisory: 'Vedeți avizul',
    depAdviceUpdate: 'Actualizați la o versiune remediată.',
    depAdviceNoFix:
      'Nu există încă o versiune corectată. Poți accepta riscul sau elimina pachetul.',
    depAdviceUnrated: 'Actualizați-l sau așteptați până când avizul este evaluat.',
    actionFixAndRecheck: 'Remediați și verificați din nou',
    actionCheckAgain: 'Verificați din nou',
    actionReview: 'Examinați',
    actionPublishAnyway: 'Acceptă riscul…',
    publishAnywayTitle: 'Accepți această vulnerabilitate cunoscută?',
    publishAnywayBody:
      '{package} {version} are o vulnerabilitate {severity} care încă nu are o remediere. Persoanele care folosesc această aplicație ar putea fi afectate. Acțiunea se înregistrează cu numele dvs. și ora.',
    publishAnywayConfirm: 'Acceptă riscul',
    publishAnywayCancel: 'Renunțați',
    publishAnywayAcknowledged: 'Risc acceptat · confirmat de {name} · {date}',
    fixRequestSecret:
      'Mutați secretul ({label}) din {file} într-o setare secretă și verificați din nou',
    fixRequestPackage: 'Actualizați {package} la o versiune remediată și verificați din nou',
    chatHeldSecret:
      'Verificarea de publicare a găsit un secret în cod. Publicarea este suspendată.',
    chatHeldMany: 'Verificarea de publicare a găsit {issues}. Publicarea este suspendată.',
    chatWarnings: 'Verificarea de publicare a găsit {things}. Puteți publica oricum.',
    chatReview: 'Examinați',
    checkIssues: 'one:problemă|few:probleme|other:de probleme',
    checkThings: 'one:aspect de examinat|few:aspecte de examinat|other:de aspecte de examinat',
  },
  sk: {
    title: 'Publikovať {app} v{n}',
    firstTitle: 'Publikovať {app}',
    lead: '{team} bude používať v{n}. v{prev} zostane vo Verziách.',
    firstLead: '{team} bude môcť otvoriť aplikáciu {app}. Publikovanie môžete kedykoľvek zrušiť.',
    close: 'Zavrieť',
    whatsNew: 'Čo je nové vo v{n}',
    whatsNewPlaceholder: 'Jeden riadok, ktorý ľudia uvidia',
    changesHeading: 'Čo sa pre ľudí zmení',
    rowLooks: 'Vzhľad',
    rowStores: 'Ukladá',
    rowReads: 'Číta',
    rowWrites: 'Zapisuje',
    rowRuns: 'Spúšťa',
    whoHeading: 'Kto ju môže používať',
    whoMeta: '{team} · počet ľudí: {people}',
    whoRole: 'Môže používať · rovnako ako v{prev}',
    whoRoleFirst: 'Môže používať',
    changeInShare: 'Zmeniť v Zdieľaní',
    checksHeading: 'Kontroly',
    blocksPublishing: 'Blokuje publikovanie',
    fixInChat: 'Opraviť v chate',
    checksPassed: 'Úspešné kontroly: {count}',
    show: 'Zobraziť',
    hide: 'Skryť',
    contractionHeading: 'Táto verzia niečo odstraňuje',
    contractionNote: 'Každú položku potvrďte začiarknutím. Nič sa neodstráni, kým nepublikujete.',
    permission: 'Môžete publikovať, pretože máte vo firme {company} oprávnenie na publikovanie.',
    undoStays: 'Vrátenie späť zostane po publikovaní dostupné, pretože sa nič neodstraňuje.',
    undoGone: 'Vrátenie späť nebude po publikovaní dostupné, pretože sa tým odstránia údaje.',
    footerAudience: 'Len členovia firmy. Žiadne verejné odkazy.',
    footerBlocked: 'Kontroly blokujúce publikovanie: {count}',
    footerBlockedMany: 'Kontroly blokujúce publikovanie: {count}',
    footerContraction: 'Na publikovanie potvrďte, čo sa odstráni.',
    cancel: 'Zrušiť',
    publish: 'Publikovať v{n}',
    publishFirst: 'Publikovať',
    publishing: 'Publikuje sa…',
    doneTitle: '{team} už má v{n}',
    doneFirstTitle: '{team} už má aplikáciu {app}',
    doneBody:
      'Počet ľudí, ktorí odteraz používajú v{n}: {people}. Ich záznamy ({entries}) zostávajú bez zmeny.',
    doneBodyUncounted:
      'Počet ľudí, ktorí odteraz používajú v{n}: {people}. Ich záznamy zostávajú bez zmeny.',
    doneBodyBare: 'Počet ľudí, ktorí odteraz používajú v{n}: {people}.',
    doneFirstBody: 'Počet ľudí, ktorí ju teraz môžu otvoriť: {people}. Ich záznamy sa začínajú tu.',
    doneWhere: 'v ich Knižnici a medzi pripnutými pre tím',
    doneThey: 'Uvidia {what}, keď nabudúce otvoria aplikáciu {app}.',
    doneUndoNote: 'Nič sa neodstránilo, takže vrátenie späť zostáva dostupné.',
    undoPublish: 'Vrátiť publikovanie späť',
    publishUnknownTitle: 'Nevieme, či bola v{n} publikovaná',
    publishUnknownBody:
      '{team} ju možno už má, možno nie. Pred ďalším publikovaním to skontrolujte, aby sa nič nepublikovalo dvakrát.',
    publishFailedTitle: 'v{n} nebola publikovaná',
    publishFailedBody: 'Pre {team} sa nič nezmenilo. Skúste to znova.',
    checkAgain: 'Skontrolovať znova',
    tryAgain: 'Skúsiť znova',
    askOwner: 'Opýtať sa: {owner}',
    restoreAction: 'Obnoviť…',
    copyLink: 'Kopírovať odkaz',
    copied: 'Odkaz skopírovaný',
    done: 'Hotovo',
    peopleList: '{names} v {place}',
    peopleAndOthers: '{first}, {second} a ďalší ({count})',
    peopleAndOneOther: '{first}, {second} a 1 ďalšia osoba',
    agentPublished: 'Publikované · v{n} agentom {agent}',
    undo: 'Vrátiť späť',
    restoreVersion: 'Obnoviť v{prev}',
    agentPublishedNote:
      'Agent ju publikoval po vlastných kontrolách. Vo Verziách to môžete vrátiť späť alebo obnoviť v{prev}.',
    shareTitle: 'Zdieľať {app}',
    shareSearch: 'Pridať ľudí alebo skupiny z firmy {company}',
    roleUse: 'Môže používať',
    roleUseHint: 'Otvára aplikáciu a pridáva záznamy. Vidí len to, čo aplikácia zobrazuje.',
    roleEdit: 'Môže upravovať',
    roleEditHint:
      'Mení aplikáciu v chate; vidí Údaje, Históriu a Verzie; môže exportovať. Nemôže vymazať ani obnoviť údaje.',
    rolePublish: 'Môže publikovať',
    rolePublishHint: 'Len ľudia s oprávnením na publikovanie: {names}.',
    roleRemove: 'Odstrániť',
    owner: 'Vlastník',
    ownerLine: '{you} (vy)',
    ownerPublisher: 'Vlastník · publikujúci',
    throughConnection: 'Ľudia vidia údaje cez vaše pripojenie',
    throughConnectionBody:
      'Aplikácia ukazuje údaje zo systému {system} len v rozsahu toho, čo sama zobrazuje, nie vaše ostatné údaje zo systému {system}.',
    sharePublishDisabledReason: 'Vyžaduje oprávnenie na publikovanie',
    accessTitle: 'Ako sa používatelia dostanú k firemným systémom',
    accessOwnerYours: 'Cez vaše pripojenie · všetci vidia to isté',
    accessOwnerOther: 'Cez pripojenie používateľa {owner} · všetci vidia to isté',
    accessViewer: 'Vlastné pripojenie každého · každý vidí svoje',
    accessConnected: 'Ľudia v publiku s pripojeným systémom {system}: {k} z {n}',
    accessChange: 'Zmeniť…',
    accessApplies: 'Zmeny sa prejavia pri ďalšom publikovaní',
    accessNarrowedPill: 'Už nie je povolené',
    accessNarrowed:
      'Firma {company} zmenila, čo môžu zdieľané aplikácie čítať zo systému {system}. Táto časť je skrytá, kým znova nevyberiete a znova nepublikujete.',
    accessUnavailablePill: 'Pripojenie nie je dostupné',
    accessUnavailable: '{owner} musí znova pripojiť {system}',
    accessUnavailableTold: 'Upozornili sme používateľa {owner}',
    unpublishTitle: 'Zrušiť publikovanie aplikácie {app}?',
    unpublishBody:
      '{team} ju už nebude môcť otvoriť. Ich záznamy ({entries}) zostanú zachované a vrátia sa, ak ju znova publikujete.',
    unpublishConfirm: 'Zrušiť publikovanie',
    unpublishBusy: 'Ruší sa publikovanie…',
    error: 'Nepodarilo sa to. Nič sa nezmenilo. Skúste to znova.',
    checkChecking: 'Kontrola kódu a balíkov…',
    checkClean: 'V tejto verzii sa nenašli žiadne tajné údaje ani zraniteľné balíky.',
    checkWarnings: '{things}. Publikovať môžete aj tak.',
    checkHeldSecret: 'Publikovanie je pozastavené. V kóde sa našiel tajný údaj.',
    checkHeldDependency: 'Publikovanie je pozastavené. Balík má známu závažnú zraniteľnosť.',
    checkHeldMany: 'Publikovanie je pozastavené. Najprv treba opraviť {issues}.',
    checkExpired: 'Táto kontrola už nie je aktuálna.',
    checkIncomplete:
      'Kontrolu sa nepodarilo dokončiť. Publikovanie zostane pozastavené, kým sa nedokončí.',
    checkHeldPrivate: 'Publikovanie pozastavila bezpečnostná kontrola.',
    checkLastChecked: 'Naposledy skontrolované {time}',
    severityCritical: 'Kritická',
    severityHigh: 'Vysoká',
    severityModerate: 'Stredná',
    severityLow: 'Nízka',
    severityUnrated: 'Nehodnotené',
    severityWordCritical: 'kritickú',
    severityWordHigh: 'závažnú',
    severityWordModerate: 'stredne závažnú',
    severityWordLow: 'menej závažnú',
    secretApiKey: 'Kľúč API v kóde',
    secretPrivateKey: 'Súkromný kľúč v kóde',
    secretAccessToken: 'Prístupový token v kóde',
    secretPassword: 'Heslo v kóde',
    secretUnknown: 'Možný tajný údaj v kóde',
    secretWhere: '{area} {file}, riadok {line}',
    secretAreaCode: 'Kód:',
    secretAreaBuilt: 'Zostavené súbory:',
    secretWhy: 'Ktokoľvek, kto môže aplikáciu otvoriť, by ho mohol prečítať a použiť.',
    secretWhat:
      'Presuňte ho do pripojenia alebo do tajného nastavenia. Potom kľúč u jeho poskytovateľa vymeňte, pretože už mohol byť skopírovaný.',
    depTitle: '{package} {version} má známu {severity} zraniteľnosť',
    depTitleUnrated: '{package} {version} má zraniteľnosť, ktorá ešte nie je ohodnotená',
    depWhere: 'Balík, ktorý aplikácia používa',
    depWhy: 'Na túto verziu sa vzťahuje zverejnené bezpečnostné upozornenie.',
    depViewAdvisory: 'Zobraziť upozornenie',
    depAdviceUpdate: 'Aktualizujte na opravenú verziu.',
    depAdviceNoFix:
      'Opravená verzia zatiaľ neexistuje. Môžete prijať riziko alebo odstrániť balík.',
    depAdviceUnrated: 'Aktualizujte ho alebo počkajte, kým bude upozornenie ohodnotené.',
    actionFixAndRecheck: 'Opraviť a skontrolovať znova',
    actionCheckAgain: 'Skontrolovať znova',
    actionReview: 'Prezrieť',
    actionPublishAnyway: 'Prijať riziko…',
    publishAnywayTitle: 'Prijať túto známu zraniteľnosť?',
    publishAnywayBody:
      '{package} {version} má {severity} zraniteľnosť, pre ktorú zatiaľ neexistuje oprava. Môže to ovplyvniť ľudí, ktorí túto aplikáciu používajú. Zaznamená sa to s vaším menom a časom.',
    publishAnywayConfirm: 'Prijať riziko',
    publishAnywayCancel: 'Zrušiť',
    publishAnywayAcknowledged: 'Riziko prijaté · potvrdil(a) {name} · {date}',
    fixRequestSecret:
      'Presunúť tajný údaj ({label}) zo súboru {file} do tajného nastavenia a skontrolovať znova',
    fixRequestPackage: 'Aktualizovať {package} na opravenú verziu a skontrolovať znova',
    chatHeldSecret:
      'Kontrola pred publikovaním našla v kóde tajný údaj. Publikovanie je pozastavené.',
    chatHeldMany: 'Kontrola pred publikovaním našla {issues}. Publikovanie je pozastavené.',
    chatWarnings: 'Kontrola pred publikovaním našla {things}. Publikovať môžete aj tak.',
    chatReview: 'Prezrieť',
    checkIssues: 'one:problém|few:problémy|many:problému|other:problémov',
    checkThings:
      'one:vec na prezretie|few:veci na prezretie|many:veci na prezretie|other:vecí na prezretie',
  },
  sl: {
    title: 'Objavi {app} v{n}',
    firstTitle: 'Objavi {app}',
    lead: '{team} odslej uporablja v{n}. v{prev} ostane v Različicah.',
    firstLead: '{team} lahko odpre aplikacijo {app}. Objavo lahko kadar koli prekličete.',
    close: 'Zapri',
    whatsNew: 'Novosti v različici v{n}',
    whatsNewPlaceholder: 'Ena vrstica, ki jo bodo videli uporabniki',
    changesHeading: 'Kaj se spremeni za uporabnike',
    rowLooks: 'Videz',
    rowStores: 'Shranjuje',
    rowReads: 'Bere',
    rowWrites: 'Zapisuje',
    rowRuns: 'Zaganja',
    whoHeading: 'Kdo jo lahko uporablja',
    whoMeta: '{team} · število oseb: {people}',
    whoRole: 'Lahko uporablja · enako kot v{prev}',
    whoRoleFirst: 'Lahko uporablja',
    changeInShare: 'Spremeni v Deljenju',
    checksHeading: 'Preverjanja',
    blocksPublishing: 'Preprečuje objavo',
    fixInChat: 'Popravi v klepetu',
    checksPassed: 'Uspešna preverjanja: {count}',
    show: 'Pokaži',
    hide: 'Skrij',
    contractionHeading: 'Ta različica nekaj odstrani',
    contractionNote:
      'Za potrditev označite vsako postavko. Nič ne bo odstranjeno, dokler ne objavite.',
    permission: 'Objavljate lahko, ker imate v podjetju {company} dovoljenje za objavljanje.',
    undoStays: 'Razveljavitev ostane na voljo tudi po objavi, ker se nič ne odstrani.',
    undoGone: 'Razveljavitev po objavi ne bo na voljo, ker se s tem odstranijo podatki.',
    footerAudience: 'Samo člani podjetja. Brez javnih povezav.',
    footerBlocked: 'Preverjanja, ki preprečujejo objavo: {count}',
    footerBlockedMany: 'Preverjanja, ki preprečujejo objavo: {count}',
    footerContraction: 'Za objavo potrdite, kaj bo odstranjeno.',
    cancel: 'Prekliči',
    publish: 'Objavi v{n}',
    publishFirst: 'Objavi',
    publishing: 'Objavljanje…',
    doneTitle: '{team} ima zdaj v{n}',
    doneFirstTitle: '{team} ima zdaj aplikacijo {app}',
    doneBody:
      'Število oseb, ki odslej uporabljajo v{n}: {people}. Njihovi vnosi ({entries}) ostanejo nespremenjeni.',
    doneBodyUncounted:
      'Število oseb, ki odslej uporabljajo v{n}: {people}. Njihovi vnosi ostanejo nespremenjeni.',
    doneBodyBare: 'Število oseb, ki odslej uporabljajo v{n}: {people}.',
    doneFirstBody:
      'Število oseb, ki jo zdaj lahko odprejo: {people}. Njihovi vnosi se začnejo tukaj.',
    doneWhere: 'v njihovi Knjižnici in med pripetimi za ekipo',
    doneThey: 'Videli bodo {what}, ko bodo naslednjič odprli aplikacijo {app}.',
    doneUndoNote: 'Nič ni bilo odstranjeno, zato razveljavitev ostane na voljo.',
    undoPublish: 'Razveljavi objavo',
    publishUnknownTitle: 'Ne vemo, ali je bila v{n} objavljena',
    publishUnknownBody:
      '{team} jo morda že ima, morda pa ne. Pred ponovno objavo preverite, da ne bo nič objavljeno dvakrat.',
    publishFailedTitle: 'v{n} ni bila objavljena',
    publishFailedBody: 'Za {team} se ni nič spremenilo. Poskusite znova.',
    checkAgain: 'Preveri znova',
    tryAgain: 'Poskusi znova',
    askOwner: 'Vprašaj: {owner}',
    restoreAction: 'Obnovi…',
    copyLink: 'Kopiraj povezavo',
    copied: 'Povezava je kopirana',
    done: 'Končano',
    peopleList: '{names} v {place}',
    peopleAndOthers: '{first}, {second} in drugi ({count})',
    peopleAndOneOther: '{first}, {second} in še 1 oseba',
    agentPublished: 'Objavljeno · v{n} · agent {agent}',
    undo: 'Razveljavi',
    restoreVersion: 'Obnovi v{prev}',
    agentPublishedNote:
      'Agent jo je objavil po svojih preverjanjih. V Različicah lahko razveljavite ali obnovite v{prev}.',
    shareTitle: 'Deli {app}',
    shareSearch: 'Dodajte osebe ali skupine iz podjetja {company}',
    roleUse: 'Lahko uporablja',
    roleUseHint: 'Odpre aplikacijo in dodaja vnose. Vidi samo to, kar prikazuje aplikacija.',
    roleEdit: 'Lahko ureja',
    roleEditHint:
      'Spreminja aplikacijo v klepetu; vidi Podatke, Zgodovino in Različice; lahko izvaža. Ne more počistiti ali obnoviti podatkov.',
    rolePublish: 'Lahko objavlja',
    rolePublishHint: 'Samo osebe z dovoljenjem za objavljanje: {names}.',
    roleRemove: 'Odstrani',
    owner: 'Lastnik',
    ownerLine: '{you} (vi)',
    ownerPublisher: 'Lastnik · objavitelj',
    throughConnection: 'Uporabniki vidijo podatke prek vaše povezave',
    throughConnectionBody:
      'Aplikacija prikazuje podatke iz sistema {system} samo v obsegu tega, kar sama prikazuje, ne pa vaših drugih podatkov iz sistema {system}.',
    sharePublishDisabledReason: 'Zahteva dovoljenje za objavljanje',
    accessTitle: 'Kako uporabniki dostopajo do sistemov podjetja',
    accessOwnerYours: 'Prek vaše povezave · vsi vidijo enako',
    accessOwnerOther: 'Prek povezave uporabnika {owner} · vsi vidijo enako',
    accessViewer: 'Lastna povezava vsake osebe · vsak vidi svoje',
    accessConnected: 'Osebe v občinstvu s povezanim sistemom {system}: {k} od {n}',
    accessChange: 'Spremeni…',
    accessApplies: 'Spremembe začnejo veljati ob ponovni objavi',
    accessNarrowedPill: 'Ni več dovoljeno',
    accessNarrowed:
      'Podjetje {company} je spremenilo, kaj lahko deljene aplikacije berejo iz sistema {system}. Ta del je skrit, dokler znova ne izberete in ponovno ne objavite.',
    accessUnavailablePill: 'Povezava ni na voljo',
    accessUnavailable: '{owner} mora znova povezati {system}',
    accessUnavailableTold: 'Obvestili smo uporabnika {owner}',
    unpublishTitle: 'Želite preklicati objavo aplikacije {app}?',
    unpublishBody:
      'Za {team} je ne bo več mogoče odpreti. Njihovi vnosi ({entries}) se ohranijo in se vrnejo, če jo znova objavite.',
    unpublishConfirm: 'Prekliči objavo',
    unpublishBusy: 'Preklic objave…',
    error: 'Ni uspelo. Nič ni bilo spremenjeno. Poskusite znova.',
    checkChecking: 'Preverjanje kode in paketov…',
    checkClean: 'V tej različici ni bilo najdenih skrivnosti ali ranljivih paketov.',
    checkWarnings: '{things}. Objavite lahko vseeno.',
    checkHeldSecret: 'Objava je zadržana. V kodi je bila najdena skrivnost.',
    checkHeldDependency: 'Objava je zadržana. Paket ima znano resno ranljivost.',
    checkHeldMany: 'Objava je zadržana. Najprej je treba odpraviti {issues}.',
    checkExpired: 'To preverjanje ni več ažurno.',
    checkIncomplete:
      'Preverjanja ni bilo mogoče dokončati. Objava ostane zadržana, dokler se ne dokonča.',
    checkHeldPrivate: 'Objavo je zadržalo varnostno preverjanje.',
    checkLastChecked: 'Nazadnje preverjeno {time}',
    severityCritical: 'Kritično',
    severityHigh: 'Visoko',
    severityModerate: 'Zmerno',
    severityLow: 'Nizko',
    severityUnrated: 'Ni ocenjeno',
    severityWordCritical: 'kritično',
    severityWordHigh: 'resno',
    severityWordModerate: 'zmerno',
    severityWordLow: 'manjšo',
    secretApiKey: 'Ključ API v kodi',
    secretPrivateKey: 'Zasebni ključ v kodi',
    secretAccessToken: 'Žeton za dostop v kodi',
    secretPassword: 'Geslo v kodi',
    secretUnknown: 'Morebitna skrivnost v kodi',
    secretWhere: '{area} {file}, vrstica {line}',
    secretAreaCode: 'Koda:',
    secretAreaBuilt: 'Zgrajene datoteke:',
    secretWhy: 'Vsak, ki lahko odpre aplikacijo, bi jo lahko prebral in uporabil.',
    secretWhat:
      'Premaknite jo v povezavo ali skrivno nastavitev. Nato ključ zamenjajte pri njegovem ponudniku, saj je morda že bil kopiran.',
    depTitle: '{package} {version} ima znano {severity} ranljivost',
    depTitleUnrated: '{package} {version} ima ranljivost, ki še ni ocenjena',
    depWhere: 'Paket, ki ga uporablja aplikacija',
    depWhy: 'Objavljeno varnostno obvestilo velja za to različico.',
    depViewAdvisory: 'Prikaži obvestilo',
    depAdviceUpdate: 'Posodobite na popravljeno različico.',
    depAdviceNoFix:
      'Popravljena različica še ne obstaja. Lahko sprejmete tveganje ali odstranite paket.',
    depAdviceUnrated: 'Posodobite ga ali počakajte, da bo obvestilo ocenjeno.',
    actionFixAndRecheck: 'Popravi in preveri znova',
    actionCheckAgain: 'Preveri znova',
    actionReview: 'Preglej',
    actionPublishAnyway: 'Sprejmi tveganje…',
    publishAnywayTitle: 'Sprejeti to znano ranljivost?',
    publishAnywayBody:
      '{package} {version} ima {severity} ranljivost, za katero še ni popravka. To lahko vpliva na ljudi, ki uporabljajo to aplikacijo. To se zabeleži z vašim imenom in časom.',
    publishAnywayConfirm: 'Sprejmi tveganje',
    publishAnywayCancel: 'Prekliči',
    publishAnywayAcknowledged: 'Tveganje sprejeto · potrdil(a) {name} · {date}',
    fixRequestSecret:
      'Premakni skrivnost ({label}) iz datoteke {file} v skrivno nastavitev in preveri znova',
    fixRequestPackage: 'Posodobi {package} na popravljeno različico in preveri znova',
    chatHeldSecret: 'Preverjanje pred objavo je v kodi našlo skrivnost. Objava je zadržana.',
    chatHeldMany: 'Preverjanje pred objavo je našlo {issues}. Objava je zadržana.',
    chatWarnings: 'Preverjanje pred objavo je našlo {things}. Objavite lahko vseeno.',
    chatReview: 'Preglej',
    checkIssues: 'one:težavo|two:težavi|few:težave|other:težav',
    checkThings:
      'one:stvar za pregled|two:stvari za pregled|few:stvari za pregled|other:stvari za pregled',
  },
};

export const appPublishCopy: NamespaceCatalog<AppPublishCopyKey> = {
  en,
  bg: translated.bg,
  cs: translated.cs,
  da: translated.da,
  de: translated.de,
  el: translated.el,
  es: translated.es,
  et: translated.et,
  fi: translated.fi,
  fr: translated.fr,
  ga: translated.ga,
  hr: translated.hr,
  hu: translated.hu,
  it: translated.it,
  lt: translated.lt,
  lv: translated.lv,
  mt: translated.mt,
  nl: translated.nl,
  pl: translated.pl,
  pt: translated.pt,
  ro: translated.ro,
  sk: translated.sk,
  sl: translated.sl,
  sv,
};
export default appPublishCopy;
